import { accountMessages, type AccountMessageKey } from './areas/account';
import { callsMessages, type CallsMessageKey } from './areas/calls';
import { chatMessages, type ChatMessageKey } from './areas/chat';
import { chatListMessages, type ChatListMessageKey } from './areas/chatList';
import { commonMessages, type CommonMessageKey } from './areas/common';
import { musicMessages, type MusicMessageKey } from './areas/music';
import { meetingsMessages, type MeetingsMessageKey } from './areas/meetings';
import { feedbackMessages, type FeedbackMessageKey } from './areas/feedback';

export type AppLocale = 'ru' | 'en' | 'sr' | 'fr';

type BaseMessageKey =
  | 'appName'
  | 'appTagline'
  | 'loginEpigraphCalm'
  | 'loginEpigraphVarangian'
  | 'settingsAppearanceSection'
  | 'settingsVisualThemeLabel'
  | 'visualThemeNeutral'
  | 'visualThemeVarangian'
  | 'visualThemeDark'
  | 'visualThemeAuto'
  | 'loginField'
  | 'passwordPlaceholder'
  | 'loginRequired'
  | 'loginInvalid'
  | 'passwordRequired'
  | 'validationFailed'
  | 'validationFieldRequired'
  | 'validationFieldLength'
  | 'validationFieldFormat'
  | 'errorGeneric'
  | 'signIn'
  | 'logout'
  | 'group'
  | 'chatTypeGroup'
  | 'chatTypeChannel'
  | 'noChatsHint'
  | 'nicknamePlaceholder'
  | 'searchError'
  | 'nobodyFound'
  | 'close'
  | 'settingsUnsavedTitle'
  | 'settingsUnsavedBody'
  | 'settingsUnsavedDiscard'
  | 'settingsUnsavedStay'
  | 'settingsNewPasswordTooShort'
  | 'newChannelTitle'
  | 'channelNamePlaceholder'
  | 'channelEditorsHint'
  | 'channelInviteSubscribersHint'
  | 'addSubscriber'
  | 'addEditor'
  | 'channelAllowReactions'
  | 'channelAllowComments'
  | 'createChannelFailed'
  | 'channelNameRequired'
  | 'channelDescriptionPlaceholder'
  | 'channelsHubTitle'
  | 'channelSearchPlaceholder'
  | 'channelsHubEmpty'
  | 'channelPreviewTitle'
  | 'channelPreviewEmptyPosts'
  | 'channelPreviewUnavailable'
  | 'channelPreviewSubscribeHint'
  | 'channelSubscribe'
  | 'channelUnsubscribe'
  | 'channelManage'
  | 'channelSubscribersCount'
  | 'channelDirectoryLoadFailed'
  | 'channelSubscriptionFailed'
  | 'channelUpdateFailed'
  | 'channelEditorAddFailed'
  | 'channelEditorRemoveFailed'
  | 'deleteChannel'
  | 'saveChannel'
  | 'groupNamePlaceholder'
  | 'removeMember'
  | 'cancel'
  | 'dialogOk'
  | 'create'
  | 'groupInfoTitle'
  | 'groupTabMembers'
  | 'groupTabSettings'
  | 'groupMembersLabel'
  | 'groupYouSuffix'
  | 'groupAdminBadge'
  | 'groupSearchPlaceholder'
  | 'groupNoUsersFound'
  | 'groupAddAction'
  | 'groupRetry'
  | 'groupLeaveBtn'
  | 'groupLeaveTitle'
  | 'groupLeaveBody'
  | 'groupLeaveConfirm'
  | 'groupLeaveFailed'
  | 'groupNameLabel'
  | 'groupDescLabel'
  | 'groupDescPlaceholder'
  | 'groupSaveChanges'
  | 'groupNameRequired'
  | 'groupInfoSaved'
  | 'groupInfoSaveFailed'
  | 'groupChangePhoto'
  | 'groupRemoveMemberTitle'
  | 'groupRemoveMemberBody'
  | 'groupRemoveAction'
  | 'groupRemoveMemberFailed'
  | 'groupMakeAdminTitle'
  | 'groupRemoveAdminTitle'
  | 'groupMakeAdminBody'
  | 'groupRemoveAdminBody'
  | 'groupChangeRoleFailed'
  | 'groupAddMemberFailed'
  | 'groupLoadMembersFailed'
  | 'deleteChatConfirmTitle'
  | 'deleteChatFailed'
  | 'errorTitle'
  | 'openChatFailed'
  | 'createGroupFailed'
  | 'callUnavailableTitle'
  | 'webrtcExpoGoChat'
  | 'webrtcExpoGoCall'
  | 'webrtcWebChat'
  | 'webrtcWebCall'
  | 'loadMessagesFailed'
  | 'sendFailed'
  | 'messageSending'
  | 'messageUploading'
  | 'messageCompressing'
  | 'messageNotDelivered'
  | 'audioStopRecord'
  | 'audioStartRecord'
  | 'audioCancelRecord'
  | 'accessibilitySend'
  | 'audioPlay'
  | 'audioPause'
  | 'transcriptShow'
  | 'transcriptHide'
  | 'transcriptPending'
  | 'transcriptReadAll'
  | 'transcriptFailed'
  | 'transcriptTooLong'
  | 'transcriptSheetTitle'
  | 'audioRecordFailed'
  | 'audioPlayFailed'
  | 'audioRecordUnsupported'
  | 'audioRecordSecureContext'
  | 'videoRecordFailed'
  | 'videoDurationLimit'
  | 'videoPayloadTooLarge'
  | 'callReady'
  | 'callCalling'
  | 'callConnecting'
  | 'callReconnecting'
  | 'messagePlaceholder'
  | 'emojiHide'
  | 'emojiShow'
  | 'emojiFrequent'
  | 'emojiPopular'
  | 'emojiDarkSkin'
  | 'endCall'
  | 'accessibilityCall'
  | 'callNavOutgoing'
  | 'callNavIncoming'
  | 'callIncomingFull'
  | 'callIncomingAudioLine'
  | 'callIncomingVideoLine'
  | 'callIncomingChipAudio'
  | 'callIncomingChipVideo'
  | 'callTalkingAudio'
  | 'callDuration'
  | 'callBtnAnswer'
  | 'callBtnReject'
  | 'callBtnHangup'
  | 'callFullScreenIntentWarn'
  | 'callFullScreenIntentSettings'
  | 'callUseSpeaker'
  | 'callUseEarpiece'
  | 'callMicMute'
  | 'callMicUnmute'
  | 'callMicUnavailable'
  | 'callSpeakingNow'
  | 'accessibilityVideoCall'
  | 'nonJsonServer'
  | 'loginFailed'
  | 'loginTooManyAttempts'
  | 'searchFailed'
  | 'chatFallback'
  | 'newMessage'
  | 'callGeneric'
  | 'callGroupIncomingFrom'
  | 'callScopeBadgeGroupAudio'
  | 'callParticipantOrdinal'
  | 'callParticipantsInCall'
  | 'callEventMissedOutgoing'
  | 'callEventMissedIncoming'
  | 'callEventDeclinedOutgoing'
  | 'callEventBusy'
  | 'callEventEnded'
  | 'peerDefault'
  | 'callVideoFallbackAudio'
  | 'callPeerBusy'
  | 'notifyChannelMessages'
  | 'notifyChannelMessagesSilent'
  | 'notifyChannelCalls'
  | 'notifyBodyMessage'
  | 'notifyContactRequestTitle'
  | 'notifyContactRequestBody'
  | 'notifyCommentReplyTitle'
  | 'androidNotifyCallsTitle'
  | 'androidNotifyCallsBody'
  | 'androidNotifyCallsOpenSettings'
  | 'androidNotifyCallsLater'
  | 'androidFullScreenCallsTitle'
  | 'androidFullScreenCallsBody'
  | 'onlineNow'
  | 'scrollToLatest'
  | 'sessionExpired'
  | 'accessibilityAttachMenu'
  | 'photoPickFailed'
  | 'accessibilityExpandVideo'
  | 'accessibilityPrevPhoto'
  | 'accessibilityNextPhoto'
  | 'videoResolutionTooHigh'
  | 'photoProcessFailed'
  | 'photoPreviewTitle'
  | 'photoPreviewBack'
  | 'photoPreviewSend'
  | 'photoPreviewImageLabel'
  | 'photoPreviewCaptionPlaceholder'
  | 'photoPreviewDiscardTitle'
  | 'photoPreviewDiscardMessage'
  | 'photoPreviewDiscard'
  | 'photoPreviewKeepEditing'
  | 'photoMasksTitle'
  | 'photoMaskBlur'
  | 'photoMaskPixelate'
  | 'photoMaskEmoji'
  | 'photoMaskHideAllFaces'
  | 'photoMaskNoFaces'
  | 'photoMaskDetecting'
  | 'photoMaskHint'
  | 'photoFiltersTitle'
  | 'photoFilterStrength'
  | 'photoFilterOriginal'
  | 'photoFilterFilm400'
  | 'photoFilterNight800t'
  | 'photoFilterDigicam2004'
  | 'photoFilterMatte'
  | 'photoFilterCinema'
  | 'photoFilterCleanLight'
  | 'photoFilterMono3200'
  | 'photoFilterDuotone'
  | 'photoFilterGlow'
  | 'photoFilterInfra'
  | 'photoFilterChrome'
  | 'photoFilterNoon'
  | 'photoCropTitle'
  | 'photoCropApply'
  | 'photoCropFree'
  | 'photoCropRotate'
  | 'photoCropReset'
  | 'photoCropFailed'
  | 'photoPreviewRemove'
  | 'photoSendPartialFailed'
  | 'photoQualityEconomy'
  | 'photoQualityStandard'
  | 'photoQualityMax'
  | 'photoQualityOriginal'
  | 'photoQualityCalculating'
  | 'unitKilobytes'
  | 'unitMegabytes'
  | 'currentPassword'
  | 'newPasswordLabel'
  | 'savePassword'
  | 'passwordChangeFailed'
  | 'bossUsersTitle'
  | 'bossUsersLoadFailed'
  | 'bossCreateUserFailed'
  | 'bossDeleteUserFailed'
  | 'bossUserLoginPlaceholder'
  | 'bossDisplayNamePlaceholder'
  | 'bossNewUserPassword'
  | 'bossTempPasswordHint'
  | 'bossRegeneratePassword'
  | 'bossAddUser'
  | 'bossDeleteUser'
  | 'bossDeleteConfirmTitle'
  | 'bossDeleteConfirmBody'
  | 'bossCredentialsSentHint'
  | 'bossInviteCopyBody'
  | 'bossCopyInviteButton'
  | 'bossCopyInviteDone'
  | 'bossInstructionLanguage'
  | 'forwardedLabel'
  | 'forwardedFromPrefix'
  | 'savedChatTitle'
  | 'shareIntentTitle'
  | 'shareIntentPhotos'
  | 'shareIntentSkipped'
  | 'shareIntentSending'
  | 'shareIntentFailed'
  | 'shareIntentEmpty'
  | 'shareIntentSavedHint'
  | 'savedChatCardHint'
  | 'savedEmptyTitle'
  | 'savedEmptyBody'
  | 'saveToSavedAction'
  | 'savedMessageDone'
  | 'savedSourceDeleted'
  | 'savedOpenFailed'
  | 'savedSectionAll'
  | 'savedSectionNotes'
  | 'savedSectionTasks'
  | 'savedSectionFiles'
  | 'savedSectionLinks'
  | 'savedSectionVoice'
  | 'savedSectionEmptyNotes'
  | 'savedSectionEmptyTasks'
  | 'savedSectionEmptyFiles'
  | 'savedSectionEmptyLinks'
  | 'savedSectionEmptyVoice'
  | 'noteNewAction'
  | 'noteTitlePlaceholder'
  | 'noteBodyPlaceholder'
  | 'notePinAction'
  | 'noteUnpinAction'
  | 'notePinnedBadge'
  | 'noteSavingHint'
  | 'noteSavedHint'
  | 'noteColorLabel'
  | 'noteEditFailed'
  | 'settingsGenderLabel'
  | 'genderUnset'
  | 'genderMale'
  | 'genderFemale'
  | 'settingsBirthDateLabel'
  | 'settingsBirthDateHint'
  | 'settingsBirthDateInvalid'
  | 'settingsBirthDateAgeRange'
  | 'profileYearsSuffix'
  | 'birthdayTodayTitle'
  | 'birthdaySoonTitle'
  | 'birthdayCongratulate'
  | 'birthdayTurns'
  | 'birthdayInDays'
  | 'birthdayTomorrow'
  | 'birthdayPickTemplate'
  | 'birthdayTemplateHint'
  | 'birthdaySend'
  | 'birthdaySent'
  | 'birthdaySendFailed'
  | 'birthdayHide'
  | 'savedSectionReminders'
  | 'savedSectionScheduled'
  | 'savedSectionEmptyReminders'
  | 'savedSectionEmptyScheduled'
  | 'reminderNewAction'
  | 'reminderTitlePlaceholder'
  | 'reminderBodyPlaceholder'
  | 'reminderWhenLabel'
  | 'reminderDatePlaceholder'
  | 'reminderTimePlaceholder'
  | 'reminderPrecisionHint'
  | 'reminderSaveAction'
  | 'reminderSaveFailed'
  | 'reminderDeleteAction'
  | 'reminderSnoozeAction'
  | 'reminderStatusScheduled'
  | 'reminderStatusFired'
  | 'reminderStatusCancelled'
  | 'reminderInvalidTime'
  | 'reminderZoneNote'
  | 'organizerEditAction'
  | 'quickToday'
  | 'quickTomorrow'
  | 'quickInAnHour'
  | 'scheduledNewAction'
  | 'scheduledRecipientsLabel'
  | 'scheduledRecipientsEmpty'
  | 'scheduledRecipientsSearch'
  | 'scheduledTextPlaceholder'
  | 'scheduledWhenLabel'
  | 'scheduledCreateAction'
  | 'scheduledCancelAction'
  | 'scheduledMessageSaveFailed'
  | 'scheduledMessageCancelFailed'
  | 'scheduledStatusScheduled'
  | 'scheduledStatusSent'
  | 'scheduledStatusCancelled'
  | 'scheduledStatusFailed'
  | 'scheduledTooSoon'
  | 'notifyReminderTitle'
  | 'notifyBirthdayTitle'
  | 'notifyBirthdayBody'
  | 'registerAction'
  | 'registerTitle'
  | 'registerEmailPlaceholder'
  | 'registerNamePlaceholder'
  | 'registerSubmit'
  | 'registerEmailInvalid'
  | 'registerPasswordTooShort'
  | 'registerNameRequired'
  | 'registerInviteOnly'
  | 'registerFailed'
  | 'verifySentTitle'
  | 'verifySentBody'
  | 'verifyResend'
  | 'verifyResendDone'
  | 'backToLogin'
  | 'forgotAction'
  | 'forgotTitle'
  | 'forgotBody'
  | 'forgotSubmit'
  | 'forgotSentTitle'
  | 'forgotSentBody'
  | 'resetTitle'
  | 'resetBody'
  | 'resetNewPassword'
  | 'resetSubmit'
  | 'resetDone'
  | 'resetFailed'
  | 'verifyDone'
  | 'verifyLinkDead'
  | 'captchaRequired'
  | 'forwardSelectCount'
  | 'selectMessagesAction'
  | 'deleteSelectedAction'
  | 'deleteSelectedConfirmTitle'
  | 'deleteSelectedConfirmBody'
  | 'deleteSelectedFailed'
  | 'forwardAction'
  | 'forwardDoneBody'
  | 'replyAction'
  | 'replyQuoteLabel'
  | 'replyOriginalDeleted'
  | 'copyTextAction'
  | 'copyTextDone'
  | 'editAction'
  | 'editingMessageTitle'
  | 'editMessageFailed'
  | 'messageEditedMark'
  | 'deleteMessageAction'
  | 'deleteMessageFailed'
  | 'reactionLimitReached'
  | 'savedClearAction'
  | 'savedClearDone'
  | 'savedClearFailed'
  | 'helpCenterTitle'
  | 'helpCenterOpenLabel'
  | 'helpCenterOpenLabelUnread'
  | 'helpCenterNewBadge'
  | 'helpCenterAllReadHint'
  | 'helpCenterIntro'
  | 'settingsHandleSuggestions'
  | 'saveChatImage'
  | 'saveChatImageSuccess'
  | 'saveChatImageFailed'
  | 'saveChatImagePermissionDenied'
  | 'saveChatVideo'
  | 'saveChatVideoSuccess'
  | 'saveChatVideoFailed'
  | 'saveChatVideoPermissionDenied'
  | 'forwardSameChatBlocked'
  | 'messageReferenceNotFound'
  | 'gearSettings'
  | 'myDruzhina'
  | 'myDruzhinaHint'
  | 'myDruzhinaVarangian'
  | 'myDruzhinaHintVarangian'
  | 'contactRequestIncomingTitle'
  | 'contactLoadFailed'
  | 'contactRequestLoadFailed'
  | 'contactRequestSendFailed'
  | 'contactRequestApproveFailed'
  | 'contactRequestRejectFailed'
  | 'contactSearchSelfRow'
  | 'chatLastMessageFallback'
  | 'unseenReactionA11y'
  | 'settingsHapticsLabel'
  | 'settingsSoundsLabel'
  | 'retentionNoticeTitle'
  | 'retentionNoticeBody'
  | 'retentionNoticeWhen'
  | 'contactRemoveConfirmTitle'
  | 'contactRemoveConfirmTitleVarangian'
  | 'contactRemoveConfirmBody'
  | 'contactRemoveFailed'
  | 'profileUpdateFailed'
  | 'settingsProfileSection'
  | 'settingsChangeNameLabel'
  | 'settingsChangeLoginLabel'
  | 'settingsSaveProfile'
  | 'settingsSaved'
  | 'settingsShareSection'
  | 'settingsPublicHandleLabel'
  | 'settingsPublicHandleHint'
  | 'settingsHandleWillSaveAs'
  | 'settingsHandleChecking'
  | 'settingsHandleFree'
  | 'settingsHandleTaken'
  | 'settingsHandleFormat'
  | 'settingsHandleReserved'
  | 'settingsHandleNotAllowed'
  | 'settingsHandleSaveFailedInvalid'
  | 'settingsHandleConfirmChangeTitle'
  | 'settingsHandleConfirmChangeBody'
  | 'settingsHandleConfirmClearTitle'
  | 'settingsHandleConfirmClearBody'
  | 'settingsHandleKeep'
  | 'settingsProfilePublicLabel'
  | 'settingsAllowDiscoveryLabel'
  | 'settingsKeepPhotoMetadataLabel'
  | 'settingsKeepPhotoMetadataHint'
  | 'settingsSaveShareSettings'
  | 'settingsCopyHandleTag'
  | 'settingsCopied'
  | 'profileLinkUnavailable'
  | 'profileLinkSelf'
  | 'settingsShareDisabledHidden'
  | 'settingsShareDisabledUnsaved'
  | 'settingsProfileSaved'
  | 'settingsShareSaved'
  | 'settingsPasswordChanged'
  // REL-1. Удаление аккаунта: требование Apple 5.1.1(v) и Google Play Data deletion.
  | 'settingsDeleteAccountSection'
  | 'settingsDeleteAccountWarning'
  | 'settingsDeleteAccountStart'
  | 'settingsDeleteAccountConfirmHint'
  | 'settingsDeleteAccountConfirmWord'
  | 'settingsDeleteAccountConfirmPlaceholder'
  | 'settingsDeleteAccountConfirm'
  | 'settingsDeleteAccountCancel'
  | 'settingsDeleteAccountPasswordRequired'
  | 'settingsDeleteAccountWordMismatch'
  | 'settingsDeleteAccountFailed'
  // REL-2. Жалобы и блокировка — требование Apple 1.2 к приложениям с пользовательским
  // контентом: механизм жалобы, блокировка обидчика и оба видны там, где происходит общение.
  | 'reportAction'
  | 'reportTitle'
  | 'reportSent'
  | 'reportFailed'
  | 'reportCategorySpam'
  | 'reportCategoryHarassment'
  | 'reportCategorySexualContent'
  | 'reportCategoryViolence'
  | 'reportCategoryFraud'
  | 'reportCategoryChildSafety'
  | 'reportCategoryOther'
  | 'blockAction'
  | 'blockConfirmTitle'
  | 'blockConfirmHint'
  | 'blockDone'
  | 'blockFailed'
  | 'unblockAction'
  | 'settingsBlockedSection'
  | 'settingsBlockedEmpty'
  | 'moderationCancel'
  // REL-4. Ссылки на документы: Apple требует, чтобы они были доступны из приложения, а не
  // только из карточки в сторе.
  | 'settingsLegalSection'
  | 'settingsPrivacyPolicyLink'
  | 'settingsTermsLink'
  | 'settingsLocalOnlyHint'
  | 'settingsNamePlaceholder'
  | 'settingsPublicHandlePlaceholder'
  | 'settingsAvatarTitle'
  | 'settingsAvatarChooseShape'
  | 'settingsAvatarRemove'
  | 'settingsAvatarUploadFailed'
  | 'avatarAnimatedTooLarge'
  | 'galleryPermissionDenied'
  | 'settingsAvatarWebHint'
  | 'avatarChangePhoto'
  | 'avatarTapToChange'
  | 'avatarTakePhoto'
  | 'avatarFromLibrary'
  | 'avatarCameraDenied'
  | 'avatarRemoveConfirm'
  | 'avatarEditorHint'
  | 'avatarEditorHintPanOnly'
  | 'avatarPreviewCard'
  | 'chatAvatarSectionTitle'
  | 'userCardTitle'
  | 'userCardOpenPhoto'
  | 'userCardPhotoHint'
  | 'userCardPhotoFriendsOnly'
  | 'userCardPhotoLoadFailed'
  | 'chatUnknownMember'
  | 'contactCardLabel'
  | 'contactShareAction'
  | 'contactShareChooseChat'
  | 'contactShareSent'
  | 'contactShareFailed'
  | 'checklistItemA11y'
  | 'checklistCheckedBy'
  | 'checklistToggleFailed'
  | 'checklistDuePast'
  | 'checklistDueUnparsed'
  | 'checklistAssigneeInvalid'
  | 'checklistItemEmpty'
  | 'checklistAssigneeNotFound'
  | 'checklistDueToday'
  | 'checklistDueTomorrow'
  | 'checklistOverdue'
  | 'checklistDueA11y'
  | 'checklistAssigneeA11y'
  | 'channelPostCommentsCount'
  | 'settingsLoginHint'
  | 'loginCheckInProgress'
  | 'loginAvailable'
  | 'loginUnavailable'
  | 'chatTileCallAudio'
  | 'chatTileCallVideo'
  | 'channelReadOnlyHint'
  | 'channelCommentComposerHint'
  | 'channelUnsubscribeConfirmBody'
  | 'channelUnsubscribeFailed'
  | 'channelPostRestrictedHint'
  | 'channelArticlesLoadFailed'
  | 'channelArticlesDrafts'
  | 'channelArticleStatusDraft'
  | 'channelDraftSavedTitle'
  | 'channelDraftSavedBody'
  | 'channelDraftView'
  | 'channelPostPublishedTitle'
  | 'channelSettingsTitle'
  | 'channelDraftsSettingsHint'
  | 'channelDraftsEmpty'
  | 'deleteChannelConfirmBody'
  | 'channelPostOpenFailed'
  | 'channelPostLoadFailed'
  | 'channelPostComments'
  | 'channelCommentsClosed'
  | 'channelPublishAction'
  | 'channelPublishDraftTitle'
  | 'channelPublishFailed'
  | 'channelDraftOpen'
  | 'channelSubscribedTitle'
  | 'channelGuestSubscribeHint'
  | 'channelArchiveTitle'
  | 'channelArchiveEmpty'
  | 'channelArchiveLoadMore'
  | 'blockParagraph'
  | 'blockHeading'
  | 'blockImage'
  | 'blockQuote'
  | 'blockBulletList'
  | 'blockOrderedList'
  | 'blockDivider'
  | 'blockCode'
  | 'blockEmbed'
  | 'blockVideo'
  | 'blockGallery'
  | 'editorTitleLabel'
  | 'editorTitlePlaceholder'
  | 'editorBodyLabel'
  | 'editorAddCover'
  | 'editorChangeCover'
  | 'editorRemoveCover'
  | 'editorSaveDraft'
  | 'editorPublish'
  | 'editorListItem'
  | 'editorBlockEmpty'
  | 'editorReplaceImage'
  | 'editorUploadImage'
  | 'editorAddPhotos'
  | 'editorAddBlockA11y'
  | 'editorInsertHere'
  | 'commentsTitle'
  | 'commentsReply'
  | 'commentsReplyingTo'
  | 'commentsPlaceholder'
  | 'commentsSend'
  | 'commentsPending'
  | 'commentsThumbUp'
  | 'commentsDelete'
  | 'commentsApprove'
  | 'commentsBan'
  | 'commentsDeleteConfirmBody'
  | 'commentsBanConfirmTitle'
  | 'commentsBanConfirmBody'
  | 'commentsModerationFailed'
  | 'articleReadMore'
  | 'articleCardKicker'
  | 'channelMuteOn'
  | 'channelMuteOff'
  | 'channelMuteFailed'
  | 'channelViewsCount'
  | 'editorPreview'
  | 'editorPreviewBack'
  | 'editorInsertHint'
  | 'editorInvalidChannel'
  | 'channelPostEdit'
  | 'channelPostEditTitle'
  | 'channelPostEditPublishedHint'
  | 'channelPostSaveChanges'
  | 'channelPostSaved'
  | 'channelPostSaveFailed'
  | 'channelPostDelete'
  | 'channelPostDeleteConfirmTitle'
  | 'channelPostDeleteConfirmBody'
  | 'channelPostDeleteDraftConfirmBody'
  | 'channelPostDeleteFailed'
  | 'channelPostUnpublish'
  | 'channelPostUnpublishConfirmTitle'
  | 'channelPostUnpublishConfirmBody'
  | 'channelPostUnpublishFailed'
  | 'channelDraftRestoredHint'
  | 'channelDraftLeaveTitle'
  | 'channelDraftLeaveBody'
  | 'channelDraftLeaveConfirm'
  | 'channelDraftLeaveStay'
  | 'channelDraftDiscardRestored'
  | 'channelSlugLabel'
  | 'channelSlugPlaceholder'
  | 'channelSlugHint'
  | 'channelSlugInvalid'
  | 'channelCommentSendFailed'
  | 'channelCommentsLoadFailed'
  | 'chatFilterGroup'
  | 'chatFilterChannel'
  | 'chatSortRecent'
  | 'chatSortOld'
  | 'chatSortTitleAsc'
  | 'chatSortTitleDesc'
  | 'callScopeBadgeGroupVideo'
  | 'callScopeBadgeDirectVideo'
  | 'groupCallUnavailableTitle'
  | 'groupCallsTemporarilyOff'
  | 'groupCallUnavailableDisabled'
  | 'groupCallUnavailableNetwork'
  | 'groupCallUnavailableConnect'
  | 'groupCallJoinOngoing'
  | 'groupCallOngoingBadge'
  | 'callDiagnosticsTitle'
  | 'callDiagnosticsHint'
  | 'callDiagnosticsCopy'
  | 'callDiagTransport'
  | 'callDiagCandidatePair'
  | 'callDiagBitrate'
  | 'callDiagPacketsLost'
  | 'callDiagParticipants'
  | 'screenShareTitle'
  | 'screenShareBusyBody'
  | 'screenShareLabel'
  | 'screenShareBody'
  | 'callShareScreen'
  | 'callShareScreenStop'
  | 'callSwitchCamera'
  | 'settingsLanguageSection'
  | 'settingsLanguageLabel'
  | 'settingsTabPreferences'
  | 'settingsTabAccount'
  | 'settingsTabAvatar'
  | 'settingsTabProfile'
  | 'settingsTabPrivacy'
  | 'settingsLoginSection'
  | 'settingsSaveLogin'
  | 'settingsLoginSaved'
  | 'settingsPasswordSection'
  | 'settingsVisibilitySection'
  | 'settingsProfilePublicHint'
  | 'settingsAllowDiscoveryHint'
  | 'settingsMediaPrivacySection'
  | 'settingsShareDisabledNoHandle'
  | 'settingsPreferencesHint'
  | 'settingsPreferencesDone'
  | 'settingsCurrentPasswordRequired'
  | 'directRetentionPolicyTitle'
  | 'directRetentionPolicyBody'
  | 'directRetentionTimerA11y'
  | 'quickRepliesTitle'
  | 'quickReplyAddTitle'
  | 'quickReplyAddPlaceholder'
  | 'quickReplySave'
  | 'quickReplyDelete'
  | 'quickReplyDeleteConfirmTitle'
  | 'quickReplyDefaultGreeting'
  | 'quickReplyDefaultInTouch'
  | 'quickReplyDefaultThanks'
  | 'sharePost'
  | 'shareChannel'
  | 'shareProfile'
  | 'settingsBioLabel'
  | 'settingsBioPlaceholder'
  | 'settingsStatusLabel'
  | 'settingsStatusPlaceholder'
  | 'selfStatusEmpty'
  | 'quickSettingsTitle'
  | 'selfStatusSave'
  | 'dndLabel'
  | 'dndHint'
  | 'dndFailed'
  | 'notifyShowTextLabel'
  | 'notifyShowTextHint'
  | 'userCardAddContact'
  | 'userCardContactPending'
  | 'navTitleNewPost'
  | 'navTitlePost';

const en: Record<BaseMessageKey, string> = {
  appName: 'SVYAZ',
  appTagline: 'Calls and chats — stay connected',
  loginEpigraphCalm:
    'A calm space for people who value reliable contact: messages and calls in one place, with privacy in mind.',
  loginEpigraphVarangian:
    'These uneasy times are no hindrance to old Varangians and no reason to despair — we will always find a way to stay in touch! Skol',
  loginField: 'Login or email',
  passwordPlaceholder: 'Password (min. 8 characters)',
  loginRequired: 'Enter your login or email address',
  loginInvalid: 'A login uses Latin letters, digits, dot, hyphen and underscore. Or enter your email address',
  passwordRequired: 'Enter your password',
  validationFailed: 'Please check the values you entered',
  validationFieldRequired: 'Please fill in the required field',
  validationFieldLength: 'The value has an invalid length',
  validationFieldFormat: 'The value contains characters that are not allowed',
  errorGeneric: 'Error',
  signIn: 'Sign in',
  logout: 'Log out',
  group: 'Group',
  chatTypeGroup: 'Group',
  chatTypeChannel: 'Channel',
  noChatsHint: 'No chats yet — start a direct or group chat',
  nicknamePlaceholder: 'Nickname or part of a name…',
  searchError: 'Search error',
  nobodyFound: 'No one found',
  close: 'Close',
  settingsUnsavedTitle: "You have unsaved changes",
  settingsUnsavedBody: "Save them before closing?",
  settingsUnsavedDiscard: "Discard",
  settingsUnsavedStay: "Keep editing",
  settingsNewPasswordTooShort: "New password must be at least 8 characters",
  newChannelTitle: 'New channel',
  channelNamePlaceholder: 'Channel name',
  channelEditorsHint: 'Editors can publish posts in this channel.',
  channelInviteSubscribersHint: 'Invite people as subscribers. Promote editors later in channel settings.',
  addSubscriber: 'Add subscriber',
  addEditor: 'Add editor',
  channelAllowReactions: 'Allow reactions',
  channelAllowComments: 'Allow comments',
  createChannelFailed: 'Could not create the channel',
  channelNameRequired: 'Enter a channel name',
  channelDescriptionPlaceholder: 'Description',
  channelsHubTitle: 'Channels',
  channelSearchPlaceholder: 'Search channels…',
  channelsHubEmpty: 'No channels yet',
  channelPreviewTitle: 'Channel preview',
  channelPreviewEmptyPosts: 'No published posts yet',
  channelPreviewUnavailable: 'This channel has no public preview — subscribe to read it',
  channelPreviewSubscribeHint: 'Subscribe to read the channel in your chat list',
  channelSubscribe: 'Subscribe',
  channelUnsubscribe: 'Unsubscribe',
  channelManage: 'Manage',
  channelSubscribersCount: 'Subscribers: {count}',
  channelDirectoryLoadFailed: 'Could not load channels',
  channelSubscriptionFailed: 'Could not change subscription',
  channelUpdateFailed: 'Could not update channel',
  channelEditorAddFailed: 'Could not add editor',
  channelEditorRemoveFailed: 'Could not remove editor',
  deleteChannel: 'Delete channel',
  saveChannel: 'Save channel',
  groupNamePlaceholder: 'Name',
  removeMember: 'Remove',
  cancel: 'Cancel',
  dialogOk: 'Got it',
  create: 'Create',
  groupInfoTitle: 'Group info',
  groupTabMembers: 'Members',
  groupTabSettings: 'Settings',
  groupMembersLabel: '{count} members',
  groupYouSuffix: '(you)',
  groupAdminBadge: 'Admin',
  groupSearchPlaceholder: 'Search users to add…',
  groupNoUsersFound: 'No users found',
  groupAddAction: 'Add',
  groupRetry: 'Retry',
  groupLeaveBtn: 'Leave group',
  groupLeaveTitle: 'Leave group',
  groupLeaveBody: 'Are you sure you want to leave this group?',
  groupLeaveConfirm: 'Leave',
  groupLeaveFailed: 'Failed to leave the group',
  groupNameLabel: 'Group name',
  groupDescLabel: 'Description',
  groupDescPlaceholder: 'Group description…',
  groupSaveChanges: 'Save changes',
  groupNameRequired: 'Group name is required',
  groupInfoSaved: 'Group info updated',
  groupInfoSaveFailed: 'Failed to save group info',
  groupChangePhoto: 'Change photo',
  groupRemoveMemberTitle: 'Remove member',
  groupRemoveMemberBody: 'Remove {name} from the group?',
  groupRemoveAction: 'Remove',
  groupRemoveMemberFailed: 'Failed to remove member',
  groupMakeAdminTitle: 'Make admin',
  groupRemoveAdminTitle: 'Remove admin',
  groupMakeAdminBody: 'Make {name} an admin?',
  groupRemoveAdminBody: 'Remove admin rights from {name}?',
  groupChangeRoleFailed: 'Failed to change role',
  groupAddMemberFailed: 'Failed to add member',
  groupLoadMembersFailed: 'Failed to load members',
  deleteChatConfirmTitle: 'Delete chat?',
  deleteChatFailed: 'Could not delete the chat',
  errorTitle: 'Error',
  openChatFailed: 'Could not open the chat',
  createGroupFailed: 'Could not create the group',
  callUnavailableTitle: 'Call unavailable',
  webrtcExpoGoChat:
    'Voice works only in a dev build (not in Expo Go). In mobile: npx expo prebuild, then run:android / run:ios.',
  webrtcExpoGoCall:
    'Voice works only in a dev build (not in Expo Go). In the mobile folder: npx expo prebuild, then npx expo run:android or run:ios.',
  webrtcWebChat: 'WebRTC, microphone and HTTPS (or localhost) are required.',
  webrtcWebCall: 'A WebRTC-capable browser, microphone access and HTTPS (or localhost) are required.',
  loadMessagesFailed: 'Could not load messages',
  sendFailed: 'Send failed',
  messageSending: 'Sending…',
  messageUploading: 'Uploading…',
  messageCompressing: 'Compressing…',
  messageNotDelivered: 'Not delivered',
  audioStopRecord: 'Stop recording',
  audioStartRecord: 'Record a voice message',
  accessibilityAttachMenu: 'Attach',
  audioCancelRecord: 'Cancel recording',
  accessibilitySend: 'Send',
  audioPlay: 'Play voice',
  audioPause: 'Playing…',
  transcriptShow: 'Text',
  transcriptHide: 'Collapse',
  transcriptPending: 'Transcribing…',
  transcriptReadAll: 'Read in full',
  transcriptFailed: 'Could not transcribe',
  transcriptTooLong: 'Recording too long to transcribe',
  transcriptSheetTitle: 'Voice message text',
  audioRecordFailed: 'Could not record voice message',
  audioPlayFailed: 'Could not play voice message',
  audioRecordUnsupported: 'Voice recording is not supported in this browser',
  audioRecordSecureContext: 'Voice recording on web requires HTTPS or localhost',
  videoRecordFailed: 'Could not record video message',
  videoDurationLimit: 'Video message must be 44 seconds or shorter',
  videoPayloadTooLarge:
    'File is too large to send. Video: soft limit 100 MB, target bitrate up to 5 Mbps, 1080p.',
  callReady: 'Ready to call',
  callCalling: 'Calling… waiting for answer',
  callConnecting: 'Connecting…',
  callReconnecting: 'Reconnecting…',
  messagePlaceholder: 'Message…',
  emojiHide: 'Hide emoji',
  emojiShow: 'Emoji',
  emojiFrequent: 'Frequently used',
  emojiPopular: 'Popular',
  emojiDarkSkin: 'Dark skin (HD)',
  endCall: 'End',
  accessibilityCall: 'Call',
  callNavOutgoing: 'Call',
  callNavIncoming: 'Incoming',
  callIncomingFull: 'Incoming call',
  callIncomingAudioLine: 'Voice call',
  callIncomingVideoLine: 'Video call',
  callIncomingChipAudio: 'Audio',
  callIncomingChipVideo: 'Video',
  callTalkingAudio: 'Voice call',
  callDuration: 'Call duration',
  callBtnAnswer: 'Answer',
  callBtnReject: 'Decline',
  callBtnHangup: 'Hang up',
  callFullScreenIntentWarn:
    'Full-screen incoming calls are disabled in system settings — you may not see the call UI when the phone is locked.',
  callFullScreenIntentSettings: 'Full-screen call alerts (system settings)',
  callUseSpeaker: 'Speaker',
  callUseEarpiece: 'Earpiece',
  callMicMute: 'Mute mic',
  callMicUnmute: 'Turn mic on',
  callMicUnavailable: 'Microphone unavailable — the others cannot hear you.',
  callSpeakingNow: 'Speaking',
  accessibilityVideoCall: 'Video call',
  nonJsonServer:
    'The server did not return JSON. Make sure the backend is running (port 18080) and the API URL is correct: EXPO_PUBLIC_API_BASE or local web → 127.0.0.1:18080.',
  loginFailed: 'Login failed',
  loginTooManyAttempts: 'Too many sign-in attempts. Please wait a little and try again.',
  searchFailed: 'Search could not be completed',
  chatFallback: 'Chat',
  newMessage: 'New message',
  callGeneric: 'Call',
  callGroupIncomingFrom: '{name} is calling',
  callScopeBadgeGroupAudio: 'Group call',
  callParticipantOrdinal: 'Participant {n}',
  callParticipantsInCall: 'In the call: {count}',
  callEventMissedOutgoing: '📞 No answer',
  callEventMissedIncoming: '📞 Missed call',
  callEventDeclinedOutgoing: '📞 Declined',
  callEventBusy: '📞 Busy',
  callEventEnded: '📞 Call ended',
  peerDefault: 'Contact',
  callVideoFallbackAudio: 'Camera is unavailable, answering with audio only.',
  callPeerBusy: 'The line is busy.',
  notifyChannelMessages: 'Messages',
  notifyChannelMessagesSilent: 'Messages (app open)',
  notifyChannelCalls: 'Calls',
  notifyBodyMessage: 'You have a new message',
  notifyContactRequestTitle: 'Friend request',
  notifyContactRequestBody: 'wants to add you as a friend',
  notifyCommentReplyTitle: 'Reply to your comment',
  androidNotifyCallsTitle: 'Turn on notifications',
  androidNotifyCallsBody:
    'Calls and message alerts use notifications. Enable them for this app in system settings — otherwise incoming calls may not ring when the app is in the background.',
  androidNotifyCallsOpenSettings: 'Open settings',
  androidNotifyCallsLater: 'Not now',
  androidFullScreenCallsTitle: 'Full-screen call alerts',
  androidFullScreenCallsBody:
    'Android 14 and newer only shows a full-screen incoming call after you allow it. Calls still arrive without it, but as an ordinary notification.',
  onlineNow: 'online',
  scrollToLatest: 'Jump to latest message',
  sessionExpired: 'Session expired. Please sign in again.',
  photoPickFailed: 'Could not add the photo',
  accessibilityExpandVideo: 'Open fullscreen',
  accessibilityPrevPhoto: 'Previous photo',
  accessibilityNextPhoto: 'Next photo',
  videoResolutionTooHigh: 'Video resolution must be at most 1080p (long edge 1920 px).',
  photoProcessFailed: 'Could not process the image (resize / encode).',
  photoPreviewTitle: 'Send photo',
  photoPreviewBack: 'Back to gallery',
  photoPreviewSend: 'Send',
  photoPreviewImageLabel: 'Selected photo',
  photoPreviewCaptionPlaceholder: 'Add a caption…',
  photoPreviewDiscardTitle: 'Discard changes?',
  photoPreviewDiscardMessage: 'The caption you typed will be lost.',
  photoPreviewDiscard: 'Discard',
  photoPreviewKeepEditing: 'Keep editing',
  photoMasksTitle: "Masks",
  photoMaskBlur: "Blur",
  photoMaskPixelate: "Pixelate",
  photoMaskEmoji: "Sticker",
  photoMaskHideAllFaces: "Hide all faces",
  photoMaskNoFaces: "No faces found",
  photoMaskDetecting: "Looking for faces…",
  photoMaskHint: "Tap to place a mask, tap it again to remove",
  photoFiltersTitle: "Filters",
  photoFilterStrength: "Strength",
  photoFilterOriginal: "Original",
  photoFilterFilm400: "Film 400",
  photoFilterNight800t: "Night 800T",
  photoFilterDigicam2004: "Digicam 2004",
  photoFilterMatte: "Matte",
  photoFilterCinema: "Cinema",
  photoFilterCleanLight: "Clean Light",
  photoFilterMono3200: "Mono 3200",
  photoFilterDuotone: "Duotone",
  photoFilterGlow: "Glow",
  photoFilterInfra: "Infra",
  photoFilterChrome: "Chrome",
  photoFilterNoon: "Noon",
  photoCropTitle: "Crop",
  photoCropApply: "Done",
  photoCropFree: "Free",
  photoCropRotate: "Rotate 90°",
  photoCropReset: "Reset",
  photoCropFailed: "Could not apply the crop",
  photoPreviewRemove: 'Remove this photo',
  photoSendPartialFailed: 'Some photos could not be sent',
  photoQualityEconomy: 'Data saver',
  photoQualityStandard: 'Standard',
  photoQualityMax: 'Maximum',
  photoQualityOriginal: 'Original',
  photoQualityCalculating: 'Preparing…',
  unitKilobytes: 'KB',
  unitMegabytes: 'MB',
  currentPassword: 'Current password',
  newPasswordLabel: 'New password',
  savePassword: 'Save password',
  passwordChangeFailed: 'Could not change password',
  bossUsersTitle: 'Users',
  bossUsersLoadFailed: 'Could not load users',
  bossCreateUserFailed: 'Could not create user',
  bossDeleteUserFailed: 'Could not delete user',
  bossUserLoginPlaceholder: 'Login (letters, digits, ._-)',
  bossDisplayNamePlaceholder: 'Display name',
  bossNewUserPassword: 'Temporary password (auto-filled)',
  bossTempPasswordHint: 'Copy and send to the user; they can change it in settings (⚙).',
  bossRegeneratePassword: 'New password',
  bossAddUser: 'Add user',
  bossDeleteUser: 'Delete',
  bossDeleteConfirmTitle: 'Delete user?',
  bossDeleteConfirmBody: 'This will remove the account and related chats.',
  bossCredentialsSentHint:
    'We sent the user a direct message with these details. You can copy the text below to share elsewhere.',
  bossInviteCopyBody:
    'Hello!\n\nYour SVYAZ account is ready (beta version; more features are coming).\nLogin: {login}\nTemporary password: {password}\n\nAfter you sign in, change your password: Chats list → ⚙ (settings) → enter your current password and a new one.\n\nWhy use this chat:\n• For people who need dependable contact when mainstream messengers are blocked, risky, or too noisy — a smaller, purpose-driven space with calls and chats together.{retention}\n• You stay in control: delete a chat from the app whenever you need.\n\nThis is beta software — protections and features will keep improving.\n\nGroups and calls are available in a basic form today.\n\nWelcome aboard!',
  bossCopyInviteButton: 'Copy login & instructions',
  bossCopyInviteDone: 'Copied to clipboard',
  bossInstructionLanguage:
    "Welcome message language (only this text; does not change the user's app language)",
  forwardedLabel: 'Forwarded',
  forwardedFromPrefix: 'From:',
  savedChatTitle: 'Saved',
  shareIntentTitle: 'Send to',
  shareIntentPhotos: 'Photos: {count}',
  shareIntentSkipped: 'Skipped files: {count}. SVYAZ sends photos, links and text.',
  shareIntentSending: 'Sending… {sent} of {total}',
  shareIntentFailed: 'Could not send',
  shareIntentEmpty: 'No chats yet — start one and share again',
  shareIntentSavedHint: 'Your own space',
  savedChatCardHint: 'Notes, files and links — only you see them',
  savedEmptyTitle: 'Everything here is yours',
  savedEmptyBody: 'Notes, files, links. Nobody but you sees this.',
  saveToSavedAction: 'Add to Saved',
  savedMessageDone: 'Added to Saved',
  savedSourceDeleted: 'The original message was deleted',
  savedOpenFailed: 'Could not open Saved',
  savedSectionAll: 'All',
  savedSectionNotes: 'Notes',
  savedSectionTasks: 'Tasks',
  savedSectionFiles: 'Files',
  savedSectionLinks: 'Links',
  savedSectionVoice: 'Voice',
  savedSectionEmptyNotes: 'No notes yet. Anything you type here without a link lands in this tab.',
  savedSectionEmptyTasks: 'No checklists yet. Send one and it will show up here.',
  savedSectionEmptyFiles: 'No photos or videos saved yet.',
  savedSectionEmptyLinks: 'No links saved yet.',
  savedSectionEmptyVoice: 'No voice notes yet.',
  noteNewAction: 'New note',
  noteTitlePlaceholder: 'Title (optional)',
  noteBodyPlaceholder: 'Note text',
  notePinAction: 'Pin',
  noteUnpinAction: 'Unpin',
  notePinnedBadge: 'Pinned',
  noteSavingHint: 'Saving…',
  noteSavedHint: 'Saved',
  noteColorLabel: 'Color',
  noteEditFailed: 'Could not save the note',
  settingsGenderLabel: 'Gender',
  genderUnset: 'Not set',
  genderMale: 'Male',
  genderFemale: 'Female',
  settingsBirthDateLabel: 'Date of birth',
  settingsBirthDateHint: 'DD.MM.YYYY, age from {min} to {max}. Your contacts see the day and your age — not the year. They get a birthday reminder.',
  settingsBirthDateInvalid: 'Enter the date as DD.MM.YYYY',
  settingsBirthDateAgeRange: 'Age must be between {min} and {max} years',
  profileYearsSuffix: 'y.o.',
  birthdayTodayTitle: 'Birthday today',
  birthdaySoonTitle: 'Birthdays soon',
  birthdayCongratulate: 'Send wishes',
  birthdayTurns: 'turns {age}',
  birthdayInDays: 'in {days} d.',
  birthdayTomorrow: 'tomorrow',
  birthdayPickTemplate: 'Pick a greeting',
  birthdayTemplateHint: 'Tap to put it in the box — you can still edit it before sending.',
  birthdaySend: 'Send',
  birthdaySent: 'Greeting sent',
  birthdaySendFailed: 'Could not send the greeting',
  birthdayHide: 'Hide',
  savedSectionReminders: 'Reminders',
  savedSectionScheduled: 'Scheduled',
  savedSectionEmptyReminders: 'No reminders yet.',
  savedSectionEmptyScheduled: 'No scheduled messages yet.',
  reminderNewAction: 'New reminder',
  reminderTitlePlaceholder: 'Remind me to…',
  reminderBodyPlaceholder: 'Details (optional)',
  reminderWhenLabel: 'When',
  reminderDatePlaceholder: 'DD.MM.YYYY',
  reminderTimePlaceholder: 'HH:MM',
  reminderPrecisionHint: 'Accurate to about a minute.',
  reminderSaveAction: 'Save',
  reminderSaveFailed: 'Could not save the reminder',
  reminderDeleteAction: 'Delete',
  reminderSnoozeAction: 'Snooze 10 min',
  reminderStatusScheduled: 'Scheduled',
  reminderStatusFired: 'Fired',
  reminderStatusCancelled: 'Cancelled',
  reminderInvalidTime: 'Pick a date and time in the future',
  reminderZoneNote: 'Set in {zone}',
  organizerEditAction: 'Edit',
  quickToday: 'Today 18:00',
  quickTomorrow: 'Tomorrow 09:00',
  quickInAnHour: 'In an hour',
  scheduledNewAction: 'Schedule a message',
  scheduledRecipientsLabel: 'To whom',
  scheduledRecipientsEmpty: 'Pick at least one contact',
  scheduledRecipientsSearch: 'Search contacts',
  scheduledTextPlaceholder: 'Message text',
  scheduledWhenLabel: 'When to send',
  scheduledCreateAction: 'Schedule',
  scheduledCancelAction: 'Cancel sending',
  scheduledMessageSaveFailed: 'Could not schedule the message',
  scheduledMessageCancelFailed: 'Could not cancel it',
  scheduledStatusScheduled: 'Waiting',
  scheduledStatusSent: 'Sent',
  scheduledStatusCancelled: 'Cancelled',
  scheduledStatusFailed: 'Not delivered',
  scheduledTooSoon: 'Pick a time at least a minute from now',
  notifyReminderTitle: 'Reminder',
  notifyBirthdayTitle: 'Birthday',
  notifyBirthdayBody: 'has a birthday today',
  registerAction: 'Create an account',
  registerTitle: 'New account',
  registerEmailPlaceholder: 'Email',
  registerNamePlaceholder: 'How should we call you?',
  registerSubmit: 'Sign up',
  registerEmailInvalid: 'This does not look like an email address',
  registerPasswordTooShort: 'At least 8 characters',
  registerNameRequired: 'Please enter a name',
  registerInviteOnly: 'Sign-up is by invitation only right now',
  registerFailed: 'Could not sign up',
  verifySentTitle: 'Check your inbox',
  verifySentBody: 'We sent a confirmation link to {email}. Open it to finish signing up.',
  verifyResend: 'Send the link again',
  verifyResendDone: 'If that address is waiting for confirmation, the link is on its way.',
  backToLogin: 'Back to sign in',
  forgotAction: 'Forgot your password?',
  forgotTitle: 'Password recovery',
  forgotBody: 'Enter the address you signed up with and we will send a link to set a new password.',
  forgotSubmit: 'Send the link',
  forgotSentTitle: 'Check your inbox',
  forgotSentBody: 'If an account uses {email}, the link is on its way. It works once and expires in an hour.',
  resetTitle: 'New password',
  resetBody: 'Set a new password. Everyone signed in to this account will be signed out.',
  resetNewPassword: 'New password',
  resetSubmit: 'Save and sign in',
  resetDone: 'Password changed. Sign in with the new one.',
  resetFailed: 'Could not change the password',
  verifyDone: 'Address confirmed. You can sign in now.',
  verifyLinkDead: 'The link did not work: it expired or was already used. Ask for a new one.',
  captchaRequired: 'Please confirm you are not a robot and try again.',
  forwardSelectCount: 'Selected: {count}',
  selectMessagesAction: 'Select',
  deleteSelectedAction: 'Delete',
  deleteSelectedConfirmTitle: 'Delete selected messages?',
  deleteSelectedConfirmBody: 'Messages: {count}. They will disappear for everyone in the chat and cannot be brought back.',
  deleteSelectedFailed: 'Some messages could not be deleted',
  forwardAction: 'Forward',
  forwardDoneBody: 'Forwarded {count} messages',
  replyAction: 'Reply',
  replyQuoteLabel: 'Reply',
  replyOriginalDeleted: 'Message deleted',
  copyTextAction: 'Copy text',
  copyTextDone: 'Copied',
  editAction: 'Edit',
  editingMessageTitle: 'Editing',
  editMessageFailed: 'Could not edit the message',
  messageEditedMark: 'edited',
  deleteMessageAction: 'Delete',
  deleteMessageFailed: 'Could not delete the message',
  reactionLimitReached: 'Up to three reactions per message',
  savedClearAction: 'Clear',
  savedClearDone: 'Saved is empty now',
  savedClearFailed: 'Could not clear Saved',
  helpCenterTitle: 'Tips and news',
  helpCenterOpenLabel: 'Tips and news',
  helpCenterOpenLabelUnread: 'Tips and news — unread entries',
  helpCenterNewBadge: 'New',
  helpCenterAllReadHint: 'Everything read. New entries make the bulb blink again.',
  helpCenterIntro: 'What the app can do and how to use it. New entries go on top and make the bulb in the header blink.',
  settingsHandleSuggestions: 'Free options',
  saveChatImage: 'Save image',
  saveChatImageSuccess: 'Image saved',
  saveChatImageFailed: 'Could not save the image',
  saveChatImagePermissionDenied: 'Allow photo library access to save images',
  saveChatVideo: 'Save video',
  saveChatVideoSuccess: 'Video saved',
  saveChatVideoFailed: 'Could not save the video',
  saveChatVideoPermissionDenied: 'Allow photo library access to save videos',
  forwardSameChatBlocked: 'Cannot forward messages to the same chat',
  messageReferenceNotFound: 'Original message not found in loaded history',
  gearSettings: 'Account settings',
  myDruzhina: 'My Team',
  myDruzhinaHint: 'Keep only those you trust like yourself on your team.',
  myDruzhinaVarangian: 'Hird',
  myDruzhinaHintVarangian: 'The hird holds only those you trust like yourself.',
  contactRequestIncomingTitle: 'Incoming requests',
  contactLoadFailed: 'Could not load contacts',
  contactRequestLoadFailed: 'Could not load requests',
  contactRequestSendFailed: 'Could not send request',
  contactRequestApproveFailed: 'Could not approve request',
  contactRequestRejectFailed: 'Could not decline request',
  contactSearchSelfRow: 'You',
  chatLastMessageFallback: 'No messages yet',
  unseenReactionA11y: 'New reaction: {emoji}',
  settingsHapticsLabel: 'Haptic feedback',
  settingsSoundsLabel: 'Sounds',
  retentionNoticeTitle: 'Conversation cleared automatically',
  retentionNoticeBody:
    'Direct messages are removed from the server after a few days — that is by design, nothing was lost.',
  retentionNoticeWhen: 'Last cleared: {when}',
  contactRemoveConfirmTitle: 'Remove from the team?',
  contactRemoveConfirmTitleVarangian: 'Remove from the hird?',
  contactRemoveConfirmBody:
    'This will remove both of you from contacts and permanently delete your direct chat history for both sides.',
  contactRemoveFailed: 'Could not remove contact',
  profileUpdateFailed: 'Could not update profile',
  settingsProfileSection: 'Profile',
  settingsChangeNameLabel: 'Change display name',
  settingsChangeLoginLabel: 'Change login',
  settingsSaveProfile: 'Save profile',
  settingsSaved: 'Changes saved',
  settingsShareSection: 'Public link & handle',
  settingsPublicHandleLabel: 'Public handle (without #)',
  settingsPublicHandleHint:
    '4–32 characters: lowercase letters, digits, underscore. Clear the field to remove your handle.',
  settingsProfilePublicLabel: 'Public profile page',
  settingsAllowDiscoveryLabel: 'Discoverable by handle in search',
  settingsKeepPhotoMetadataLabel: 'Keep photo metadata',
  settingsKeepPhotoMetadataHint:
    'Off: location, camera model and serial number are removed from photos you send; orientation and capture date stay. On: the file is sent with everything it contains.',
  settingsHandleWillSaveAs: "Will be saved as: #{handle}",
  settingsHandleChecking: "Checking…",
  settingsHandleFree: "Handle is available",
  settingsHandleTaken: "Handle is taken",
  settingsHandleFormat: "4–32 characters: lowercase latin letters, digits, underscore",
  settingsHandleReserved: "This handle is reserved",
  settingsHandleNotAllowed: "This handle is not allowed",
  settingsHandleSaveFailedInvalid: "The server rejected this handle. Try another one.",
  settingsHandleConfirmChangeTitle: "Change your handle?",
  settingsHandleConfirmChangeBody: "Links with your old handle will stop working, and someone else can take it.",
  settingsHandleConfirmClearTitle: "Remove your handle?",
  settingsHandleConfirmClearBody: "Your public page will close, links will stop working, and the handle becomes available to others.",
  settingsHandleKeep: "Keep",
  settingsSaveShareSettings: 'Save handle',
  settingsCopyHandleTag: 'Copy #handle',
  settingsCopied: 'Copied',
  profileLinkUnavailable: "This profile is unavailable or the handle no longer exists.",
  profileLinkSelf: "That link points to your own profile.",
  settingsShareDisabledHidden: "Public profile page is off — turn it on to share the link.",
  settingsShareDisabledUnsaved: "Save the handle first — the link is built from the saved one.",
  settingsProfileSaved: "Profile saved",
  settingsShareSaved: "Handle saved",
  settingsPasswordChanged: "Password changed",
  reportAction: "Report",
  reportTitle: "What is wrong with this?",
  reportSent: "Report sent. We will look into it.",
  reportFailed: "Could not send the report",
  reportCategorySpam: "Spam or advertising",
  reportCategoryHarassment: "Harassment or threats",
  reportCategorySexualContent: "Sexual content",
  reportCategoryViolence: "Violence",
  reportCategoryFraud: "Scam or fraud",
  reportCategoryChildSafety: "Child safety",
  reportCategoryOther: "Something else",
  blockAction: "Block user",
  blockConfirmTitle: "Block this person?",
  blockConfirmHint: "They will not be able to message or call you. You can undo this in Settings → Privacy.",
  blockDone: "Blocked",
  blockFailed: "Could not block",
  unblockAction: "Unblock",
  settingsBlockedSection: "Blocked people",
  settingsBlockedEmpty: "You have not blocked anyone",
  moderationCancel: "Cancel",
  settingsDeleteAccountSection: "Delete account",
  settingsDeleteAccountWarning:
    "Deleting is permanent. Your profile, contacts, reminders and Saved messages are erased. Messages you sent stay in other people's chats, signed «Deleted account».",
  settingsDeleteAccountStart: "Delete account",
  settingsDeleteAccountConfirmHint:
    "Enter your password and type the word below to confirm.",
  settingsDeleteAccountConfirmWord: "DELETE",
  settingsDeleteAccountConfirmPlaceholder: "Type DELETE",
  settingsDeleteAccountConfirm: "Delete permanently",
  settingsDeleteAccountCancel: "Cancel",
  settingsDeleteAccountPasswordRequired: "Enter your current password",
  settingsDeleteAccountWordMismatch: "The confirmation word does not match",
  settingsDeleteAccountFailed: "Could not delete the account",
  settingsLegalSection: "Legal",
  settingsPrivacyPolicyLink: "Privacy policy",
  settingsTermsLink: "Terms of use",
  settingsLocalOnlyHint: "Applies immediately, on this device only",
  settingsNamePlaceholder: "Your name",
  settingsPublicHandlePlaceholder: "my_handle",
  settingsAvatarTitle: 'Profile photo',
  settingsAvatarChooseShape: 'Frame',
  settingsAvatarRemove: 'Remove photo',
  settingsAvatarUploadFailed: 'Could not upload photo',
  avatarAnimatedTooLarge: 'This animation is too heavy — pick a GIF under 8 MB.',
  galleryPermissionDenied:
    'Photo access is off. Turn it on in system settings — access to selected photos is enough.',
  settingsAvatarWebHint: 'Web: pick a file in the dialog; upload goes over multipart.',
  avatarChangePhoto: 'Change photo',
  avatarTapToChange: 'Tap the photo to change it',
  avatarTakePhoto: 'Take photo',
  avatarFromLibrary: 'Choose from library',
  avatarCameraDenied: 'No camera access. Turn it on in system settings.',
  avatarRemoveConfirm: 'The photo will be gone for everyone; initials stay.',
  avatarEditorHint: 'Pinch to zoom, drag to move, double tap to reset',
  avatarEditorHintPanOnly: 'Drag to move the photo',
  avatarPreviewCard: 'See how others view you',
  chatAvatarSectionTitle: 'Chat photo',
  userCardTitle: 'User',
  userCardOpenPhoto: 'Open photo',
  userCardPhotoHint: 'Pinch or double tap to zoom, tap to close',
  userCardPhotoFriendsOnly: 'Only friends can open the photo full screen',
  userCardPhotoLoadFailed: 'Could not load the photo',
  chatUnknownMember: 'Member',
  contactCardLabel: 'Contact',
  contactShareAction: 'Share contact',
  contactShareChooseChat: 'Send contact to…',
  contactShareSent: 'Contact sent',
  contactShareFailed: 'Could not send the contact',
  checklistItemA11y: 'Checklist item',
  checklistCheckedBy: 'Checked by {name}',
  checklistToggleFailed: 'Could not update the checklist',
  checklistDuePast: 'That deadline has already passed. Pick a time still to come.',
  checklistDueUnparsed: 'Could not read the deadline. Write {20:00} or {02.02.2026 15:00}.',
  checklistAssigneeInvalid: 'One assignee per item, written as a handle: #nickname',
  checklistItemEmpty: 'An item needs text: brackets and a deadline go with it, not instead of it.',
  checklistAssigneeNotFound: 'No profile with that handle',
  checklistDueToday: 'today {time}',
  checklistDueTomorrow: 'tomorrow {time}',
  checklistOverdue: 'Overdue',
  checklistDueA11y: 'Due {when}',
  checklistAssigneeA11y: 'Assigned to {name}',
  channelPostCommentsCount: '💬 {count}',
  settingsLoginHint: 'Use letters, numbers, dot, underscore, dash',
  loginCheckInProgress: 'Checking login...',
  loginAvailable: 'Login is available',
  loginUnavailable: 'Login is not available',
  chatTileCallAudio: 'Voice call',
  chatTileCallVideo: 'Video call',
  channelReadOnlyHint: 'Read-only channel: only admins and editors can post.',
  channelCommentComposerHint: 'To comment, tap 💬 Comments under a post.',
  channelUnsubscribeConfirmBody: 'Unsubscribe from this channel? It will disappear from your chat list.',
  channelUnsubscribeFailed: 'Could not unsubscribe from the channel.',
  channelPostRestrictedHint: 'You cannot publish a post in this channel.',
  channelArticlesLoadFailed: 'Could not load articles.',
  channelArticlesDrafts: 'Drafts',
  channelArticleStatusDraft: 'Draft',
  channelDraftSavedTitle: 'Draft saved',
  channelDraftSavedBody: 'Your draft is in the Articles section at the top of this channel.',
  channelDraftView: 'Open draft',
  channelPostPublishedTitle: 'Article published',
  channelSettingsTitle: 'Channel settings',
  channelDraftsSettingsHint: 'Unpublished drafts — tap to edit or publish.',
  channelDraftsEmpty: 'No drafts.',
  deleteChannelConfirmBody: 'The channel and all its articles will be permanently deleted.',
  channelPostOpenFailed: 'Could not open this article.',
  channelPostLoadFailed: 'Could not load article.',
  channelPostComments: 'Comments',
  channelCommentsClosed: 'Commenting is closed in this channel.',
  channelPublishAction: 'Publish',
  channelPublishDraftTitle: 'Publish this draft?',
  channelPublishFailed: 'Could not publish the article.',
  channelDraftOpen: 'Open',
  channelSubscribedTitle: 'Subscribed',
  channelGuestSubscribeHint:
    'You are reading this channel as a guest. {count} people are subscribed — new articles will reach you too.',
  channelArchiveTitle: 'All articles',
  channelArchiveEmpty: 'No articles published yet.',
  channelArchiveLoadMore: 'Show more',
  editorPreview: 'Preview',
  editorPreviewBack: 'Back to editing',
  editorInsertHint: 'Pick a block type below — it will go right after this one.',
  editorInvalidChannel:
    'This screen was opened without a valid channel. Close it, open the channel from the list and start a new article there.',
  blockParagraph: 'Paragraph',
  blockHeading: 'Heading',
  blockImage: 'Image',
  blockQuote: 'Quote',
  blockBulletList: 'Bullet list',
  blockOrderedList: 'Numbered list',
  blockDivider: 'Divider',
  blockCode: 'Code',
  blockEmbed: 'Embed',
  blockVideo: 'Video link',
  blockGallery: 'Gallery',
  editorTitleLabel: 'Title',
  editorTitlePlaceholder: 'Article title',
  editorBodyLabel: 'Body',
  editorAddCover: 'Add cover image',
  editorChangeCover: 'Change cover',
  editorRemoveCover: 'Remove cover',
  editorSaveDraft: 'Save draft',
  editorPublish: 'Publish',
  editorListItem: 'List item',
  editorBlockEmpty: 'empty',
  editorReplaceImage: 'Replace image',
  editorUploadImage: 'Upload image',
  editorAddPhotos: 'Add photos',
  editorAddBlockA11y: 'Add: {name}',
  editorInsertHere: 'Insert block',
  commentsTitle: 'Comments',
  commentsReply: 'Reply',
  commentsReplyingTo: 'Replying to {name}',
  commentsPlaceholder: 'Write a comment…',
  commentsSend: 'Send',
  commentsPending: 'pending',
  commentsThumbUp: 'Like',
  commentsDelete: 'Delete',
  commentsApprove: 'Approve',
  commentsBan: 'Ban',
  commentsDeleteConfirmBody: 'The comment disappears from the discussion.',
  commentsBanConfirmTitle: 'Ban from comments?',
  commentsBanConfirmBody: '{name} will no longer be able to comment in this channel.',
  commentsModerationFailed: 'The action did not go through.',
  articleReadMore: 'Read article →',
  articleCardKicker: 'Article',
  channelMuteOn: 'Mute notifications',
  channelMuteOff: 'Unmute notifications',
  channelMuteFailed: 'Could not change notifications.',
  channelViewsCount: '👁 {count}',
  channelPostEdit: 'Edit',
  channelPostEditTitle: 'Edit article',
  channelPostEditPublishedHint:
    'Saving updates the article for everyone who already has it. Subscribers are not notified again.',
  channelPostSaveChanges: 'Save changes',
  channelPostSaved: 'Changes saved',
  channelPostSaveFailed: 'Could not save the article.',
  channelPostDelete: 'Delete',
  channelPostDeleteConfirmTitle: 'Delete this article?',
  channelPostDeleteConfirmBody:
    'The article and its announcement in the channel will be gone for good. Subscribers who already opened it will lose it too.',
  channelPostDeleteDraftConfirmBody: 'The draft will be gone for good.',
  channelPostDeleteFailed: 'Could not delete the article.',
  channelPostUnpublish: 'Move to drafts',
  channelPostUnpublishConfirmTitle: 'Move back to drafts?',
  channelPostUnpublishConfirmBody:
    'The announcement disappears from the channel feed. The text stays in your drafts, and you can publish it again.',
  channelPostUnpublishFailed: 'Could not move the article to drafts.',
  channelDraftRestoredHint: 'Unsaved text from your last visit was restored.',
  channelDraftLeaveTitle: 'Leave the editor?',
  channelDraftLeaveBody:
    'The unsaved text stays on this device — the editor will offer it back next time you open it.',
  channelDraftLeaveConfirm: 'Leave',
  channelDraftLeaveStay: 'Keep writing',
  channelDraftDiscardRestored: 'Discard restored text',
  channelSlugLabel: 'Channel address',
  channelSlugPlaceholder: 'my_channel',
  channelSlugHint:
    '4–32 characters: lowercase Latin letters, digits, underscore. Used in the channel link.',
  channelSlugInvalid:
    'The address must be 4–32 characters long and use only lowercase Latin letters, digits and underscores.',
  channelCommentSendFailed: 'Comment not sent.',
  channelCommentsLoadFailed: 'Could not load the comments.',
  chatFilterGroup: 'Groups',
  chatFilterChannel: 'Channels',
  chatSortRecent: 'newest first',
  chatSortOld: 'oldest first',
  chatSortTitleAsc: 'by name A-Z',
  chatSortTitleDesc: 'by name Z-A',
  callScopeBadgeGroupVideo: 'Group video call',
  callScopeBadgeDirectVideo: 'Direct video call',
  groupCallUnavailableTitle: 'Group calls are unavailable',
  groupCallsTemporarilyOff: 'Group calls are temporarily disabled. One-to-one calls still work.',
  groupCallUnavailableDisabled: 'The group-call server is not configured. Please contact support.',
  groupCallUnavailableNetwork: 'Could not reach the call server. Check your connection and try again.',
  groupCallUnavailableConnect: 'Could not connect to the call. Try again in a moment.',
  groupCallJoinOngoing: 'Join',
  groupCallOngoingBadge: 'Call in progress',
  callDiagnosticsTitle: 'Call diagnostics',
  callDiagnosticsHint: 'Long press to open call diagnostics',
  callDiagnosticsCopy: 'Copy report',
  callDiagTransport: 'Transport',
  callDiagCandidatePair: 'Candidate pair',
  callDiagBitrate: 'Bitrate',
  callDiagPacketsLost: 'Packets lost',
  callDiagParticipants: 'Participants',
  screenShareTitle: 'Screen share',
  screenShareBody: 'This browser did not allow screen capture.',
  screenShareBusyBody: 'Someone is already sharing their screen. Wait until they stop.',
  screenShareLabel: 'is sharing their screen',
  callShareScreen: 'Share screen',
  callShareScreenStop: 'Stop sharing',
  callSwitchCamera: 'Flip camera',
  settingsAppearanceSection: 'Appearance',
  settingsVisualThemeLabel: 'App style',
  visualThemeNeutral: 'Neutral',
  visualThemeVarangian: 'Varangian',
  visualThemeDark: 'Dark',
  visualThemeAuto: 'Auto · Sun / Cosmos',
  settingsLanguageSection: 'Language',
  settingsLanguageLabel: 'Interface language',
  settingsTabPreferences: 'App',
  settingsTabAccount: 'Account',
  settingsTabAvatar: 'Photo',
  settingsTabProfile: 'Profile',
  settingsTabPrivacy: 'Privacy',
  settingsLoginSection: 'Sign-in login',
  settingsSaveLogin: 'Save login',
  settingsLoginSaved: 'Login saved',
  settingsPasswordSection: 'Password',
  settingsVisibilitySection: 'Who can find you',
  settingsProfilePublicHint: 'When off, your profile link stops opening for everyone.',
  settingsAllowDiscoveryHint: 'When off, people can no longer find you by handle in search.',
  settingsMediaPrivacySection: 'Photos and data',
  settingsShareDisabledNoHandle: 'Pick a handle and save it — the link is built from it.',
  settingsPreferencesHint: 'Theme and language apply as soon as you pick them — no password needed.',
  settingsPreferencesDone: 'Done',
  settingsCurrentPasswordRequired: 'Enter your current password to set a new one.',
  directRetentionPolicyTitle: 'Direct chat cleanup',
  directRetentionPolicyBody:
    'For security, server-side history in personal chats is cleared on a rolling schedule. A cleanup is coming soon. Save anything you need—messages, photos, or files—to your device.',
  directRetentionTimerA11y: 'Time until server clears this chat history. Tap for details.',
  quickRepliesTitle: 'Quick replies',
  quickReplyAddTitle: 'Add a quick reply',
  quickReplyAddPlaceholder: 'Your phrase…',
  quickReplySave: 'Save',
  quickReplyDelete: 'Delete',
  quickReplyDeleteConfirmTitle: 'Delete quick reply?',
  quickReplyDefaultGreeting: 'Hello!',
  quickReplyDefaultInTouch: 'I’m around',
  quickReplyDefaultThanks: 'Thanks!',
  sharePost: 'Share post',
  shareChannel: 'Share channel',
  shareProfile: 'Share profile',
  settingsBioLabel: 'About me',
  settingsBioPlaceholder: 'A few words about yourself…',
  settingsStatusLabel: 'Status',
  settingsStatusPlaceholder: 'What are you up to?',
  selfStatusEmpty: 'Add a status',
  quickSettingsTitle: 'Quick settings',
  selfStatusSave: 'Save',
  dndLabel: 'Do not disturb',
  dndHint: 'No notifications from anyone. Calls still come through.',
  dndFailed: 'Could not change the setting',
  notifyShowTextLabel: 'Show message text',
  notifyShowTextHint: 'Off: notifications say only "New message", without the text.',
  userCardAddContact: 'Add contact',
  userCardContactPending: 'Request sent',
  navTitleNewPost: 'New article',
  navTitlePost: 'Article',
};

const ru: Record<BaseMessageKey, string> = {
  appName: 'SVYAZ',
  appTagline: 'Звонки и чаты — оставайтесь на связи',
  loginEpigraphCalm:
    'Спокойное пространство для тех, кому важна надёжная связь: переписка и звонки в одном приложении с упором на приватность.',
  loginEpigraphVarangian:
    'В это нелёгкое время старым варягам ничто не помеха и не повод отчаиваться, мы всегда найдем способ быть на связи! Skol',
  loginField: 'Логин или почта',
  passwordPlaceholder: 'Пароль (мин. 8 символов)',
  loginRequired: 'Введите логин или адрес почты',
  loginInvalid: 'Логин — латинские буквы, цифры, точка, дефис и подчёркивание. Или введите адрес почты',
  passwordRequired: 'Введите пароль',
  validationFailed: 'Проверьте введённые данные',
  validationFieldRequired: 'Заполните обязательное поле',
  validationFieldLength: 'Недопустимая длина значения',
  validationFieldFormat: 'В значении есть недопустимые символы',
  errorGeneric: 'Ошибка',
  signIn: 'Войти',
  logout: 'Выйти',
  group: 'Группа',
  chatTypeGroup: 'Группа',
  chatTypeChannel: 'Канал',
  noChatsHint: 'Нет чатов — создайте личный или групповой',
  nicknamePlaceholder: 'Ник или часть имени…',
  searchError: 'Ошибка поиска',
  nobodyFound: 'Никого не нашли',
  close: 'Закрыть',
  settingsUnsavedTitle: "Есть несохранённые изменения",
  settingsUnsavedBody: "Сохранить их перед закрытием?",
  settingsUnsavedDiscard: "Закрыть без сохранения",
  settingsUnsavedStay: "Вернуться",
  settingsNewPasswordTooShort: "Новый пароль — не короче 8 символов",
  newChannelTitle: 'Новый канал',
  channelNamePlaceholder: 'Название канала',
  channelEditorsHint: 'Редакторы могут публиковать посты в этом канале.',
  channelInviteSubscribersHint: 'Приглашённые станут подписчиками. Редакторов назначайте отдельно в настройках канала.',
  addSubscriber: 'Добавить подписчика',
  addEditor: 'Добавить редактора',
  channelAllowReactions: 'Разрешить реакции',
  channelAllowComments: 'Разрешить комментарии',
  createChannelFailed: 'Не удалось создать канал',
  channelNameRequired: 'Введите название канала',
  channelDescriptionPlaceholder: 'Описание',
  channelsHubTitle: 'Каналы',
  channelSearchPlaceholder: 'Поиск каналов…',
  channelsHubEmpty: 'Пока нет каналов',
  channelPreviewTitle: 'Предпросмотр канала',
  channelPreviewEmptyPosts: 'Опубликованных постов пока нет',
  channelPreviewUnavailable: 'У этого канала нет публичного предпросмотра — подпишитесь, чтобы читать',
  channelPreviewSubscribeHint: 'Подпишитесь, чтобы канал появился в списке чатов',
  channelSubscribe: 'Подписаться',
  channelUnsubscribe: 'Отписаться',
  channelManage: 'Управление',
  channelSubscribersCount: 'Подписчиков: {count}',
  channelDirectoryLoadFailed: 'Не удалось загрузить каналы',
  channelSubscriptionFailed: 'Не удалось изменить подписку',
  channelUpdateFailed: 'Не удалось обновить канал',
  channelEditorAddFailed: 'Не удалось добавить редактора',
  channelEditorRemoveFailed: 'Не удалось убрать редактора',
  deleteChannel: 'Удалить канал',
  saveChannel: 'Сохранить канал',
  groupNamePlaceholder: 'Название',
  removeMember: 'Убрать',
  cancel: 'Отмена',
  dialogOk: 'Понятно',
  create: 'Создать',
  groupInfoTitle: 'Информация о группе',
  groupTabMembers: 'Участники',
  groupTabSettings: 'Настройки',
  groupMembersLabel: 'Участников: {count}',
  groupYouSuffix: '(вы)',
  groupAdminBadge: 'Админ',
  groupSearchPlaceholder: 'Найти пользователей для добавления…',
  groupNoUsersFound: 'Пользователи не найдены',
  groupAddAction: 'Добавить',
  groupRetry: 'Повторить',
  groupLeaveBtn: 'Покинуть группу',
  groupLeaveTitle: 'Покинуть группу',
  groupLeaveBody: 'Вы уверены, что хотите покинуть эту группу?',
  groupLeaveConfirm: 'Покинуть',
  groupLeaveFailed: 'Не удалось покинуть группу',
  groupNameLabel: 'Название группы',
  groupDescLabel: 'Описание',
  groupDescPlaceholder: 'Описание группы…',
  groupSaveChanges: 'Сохранить изменения',
  groupNameRequired: 'Укажите название группы',
  groupInfoSaved: 'Данные группы обновлены',
  groupInfoSaveFailed: 'Не удалось сохранить данные группы',
  groupChangePhoto: 'Изменить фото',
  groupRemoveMemberTitle: 'Удалить участника',
  groupRemoveMemberBody: 'Удалить {name} из группы?',
  groupRemoveAction: 'Удалить',
  groupRemoveMemberFailed: 'Не удалось удалить участника',
  groupMakeAdminTitle: 'Назначить админом',
  groupRemoveAdminTitle: 'Снять админа',
  groupMakeAdminBody: 'Назначить {name} администратором?',
  groupRemoveAdminBody: 'Снять права администратора с {name}?',
  groupChangeRoleFailed: 'Не удалось изменить роль',
  groupAddMemberFailed: 'Не удалось добавить участника',
  groupLoadMembersFailed: 'Не удалось загрузить участников',
  deleteChatConfirmTitle: 'Удалить чат?',
  deleteChatFailed: 'Не удалось удалить чат',
  errorTitle: 'Ошибка',
  openChatFailed: 'Не удалось открыть чат',
  createGroupFailed: 'Не удалось создать группу',
  callUnavailableTitle: 'Звонок недоступен',
  webrtcExpoGoChat:
    'Голос — только в dev-сборке (не в Expo Go). mobile: npx expo prebuild, затем run:android / run:ios.',
  webrtcExpoGoCall:
    'Голос — только в dev-сборке (не в Expo Go). В папке mobile: npx expo prebuild, затем npx expo run:android или run:ios.',
  webrtcWebChat: 'Нужны WebRTC, микрофон и HTTPS (или localhost).',
  webrtcWebCall: 'Нужен браузер с WebRTC, доступ к микрофону и HTTPS (или localhost).',
  loadMessagesFailed: 'Не удалось загрузить сообщения',
  sendFailed: 'Ошибка отправки',
  messageSending: 'Отправляется…',
  messageUploading: 'Загружается…',
  messageCompressing: 'Сжимается…',
  messageNotDelivered: 'Не доставлено',
  audioStopRecord: 'Остановить запись',
  audioStartRecord: 'Записать голосовое',
  accessibilityAttachMenu: 'Прикрепить',
  audioCancelRecord: 'Отменить запись',
  accessibilitySend: 'Отправить',
  audioPlay: 'Прослушать',
  audioPause: 'Воспроизводится…',
  transcriptShow: 'Текст',
  transcriptHide: 'Свернуть',
  transcriptPending: 'Расшифровывается…',
  transcriptReadAll: 'Читать целиком',
  transcriptFailed: 'Не удалось расшифровать',
  transcriptTooLong: 'Запись слишком длинная для расшифровки',
  transcriptSheetTitle: 'Текст голосового',
  audioRecordFailed: 'Не удалось записать голосовое сообщение',
  audioPlayFailed: 'Не удалось воспроизвести голосовое сообщение',
  audioRecordUnsupported: 'Ваш браузер не поддерживает запись голосовых',
  audioRecordSecureContext: 'Для записи в web нужен HTTPS или localhost',
  videoRecordFailed: 'Не удалось записать видео-сообщение',
  videoDurationLimit: 'Видео-сообщение должно быть не длиннее 44 секунд',
  videoPayloadTooLarge:
    'Файл слишком большой. Видео: мягкий лимит 100 МБ, битрейт до 5 Мбит/с, 1080p.',
  callReady: 'Готов к вызову',
  callCalling: 'Звоним… ждём ответа',
  callConnecting: 'Соединение…',
  callReconnecting: 'Переподключение…',
  messagePlaceholder: 'Сообщение…',
  emojiHide: 'Скрыть эмодзи',
  emojiShow: 'Эмодзи',
  emojiFrequent: 'Часто используемые',
  emojiPopular: 'Частые',
  emojiDarkSkin: 'Темная кожа (HD)',
  endCall: 'Завершить',
  accessibilityCall: 'Позвонить',
  callNavOutgoing: 'Звонок',
  callNavIncoming: 'Входящий',
  callIncomingFull: 'Входящий звонок',
  callIncomingAudioLine: 'Аудиозвонок',
  callIncomingVideoLine: 'Видеозвонок',
  callIncomingChipAudio: 'Аудио',
  callIncomingChipVideo: 'Видео',
  callTalkingAudio: 'Разговор (аудио)',
  callDuration: 'Длительность звонка',
  callBtnAnswer: 'Ответить',
  callBtnReject: 'Отклонить',
  callBtnHangup: 'Завершить',
  callFullScreenIntentWarn:
    'В системе отключены полноэкранные уведомления о звонках — экран входящего может не показаться при блокировке.',
  callFullScreenIntentSettings: 'Полноэкранные звонки (настройки системы)',
  callUseSpeaker: 'Громкая связь',
  callUseEarpiece: 'Трубка',
  callMicMute: 'Выключить микрофон',
  callMicUnmute: 'Включить микрофон',
  callMicUnavailable: 'Микрофон не включился — вас не слышат.',
  callSpeakingNow: 'Сейчас говорит',
  accessibilityVideoCall: 'Видеозвонок',
  nonJsonServer:
    'Сервер вернул не JSON. Проверьте, что бэкенд запущен (порт 18080) и адрес API: EXPO_PUBLIC_API_BASE или локальный web на localhost →127.0.0.1:18080.',
  loginFailed: 'Ошибка входа',
  loginTooManyAttempts: 'Слишком много попыток входа. Подождите немного и попробуйте снова.',
  searchFailed: 'Не удалось выполнить поиск',
  chatFallback: 'Чат',
  newMessage: 'Новое сообщение',
  callGeneric: 'Звонок',
  callGroupIncomingFrom: '{name} звонит',
  callScopeBadgeGroupAudio: 'Групповой звонок',
  callParticipantOrdinal: 'Участник {n}',
  callParticipantsInCall: 'В звонке: {count}',
  callEventMissedOutgoing: '📞 Не дозвонились',
  callEventMissedIncoming: '📞 Пропущенный',
  callEventDeclinedOutgoing: '📞 Вызов отклонён',
  callEventBusy: '📞 Занято',
  callEventEnded: '📞 Разговор завершён',
  peerDefault: 'Собеседник',
  callVideoFallbackAudio: 'Камера недоступна, отвечаем только голосом.',
  callPeerBusy: 'Линия занята.',
  notifyChannelMessages: 'Сообщения',
  notifyChannelMessagesSilent: 'Сообщения (приложение открыто)',
  notifyChannelCalls: 'Звонки',
  notifyBodyMessage: 'Вам написали',
  notifyContactRequestTitle: 'Заявка в друзья',
  notifyContactRequestBody: 'хочет добавить вас в друзья',
  notifyCommentReplyTitle: 'Ответ на ваш комментарий',
  androidNotifyCallsTitle: 'Включите уведомления',
  androidNotifyCallsBody:
    'Звонки и сообщения приходят через уведомления. Включите их для приложения в настройках системы — иначе входящие в фоне могут не показываться.',
  androidNotifyCallsOpenSettings: 'Открыть настройки',
  androidNotifyCallsLater: 'Позже',
  androidFullScreenCallsTitle: 'Полноэкранные звонки',
  androidFullScreenCallsBody:
    'Android 14 и новее показывает входящий на весь экран только с вашего разрешения. Без него звонки всё равно приходят, но обычным уведомлением.',
  onlineNow: 'в сети',
  scrollToLatest: 'К последнему сообщению',
  sessionExpired: 'Сессия истекла. Войдите снова.',
  photoPickFailed: 'Не удалось добавить фото',
  accessibilityExpandVideo: 'На весь экран',
  accessibilityPrevPhoto: 'Предыдущий снимок',
  accessibilityNextPhoto: 'Следующий снимок',
  videoResolutionTooHigh: 'Разрешение видео не выше 1080p (длинная сторона 1920 px).',
  photoProcessFailed: 'Не удалось обработать изображение (сжатие / кодирование).',
  photoPreviewTitle: 'Отправить фото',
  photoPreviewBack: 'Назад к выбору',
  photoPreviewSend: 'Отправить',
  photoPreviewImageLabel: 'Выбранное фото',
  photoPreviewCaptionPlaceholder: 'Добавить подпись…',
  photoPreviewDiscardTitle: 'Отменить изменения?',
  photoPreviewDiscardMessage: 'Набранная подпись будет потеряна.',
  photoPreviewDiscard: 'Отменить',
  photoPreviewKeepEditing: 'Продолжить',
  photoMasksTitle: "Маски",
  photoMaskBlur: "Размыть",
  photoMaskPixelate: "Пикселизация",
  photoMaskEmoji: "Стикер",
  photoMaskHideAllFaces: "Скрыть все лица",
  photoMaskNoFaces: "Лица не найдены",
  photoMaskDetecting: "Ищем лица…",
  photoMaskHint: "Коснитесь, чтобы поставить маску; повторное касание убирает её",
  photoFiltersTitle: "Фильтры",
  photoFilterStrength: "Сила",
  photoFilterOriginal: "Оригинал",
  photoFilterFilm400: "Плёнка 400",
  photoFilterNight800t: "Ночь 800T",
  photoFilterDigicam2004: "Цифра 2004",
  photoFilterMatte: "Матовый",
  photoFilterCinema: "Кино",
  photoFilterCleanLight: "Чистый свет",
  photoFilterMono3200: "Моно 3200",
  photoFilterDuotone: "Дуотон",
  photoFilterGlow: "Сияние",
  photoFilterInfra: "Инфра",
  photoFilterChrome: "Хром",
  photoFilterNoon: "Полдень",
  photoCropTitle: "Кадр",
  photoCropApply: "Готово",
  photoCropFree: "Свободно",
  photoCropRotate: "Повернуть на 90°",
  photoCropReset: "Сбросить",
  photoCropFailed: "Не удалось применить кадрирование",
  photoPreviewRemove: 'Убрать это фото',
  photoSendPartialFailed: 'Часть фото отправить не удалось',
  photoQualityEconomy: 'Экономно',
  photoQualityStandard: 'Обычное',
  photoQualityMax: 'Максимум',
  photoQualityOriginal: 'Оригинал',
  photoQualityCalculating: 'Готовим…',
  unitKilobytes: 'КБ',
  unitMegabytes: 'МБ',
  currentPassword: 'Текущий пароль',
  newPasswordLabel: 'Новый пароль',
  savePassword: 'Сохранить пароль',
  passwordChangeFailed: 'Не удалось сменить пароль',
  bossUsersTitle: 'Пользователи',
  bossUsersLoadFailed: 'Не удалось загрузить список',
  bossCreateUserFailed: 'Не удалось создать пользователя',
  bossDeleteUserFailed: 'Не удалось удалить пользователя',
  bossUserLoginPlaceholder: 'Логин (буквы, цифры, ._-)',
  bossDisplayNamePlaceholder: 'Отображаемое имя',
  bossNewUserPassword: 'Временный пароль (подставлен автоматически)',
  bossTempPasswordHint:
    'Скопируйте и передайте пользователю; сменить можно в настройках (⚙).',
  bossRegeneratePassword: 'Другой пароль',
  bossAddUser: 'Добавить',
  bossDeleteUser: 'Удалить',
  bossDeleteConfirmTitle: 'Удалить пользователя?',
  bossDeleteConfirmBody: 'Аккаунт и связанные чаты будут удалены.',
  bossCredentialsSentHint:
    'Инструкция с логином и паролем отправлена пользователю в личный чат. Ниже можно скопировать тот же текст для передачи другим способом.',
  bossInviteCopyBody:
    'Здравствуйте!\n\nВам создан аккаунт SVYAZ (beta-версия; функции будут расширяться).\nЛогин: {login}\nВременный пароль: {password}\n\nПосле входа смените пароль: экран списка чатов → иконка ⚙ (настройки) → укажите текущий пароль и новый.\n\nЗачем именно этот чат:\n• Чтобы оставаться на связи в узком кругу, когда привычные мессенджеры недоступны, ненадёжны или перегружены лишними каналами — здесь звонки и переписка в одном приложении под ваши задачи.{retention}\n• Вы сами управляете перепиской: чат можно удалить в приложении в любой момент.\n\nЭто beta: относитесь к сервису как к развивающемуся продукту; защита и функции будут усиливаться.\n\nГруппы и звонки уже доступны в базовом виде.\n\nПриятного общения!',
  bossCopyInviteButton: 'Скопировать логин и инструкцию',
  bossCopyInviteDone: 'Скопировано в буфер',
  bossInstructionLanguage:
    'Язык приветствия для нового пользователя (только этот текст; интерфейс у него не меняется)',
  forwardedLabel: 'Переслано',
  forwardedFromPrefix: 'Из:',
  savedChatTitle: 'Избранное',
  shareIntentTitle: 'Куда отправить',
  shareIntentPhotos: 'Фото: {count}',
  shareIntentSkipped: 'Не принято файлов: {count}. SVYAZ отправляет снимки, ссылки и текст.',
  shareIntentSending: 'Отправляем… {sent} из {total}',
  shareIntentFailed: 'Не удалось отправить',
  shareIntentEmpty: 'Чатов пока нет — начните переписку и поделитесь снова',
  shareIntentSavedHint: 'Ваше личное пространство',
  savedChatCardHint: 'Заметки, файлы и ссылки — видите только вы',
  savedEmptyTitle: 'Здесь всё ваше',
  savedEmptyBody: 'Заметки, файлы, ссылки. Никто, кроме вас, этого не видит.',
  saveToSavedAction: 'В избранное',
  savedMessageDone: 'Добавлено в «Избранное»',
  savedSourceDeleted: 'Исходное сообщение удалено',
  savedOpenFailed: 'Не удалось открыть «Избранное»',
  savedSectionAll: 'Всё',
  savedSectionNotes: 'Заметки',
  savedSectionTasks: 'Задачи',
  savedSectionFiles: 'Файлы',
  savedSectionLinks: 'Ссылки',
  savedSectionVoice: 'Голосовые',
  savedSectionEmptyNotes: 'Заметок пока нет. Всё, что вы напишете здесь без ссылки, попадёт сюда.',
  savedSectionEmptyTasks: 'Списков дел пока нет. Отправьте чек-лист — он окажется здесь.',
  savedSectionEmptyFiles: 'Снимков и видео пока не сохранено.',
  savedSectionEmptyLinks: 'Ссылок пока не сохранено.',
  savedSectionEmptyVoice: 'Голосовых пока нет.',
  noteNewAction: 'Новая заметка',
  noteTitlePlaceholder: 'Заголовок (необязательно)',
  noteBodyPlaceholder: 'Текст заметки',
  notePinAction: 'Закрепить',
  noteUnpinAction: 'Открепить',
  notePinnedBadge: 'Закреплено',
  noteSavingHint: 'Сохраняется…',
  noteSavedHint: 'Сохранено',
  noteColorLabel: 'Цвет',
  noteEditFailed: 'Не удалось сохранить заметку',
  settingsGenderLabel: 'Пол',
  genderUnset: 'Не указан',
  genderMale: 'Мужской',
  genderFemale: 'Женский',
  settingsBirthDateLabel: 'Дата рождения',
  settingsBirthDateHint: 'ДД.ММ.ГГГГ, возраст от {min} до {max} лет. Контактам видны день и возраст — год нет. Им придёт напоминание о вашем дне рождения.',
  settingsBirthDateInvalid: 'Введите дату в виде ДД.ММ.ГГГГ',
  settingsBirthDateAgeRange: 'Возраст должен быть от {min} до {max} лет',
  profileYearsSuffix: 'лет',
  birthdayTodayTitle: 'Сегодня день рождения',
  birthdaySoonTitle: 'Скоро дни рождения',
  birthdayCongratulate: 'Поздравить',
  birthdayTurns: 'исполняется {age}',
  birthdayInDays: 'через {days} дн.',
  birthdayTomorrow: 'завтра',
  birthdayPickTemplate: 'Выберите поздравление',
  birthdayTemplateHint: 'Нажмите — текст встанет в поле, его ещё можно поправить перед отправкой.',
  birthdaySend: 'Отправить',
  birthdaySent: 'Поздравление отправлено',
  birthdaySendFailed: 'Не удалось отправить поздравление',
  birthdayHide: 'Скрыть',
  savedSectionReminders: 'Напоминания',
  savedSectionScheduled: 'Отложенные',
  savedSectionEmptyReminders: 'Напоминаний пока нет.',
  savedSectionEmptyScheduled: 'Отложенных сообщений пока нет.',
  reminderNewAction: 'Новое напоминание',
  reminderTitlePlaceholder: 'О чём напомнить',
  reminderBodyPlaceholder: 'Подробности (необязательно)',
  reminderWhenLabel: 'Когда',
  reminderDatePlaceholder: 'ДД.ММ.ГГГГ',
  reminderTimePlaceholder: 'ЧЧ:ММ',
  reminderPrecisionHint: 'Точность — около минуты.',
  reminderSaveAction: 'Сохранить',
  reminderSaveFailed: 'Не удалось сохранить напоминание',
  reminderDeleteAction: 'Удалить',
  reminderSnoozeAction: 'Отложить на 10 мин',
  reminderStatusScheduled: 'Ждёт',
  reminderStatusFired: 'Сработало',
  reminderStatusCancelled: 'Отменено',
  reminderInvalidTime: 'Укажите дату и время в будущем',
  reminderZoneNote: 'Задано по {zone}',
  organizerEditAction: 'Изменить',
  quickToday: 'Сегодня 18:00',
  quickTomorrow: 'Завтра 09:00',
  quickInAnHour: 'Через час',
  scheduledNewAction: 'Отложить сообщение',
  scheduledRecipientsLabel: 'Кому',
  scheduledRecipientsEmpty: 'Выберите хотя бы одного человека',
  scheduledRecipientsSearch: 'Поиск контакта',
  scheduledTextPlaceholder: 'Текст сообщения',
  scheduledWhenLabel: 'Когда отправить',
  scheduledCreateAction: 'Запланировать',
  scheduledCancelAction: 'Отменить отправку',
  scheduledMessageSaveFailed: 'Не удалось запланировать отправку',
  scheduledMessageCancelFailed: 'Не удалось отменить',
  scheduledStatusScheduled: 'Ждёт отправки',
  scheduledStatusSent: 'Отправлено',
  scheduledStatusCancelled: 'Отменено',
  scheduledStatusFailed: 'Не доставлено',
  scheduledTooSoon: 'Выберите время хотя бы на минуту вперёд',
  notifyReminderTitle: 'Напоминание',
  notifyBirthdayTitle: 'День рождения',
  notifyBirthdayBody: 'празднует день рождения',
  registerAction: 'Создать аккаунт',
  registerTitle: 'Новый аккаунт',
  registerEmailPlaceholder: 'Почта',
  registerNamePlaceholder: 'Как к вам обращаться?',
  registerSubmit: 'Зарегистрироваться',
  registerEmailInvalid: 'Это не похоже на адрес почты',
  registerPasswordTooShort: 'Не короче 8 символов',
  registerNameRequired: 'Введите имя',
  registerInviteOnly: 'Сейчас регистрация только по приглашению',
  registerFailed: 'Не удалось зарегистрироваться',
  verifySentTitle: 'Проверьте почту',
  verifySentBody: 'Мы отправили ссылку подтверждения на {email}. Откройте её, чтобы завершить регистрацию.',
  verifyResend: 'Отправить ссылку ещё раз',
  verifyResendDone: 'Если этот адрес ждёт подтверждения, ссылка уже в пути.',
  backToLogin: 'Вернуться ко входу',
  forgotAction: 'Забыли пароль?',
  forgotTitle: 'Восстановление доступа',
  forgotBody: 'Введите адрес, на который заводили аккаунт, — пришлём ссылку для смены пароля.',
  forgotSubmit: 'Прислать ссылку',
  forgotSentTitle: 'Проверьте почту',
  forgotSentBody: 'Если на {email} есть аккаунт, ссылка уже в пути. Она сработает один раз и действует час.',
  resetTitle: 'Новый пароль',
  resetBody: 'Задайте новый пароль. Все, кто сейчас в этом аккаунте, выйдут из него.',
  resetNewPassword: 'Новый пароль',
  resetSubmit: 'Сохранить и войти',
  resetDone: 'Пароль изменён. Войдите с новым.',
  resetFailed: 'Не удалось изменить пароль',
  verifyDone: 'Адрес подтверждён. Теперь можно войти.',
  verifyLinkDead: 'Ссылка не сработала: она устарела или уже использована. Запросите новую.',
  captchaRequired: 'Подтвердите, что вы не робот, и повторите.',
  forwardSelectCount: 'Выбрано: {count}',
  selectMessagesAction: 'Выбрать',
  deleteSelectedAction: 'Удалить',
  deleteSelectedConfirmTitle: 'Удалить выбранные?',
  deleteSelectedConfirmBody: 'Сообщений: {count}. Они исчезнут у всех участников чата, вернуть их будет нельзя.',
  deleteSelectedFailed: 'Часть сообщений удалить не удалось',
  forwardAction: 'Переслать',
  forwardDoneBody: 'Переслано сообщений: {count}',
  replyAction: 'Ответить',
  replyQuoteLabel: 'Ответ',
  replyOriginalDeleted: 'Сообщение удалено',
  copyTextAction: 'Копировать текст',
  copyTextDone: 'Скопировано',
  editAction: 'Изменить',
  editingMessageTitle: 'Редактирование',
  editMessageFailed: 'Не удалось изменить сообщение',
  messageEditedMark: 'изм.',
  deleteMessageAction: 'Удалить',
  deleteMessageFailed: 'Не удалось удалить сообщение',
  reactionLimitReached: 'На одно сообщение — не больше трёх реакций',
  savedClearAction: 'Очистить',
  savedClearDone: '«Избранное» очищено',
  savedClearFailed: 'Не удалось очистить «Избранное»',
  helpCenterTitle: 'Подсказки и новости',
  helpCenterOpenLabel: 'Подсказки и новости',
  helpCenterOpenLabelUnread: 'Подсказки и новости — есть непрочитанное',
  helpCenterNewBadge: 'Новое',
  helpCenterAllReadHint: 'Всё прочитано. Лампочка замигает снова, когда появится новая запись.',
  helpCenterIntro: 'Что приложение умеет и как этим пользоваться. Новые записи встают наверх и заставляют лампочку в шапке мигать.',
  settingsHandleSuggestions: 'Свободные варианты',
  saveChatImage: 'Сохранить изображение',
  saveChatImageSuccess: 'Изображение сохранено',
  saveChatImageFailed: 'Не удалось сохранить изображение',
  saveChatImagePermissionDenied: 'Разрешите доступ к фото, чтобы сохранять изображения',
  saveChatVideo: 'Сохранить видео',
  saveChatVideoSuccess: 'Видео сохранено',
  saveChatVideoFailed: 'Не удалось сохранить видео',
  saveChatVideoPermissionDenied: 'Разрешите доступ к фото, чтобы сохранять видео',
  forwardSameChatBlocked: 'Нельзя пересылать сообщения в этот же чат',
  messageReferenceNotFound: 'Исходное сообщение не найдено в загруженной истории',
  gearSettings: 'Настройки аккаунта',
  myDruzhina: 'Моя команда',
  myDruzhinaHint: 'В команде только те, кому доверяете как себе.',
  myDruzhinaVarangian: 'Хирд',
  myDruzhinaHintVarangian: 'В хирде только те, кому доверяете как себе.',
  contactRequestIncomingTitle: 'Входящие заявки',
  contactLoadFailed: 'Не удалось загрузить контакты',
  contactRequestLoadFailed: 'Не удалось загрузить заявки',
  contactRequestSendFailed: 'Не удалось отправить заявку',
  contactRequestApproveFailed: 'Не удалось одобрить заявку',
  contactRequestRejectFailed: 'Не удалось отклонить заявку',
  contactSearchSelfRow: 'Это вы',
  chatLastMessageFallback: 'Пока нет сообщений',
  unseenReactionA11y: 'Новая реакция: {emoji}',
  settingsHapticsLabel: 'Тактильная отдача',
  settingsSoundsLabel: 'Звуки',
  retentionNoticeTitle: 'Переписка очищена автоматически',
  retentionNoticeBody:
    'Сообщения в личных чатах удаляются с сервера через несколько дней — так задумано, ничего не потерялось.',
  retentionNoticeWhen: 'Последняя очистка: {when}',
  contactRemoveConfirmTitle: 'Удалить из команды?',
  contactRemoveConfirmTitleVarangian: 'Удалить из хирда?',
  contactRemoveConfirmBody:
    'Вы удалитесь друг у друга из контактов, а личная переписка будет удалена навсегда у обеих сторон.',
  contactRemoveFailed: 'Не удалось удалить контакт',
  profileUpdateFailed: 'Не удалось обновить профиль',
  settingsProfileSection: 'Профиль',
  settingsChangeNameLabel: 'Изменить имя',
  settingsChangeLoginLabel: 'Изменить логин',
  settingsSaveProfile: 'Сохранить профиль',
  settingsSaved: 'Изменения сохранены',
  settingsShareSection: 'Публичная ссылка и ник',
  settingsPublicHandleLabel: 'Публичный ник (без #)',
  settingsPublicHandleHint:
    '4–32 символа: латиница, цифры, подчёркивание. Очистите поле, чтобы убрать ник.',
  settingsProfilePublicLabel: 'Публичная страница профиля',
  settingsAllowDiscoveryLabel: 'Находить по нику в поиске',
  settingsKeepPhotoMetadataLabel: 'Сохранять метаданные фото',
  settingsKeepPhotoMetadataHint:
    'Выключено: из отправляемых фото удаляются координаты съёмки, модель камеры и серийный номер; ориентация и дата съёмки остаются. Включено: файл уходит со всем, что в нём есть.',
  settingsHandleWillSaveAs: "Сохраним как: #{handle}",
  settingsHandleChecking: "Проверяем…",
  settingsHandleFree: "Ник свободен",
  settingsHandleTaken: "Ник занят",
  settingsHandleFormat: "4–32 символа: строчная латиница, цифры, подчёркивание",
  settingsHandleReserved: "Этот ник зарезервирован",
  settingsHandleNotAllowed: "Такой ник недопустим",
  settingsHandleSaveFailedInvalid: "Сервер не принял этот ник. Попробуйте другой.",
  settingsHandleConfirmChangeTitle: "Сменить ник?",
  settingsHandleConfirmChangeBody: "Ссылки со старым ником перестанут работать, а сам ник сможет занять кто-то другой.",
  settingsHandleConfirmClearTitle: "Убрать ник?",
  settingsHandleConfirmClearBody: "Публичная страница закроется, ссылки перестанут работать, а ник станет доступен другим.",
  settingsHandleKeep: "Оставить",
  settingsSaveShareSettings: 'Сохранить ник',
  settingsCopyHandleTag: 'Скопировать #ник',
  settingsCopied: 'Скопировано',
  profileLinkUnavailable: "Профиль недоступен или такого ника больше нет.",
  profileLinkSelf: "Это ссылка на ваш собственный профиль.",
  settingsShareDisabledHidden: "Публичная страница профиля выключена — включите её, чтобы делиться ссылкой.",
  settingsShareDisabledUnsaved: "Сначала сохраните ник — ссылка строится по сохранённому.",
  settingsProfileSaved: "Профиль сохранён",
  settingsShareSaved: "Ник сохранён",
  settingsPasswordChanged: "Пароль изменён",
  reportAction: "Пожаловаться",
  reportTitle: "Что здесь не так?",
  reportSent: "Жалоба отправлена. Мы разберёмся.",
  reportFailed: "Не удалось отправить жалобу",
  reportCategorySpam: "Спам или реклама",
  reportCategoryHarassment: "Оскорбления или угрозы",
  reportCategorySexualContent: "Материалы сексуального характера",
  reportCategoryViolence: "Насилие",
  reportCategoryFraud: "Мошенничество",
  reportCategoryChildSafety: "Угроза безопасности ребёнка",
  reportCategoryOther: "Другое",
  blockAction: "Заблокировать",
  blockConfirmTitle: "Заблокировать этого человека?",
  blockConfirmHint: "Он не сможет писать вам и звонить. Отменить можно в «Настройках» → «Приватность».",
  blockDone: "Заблокирован",
  blockFailed: "Не удалось заблокировать",
  unblockAction: "Разблокировать",
  settingsBlockedSection: "Заблокированные",
  settingsBlockedEmpty: "Вы никого не блокировали",
  moderationCancel: "Отмена",
  settingsDeleteAccountSection: "Удаление аккаунта",
  settingsDeleteAccountWarning:
    "Удаление необратимо. Профиль, контакты, напоминания и «Избранное» стираются. Отправленные вами сообщения остаются в чатах собеседников с подписью «Удалённый аккаунт».",
  settingsDeleteAccountStart: "Удалить аккаунт",
  settingsDeleteAccountConfirmHint:
    "Введите пароль и напечатайте слово ниже, чтобы подтвердить.",
  settingsDeleteAccountConfirmWord: "УДАЛИТЬ",
  settingsDeleteAccountConfirmPlaceholder: "Напечатайте УДАЛИТЬ",
  settingsDeleteAccountConfirm: "Удалить навсегда",
  settingsDeleteAccountCancel: "Отмена",
  settingsDeleteAccountPasswordRequired: "Введите текущий пароль",
  settingsDeleteAccountWordMismatch: "Слово подтверждения не совпадает",
  settingsDeleteAccountFailed: "Не удалось удалить аккаунт",
  settingsLegalSection: "Документы",
  settingsPrivacyPolicyLink: "Политика конфиденциальности",
  settingsTermsLink: "Условия использования",
  settingsLocalOnlyHint: "Применяется сразу, только на этом устройстве",
  settingsNamePlaceholder: "Ваше имя",
  settingsPublicHandlePlaceholder: "my_handle",
  settingsAvatarTitle: 'Фото профиля',
  settingsAvatarChooseShape: 'Форма',
  settingsAvatarRemove: 'Удалить фото',
  settingsAvatarUploadFailed: 'Не удалось загрузить фото',
  avatarAnimatedTooLarge: 'Слишком тяжёлая анимация — выберите GIF до 8 МБ.',
  galleryPermissionDenied:
    'Нет доступа к фото. Включите его в настройках системы — доступа к выбранным фото достаточно.',
  settingsAvatarWebHint: 'Web: выберите файл через диалог; загрузка через multipart.',
  avatarChangePhoto: 'Изменить фото',
  avatarTapToChange: 'Нажмите на фото, чтобы изменить',
  avatarTakePhoto: 'Сделать снимок',
  avatarFromLibrary: 'Выбрать из галереи',
  avatarCameraDenied: 'Нет доступа к камере. Включите его в настройках системы.',
  avatarRemoveConfirm: 'Фото исчезнет у всех, останутся инициалы.',
  avatarEditorHint: 'Щипок — приближение, перетаскивание — сдвиг, двойное касание — сброс',
  avatarEditorHintPanOnly: 'Перетащите фото, чтобы выбрать кадр',
  avatarPreviewCard: 'Посмотреть, как видят другие',
  chatAvatarSectionTitle: 'Фото чата',
  userCardTitle: 'Профиль',
  userCardOpenPhoto: 'Открыть фото',
  userCardPhotoHint: 'Щипок или двойное касание — приближение, касание — закрыть',
  userCardPhotoFriendsOnly: 'Фото во весь экран открывают только друзья',
  userCardPhotoLoadFailed: 'Не удалось загрузить фото',
  chatUnknownMember: 'Участник',
  contactCardLabel: 'Контакт',
  contactShareAction: 'Поделиться контактом',
  contactShareChooseChat: 'Кому отправить контакт',
  contactShareSent: 'Контакт отправлен',
  contactShareFailed: 'Не удалось отправить контакт',
  checklistItemA11y: 'Пункт чеклиста',
  checklistCheckedBy: 'Отметил(а) {name}',
  checklistToggleFailed: 'Не удалось обновить чеклист',
  checklistDuePast: 'Этот срок уже прошёл. Укажите время, которое ещё впереди.',
  checklistDueUnparsed: 'Не понял срок. Пишите {20:00} или {02.02.2026 15:00}.',
  checklistAssigneeInvalid: 'Исполнитель — один и ником: #ник',
  checklistItemEmpty: 'У пункта должен быть текст: скобки и срок идут к нему, а не вместо него.',
  checklistAssigneeNotFound: 'Профиля с таким ником нет',
  checklistDueToday: 'сегодня {time}',
  checklistDueTomorrow: 'завтра {time}',
  checklistOverdue: 'Просрочено',
  checklistDueA11y: 'Срок: {when}',
  checklistAssigneeA11y: 'Исполнитель: {name}',
  channelPostCommentsCount: '💬 {count}',
  settingsLoginHint: 'Буквы, цифры, точка, подчеркивание, дефис',
  loginCheckInProgress: 'Проверяем логин...',
  loginAvailable: 'Логин свободен',
  loginUnavailable: 'Логин занят',
  chatTileCallAudio: 'Аудиозвонок',
  chatTileCallVideo: 'Видеозвонок',
  channelReadOnlyHint: 'Только просмотр: писать могут админ и редакторы.',
  channelCommentComposerHint: 'Чтобы прокомментировать, нажмите 💬 «Комментарии» под постом.',
  channelUnsubscribeConfirmBody: 'Отписаться от канала? Он пропадёт из списка чатов.',
  channelUnsubscribeFailed: 'Не удалось отписаться от канала.',
  channelPostRestrictedHint: 'В этом канале нельзя публиковать посты.',
  channelArticlesLoadFailed: 'Не удалось загрузить статьи.',
  channelArticlesDrafts: 'Черновики',
  channelArticleStatusDraft: 'Черновик',
  channelDraftSavedTitle: 'Черновик сохранён',
  channelDraftSavedBody: 'Черновик в блоке «Статьи» вверху канала.',
  channelDraftView: 'Открыть черновик',
  channelPostPublishedTitle: 'Статья опубликована',
  channelSettingsTitle: 'Настройки канала',
  channelDraftsSettingsHint: 'Черновики — нажмите, чтобы открыть и опубликовать.',
  channelDraftsEmpty: 'Черновиков нет.',
  deleteChannelConfirmBody: 'Канал и все статьи будут удалены безвозвратно.',
  channelPostOpenFailed: 'Не удалось открыть статью.',
  channelPostLoadFailed: 'Не удалось загрузить статью.',
  channelPostComments: 'Комментарии',
  channelCommentsClosed: 'Комментирование в этом канале закрыто.',
  channelPublishAction: 'Опубликовать',
  channelPublishDraftTitle: 'Опубликовать этот черновик?',
  channelPublishFailed: 'Не удалось опубликовать статью.',
  channelDraftOpen: 'Открыть',
  channelSubscribedTitle: 'Вы подписались',
  channelGuestSubscribeHint:
    'Вы читаете канал как гость. На него подписаны {count} — новые статьи будут приходить и вам.',
  channelArchiveTitle: 'Все статьи',
  channelArchiveEmpty: 'Статей пока нет.',
  channelArchiveLoadMore: 'Показать ещё',
  editorPreview: 'Предпросмотр',
  editorPreviewBack: 'Вернуться к правке',
  editorInsertHint: 'Выберите тип блока ниже — он встанет сразу за этим.',
  editorInvalidChannel:
    'Экран открыт без канала. Закройте его, откройте канал из списка и начните статью оттуда.',
  blockParagraph: 'Абзац',
  blockHeading: 'Заголовок',
  blockImage: 'Картинка',
  blockQuote: 'Цитата',
  blockBulletList: 'Список',
  blockOrderedList: 'Нумерованный список',
  blockDivider: 'Разделитель',
  blockCode: 'Код',
  blockEmbed: 'Встраивание',
  blockVideo: 'Ссылка на видео',
  blockGallery: 'Галерея',
  editorTitleLabel: 'Заголовок',
  editorTitlePlaceholder: 'Заголовок статьи',
  editorBodyLabel: 'Текст',
  editorAddCover: 'Добавить обложку',
  editorChangeCover: 'Заменить обложку',
  editorRemoveCover: 'Убрать обложку',
  editorSaveDraft: 'В черновики',
  editorPublish: 'Опубликовать',
  editorListItem: 'Пункт списка',
  editorBlockEmpty: 'пусто',
  editorReplaceImage: 'Заменить картинку',
  editorUploadImage: 'Загрузить картинку',
  editorAddPhotos: 'Добавить фото',
  editorAddBlockA11y: 'Добавить: {name}',
  editorInsertHere: 'Вставить блок',
  commentsTitle: 'Комментарии',
  commentsReply: 'Ответить',
  commentsReplyingTo: 'Ответ для {name}',
  commentsPlaceholder: 'Написать комментарий…',
  commentsSend: 'Отправить',
  commentsPending: 'на проверке',
  commentsThumbUp: 'Нравится',
  commentsDelete: 'Удалить',
  commentsApprove: 'Одобрить',
  commentsBan: 'Забанить',
  commentsDeleteConfirmBody: 'Комментарий исчезнет из обсуждения.',
  commentsBanConfirmTitle: 'Забанить в комментариях?',
  commentsBanConfirmBody: '{name} больше не сможет писать комментарии в этом канале.',
  commentsModerationFailed: 'Действие не выполнено.',
  articleReadMore: 'Читать статью →',
  articleCardKicker: 'Статья',
  channelMuteOn: 'Отключить уведомления',
  channelMuteOff: 'Включить уведомления',
  channelMuteFailed: 'Не удалось изменить уведомления.',
  channelViewsCount: '👁 {count}',
  channelPostEdit: 'Редактировать',
  channelPostEditTitle: 'Правка статьи',
  channelPostEditPublishedHint:
    'Сохранение обновит статью у всех, кто её уже видел. Повторного уведомления подписчикам не будет.',
  channelPostSaveChanges: 'Сохранить',
  channelPostSaved: 'Изменения сохранены',
  channelPostSaveFailed: 'Не удалось сохранить статью.',
  channelPostDelete: 'Удалить',
  channelPostDeleteConfirmTitle: 'Удалить статью?',
  channelPostDeleteConfirmBody:
    'Статья и её объявление в канале исчезнут насовсем. У тех, кто её уже открывал, она тоже пропадёт.',
  channelPostDeleteDraftConfirmBody: 'Черновик исчезнет насовсем.',
  channelPostDeleteFailed: 'Не удалось удалить статью.',
  channelPostUnpublish: 'В черновики',
  channelPostUnpublishConfirmTitle: 'Вернуть в черновики?',
  channelPostUnpublishConfirmBody:
    'Объявление пропадёт из ленты канала. Текст останется в черновиках, опубликовать его можно будет заново.',
  channelPostUnpublishFailed: 'Не удалось вернуть статью в черновики.',
  channelDraftRestoredHint: 'Восстановлен несохранённый текст с прошлого раза.',
  channelDraftLeaveTitle: 'Выйти из редактора?',
  channelDraftLeaveBody:
    'Несохранённый текст останется на этом устройстве — редактор предложит его при следующем открытии.',
  channelDraftLeaveConfirm: 'Выйти',
  channelDraftLeaveStay: 'Остаться',
  channelDraftDiscardRestored: 'Убрать восстановленное',
  channelSlugLabel: 'Адрес канала',
  channelSlugPlaceholder: 'my_channel',
  channelSlugHint:
    '4–32 символа: строчные латинские буквы, цифры, подчёркивание. Из него собирается ссылка на канал.',
  channelSlugInvalid:
    'Адрес должен быть длиной 4–32 символа и состоять из строчных латинских букв, цифр и подчёркиваний.',
  channelCommentSendFailed: 'Комментарий не отправлен.',
  channelCommentsLoadFailed: 'Не удалось загрузить комментарии.',
  chatFilterGroup: 'Группы',
  chatFilterChannel: 'Каналы',
  chatSortRecent: 'сначала новые',
  chatSortOld: 'сначала старые',
  chatSortTitleAsc: 'по названию А-Я',
  chatSortTitleDesc: 'по названию Я-А',
  callScopeBadgeGroupVideo: 'Групповой видеозвонок',
  callScopeBadgeDirectVideo: 'Личный видеозвонок',
  groupCallUnavailableTitle: 'Групповые звонки недоступны',
  groupCallsTemporarilyOff: 'Групповые звонки временно отключены. Личные звонки работают.',
  groupCallUnavailableDisabled: 'Сервер групповых звонков не настроен. Напишите в поддержку.',
  groupCallUnavailableNetwork: 'Не удалось связаться с сервером звонков. Проверьте соединение и попробуйте снова.',
  groupCallUnavailableConnect: 'Не удалось подключиться к звонку. Попробуйте ещё раз.',
  groupCallJoinOngoing: 'Присоединиться',
  groupCallOngoingBadge: 'Идёт звонок',
  callDiagnosticsTitle: 'Диагностика звонка',
  callDiagnosticsHint: 'Долгое нажатие — диагностика звонка',
  callDiagnosticsCopy: 'Скопировать отчёт',
  callDiagTransport: 'Транспорт',
  callDiagCandidatePair: 'Пара кандидатов',
  callDiagBitrate: 'Битрейт',
  callDiagPacketsLost: 'Потери пакетов',
  callDiagParticipants: 'Участников',
  screenShareTitle: 'Демонстрация экрана',
  screenShareBody: 'Браузер не разрешил захват экрана.',
  screenShareBusyBody: 'Кто-то уже показывает экран. Дождитесь, пока он остановит показ.',
  screenShareLabel: 'показывает экран',
  callShareScreen: 'Поделиться экраном',
  callShareScreenStop: 'Остановить показ',
  callSwitchCamera: 'Перевернуть камеру',
  settingsAppearanceSection: 'Оформление',
  settingsVisualThemeLabel: 'Стиль приложения',
  visualThemeNeutral: 'Нейтральный',
  visualThemeVarangian: 'Варяжский',
  visualThemeDark: 'Тёмный',
  visualThemeAuto: 'Авто · Солнце / Космос',
  settingsLanguageSection: 'Язык',
  settingsLanguageLabel: 'Язык интерфейса',
  settingsTabPreferences: 'Приложение',
  settingsTabAccount: 'Аккаунт',
  settingsTabAvatar: 'Фото',
  settingsTabProfile: 'Профиль',
  settingsTabPrivacy: 'Приватность',
  settingsLoginSection: 'Логин для входа',
  settingsSaveLogin: 'Сохранить логин',
  settingsLoginSaved: 'Логин сохранён',
  settingsPasswordSection: 'Пароль',
  settingsVisibilitySection: 'Кто может вас найти',
  settingsProfilePublicHint: 'Выключено — ссылка на профиль перестаёт открываться у всех.',
  settingsAllowDiscoveryHint: 'Выключено — вас больше не найдут по нику в поиске.',
  settingsMediaPrivacySection: 'Фото и данные',
  settingsShareDisabledNoHandle: 'Задайте ник и сохраните — ссылка строится по нему.',
  settingsPreferencesHint:
    'Тема и язык применяются сразу при выборе — пароль для этого не нужен.',
  settingsPreferencesDone: 'Готово',
  settingsCurrentPasswordRequired: 'Введите текущий пароль, чтобы задать новый.',
  directRetentionPolicyTitle: 'Очистка личного чата',
  directRetentionPolicyBody:
    'По политике безопасности история личных чатов на сервере периодически очищается. Скоро произойдёт удаление истории этого чата. Сохраните на устройство всё важное: сообщения, фотографии, файлы — если это необходимо.',
  directRetentionTimerA11y: 'Время до очистки истории на сервере. Нажмите для подробностей.',
  quickRepliesTitle: 'Быстрые ответы',
  quickReplyAddTitle: 'Новый быстрый ответ',
  quickReplyAddPlaceholder: 'Ваша фраза…',
  quickReplySave: 'Сохранить',
  quickReplyDelete: 'Удалить',
  quickReplyDeleteConfirmTitle: 'Удалить быстрый ответ?',
  quickReplyDefaultGreeting: 'Привет!',
  quickReplyDefaultInTouch: 'На связи',
  quickReplyDefaultThanks: 'Спасибо!',
  sharePost: 'Поделиться постом',
  shareChannel: 'Поделиться каналом',
  shareProfile: 'Поделиться профилем',
  settingsBioLabel: 'О себе',
  settingsBioPlaceholder: 'Пара слов о себе…',
  settingsStatusLabel: 'Статус',
  settingsStatusPlaceholder: 'Чем сейчас занимаетесь?',
  selfStatusEmpty: 'Добавить статус',
  quickSettingsTitle: 'Быстрые настройки',
  selfStatusSave: 'Сохранить',
  dndLabel: 'Не беспокоить',
  dndHint: 'Уведомления не приходят ни от кого. Звонки проходят.',
  dndFailed: 'Не удалось изменить настройку',
  notifyShowTextLabel: 'Показывать текст сообщения',
  notifyShowTextHint: 'Выключено — в уведомлении только «Новое сообщение», без текста.',
  userCardAddContact: 'Добавить в контакты',
  userCardContactPending: 'Заявка отправлена',
  navTitleNewPost: 'Новая статья',
  navTitlePost: 'Статья',
};

/** Serbian (Latin script) */
const sr: Record<BaseMessageKey, string> = {
  appName: 'SVYAZ',
  appTagline: 'Pozivi i ćaskanja — ostanite na vezi',
  loginEpigraphCalm:
    'Smiren prostor za one kojima je bitna pouzdana veza: poruke i pozivi na jednom mestu, uz pažnju na privatnost.',
  loginEpigraphVarangian:
    'Ovo nelako vreme starim varjacima nije smetnja ni razlog za očaj — uvek ćemo naći način da ostanemo na vezi! Skol',
  loginField: 'Korisničko ime ili e-pošta',
  passwordPlaceholder: 'Lozinka (min. 8 karaktera)',
  loginRequired: 'Unesite korisničko ime ili adresu e-pošte',
  loginInvalid: 'Korisničko ime čine latinična slova, cifre, tačka, crtica i donja crta. Ili unesite adresu e-pošte',
  passwordRequired: 'Unesite lozinku',
  validationFailed: 'Proverite unete podatke',
  validationFieldRequired: 'Popunite obavezno polje',
  validationFieldLength: 'Nedozvoljena dužina vrednosti',
  validationFieldFormat: 'Vrednost sadrži nedozvoljene znakove',
  errorGeneric: 'Greška',
  signIn: 'Prijavi se',
  logout: 'Odjavi se',
  group: 'Grupa',
  chatTypeGroup: 'Grupa',
  chatTypeChannel: 'Kanal',
  noChatsHint: 'Nema ćaskanja — započni lično ili grupno',
  nicknamePlaceholder: 'Nadimak ili deo imena…',
  searchError: 'Greška pri pretrazi',
  nobodyFound: 'Niko nije pronađen',
  close: 'Zatvori',
  settingsUnsavedTitle: "Imate nesačuvane izmene",
  settingsUnsavedBody: "Da ih sačuvamo pre zatvaranja?",
  settingsUnsavedDiscard: "Zatvori bez čuvanja",
  settingsUnsavedStay: "Vrati se",
  settingsNewPasswordTooShort: "Nova lozinka mora imati najmanje 8 znakova",
  newChannelTitle: 'Novi kanal',
  channelNamePlaceholder: 'Naziv kanala',
  channelEditorsHint: 'Urednici mogu da objavljuju postove u ovom kanalu.',
  channelInviteSubscribersHint: 'Pozvani postaju pretplatnici. Urednike dodajte kasnije u podešavanjima kanala.',
  addSubscriber: 'Dodaj pretplatnika',
  addEditor: 'Dodaj urednika',
  channelAllowReactions: 'Dozvoli reakcije',
  channelAllowComments: 'Dozvoli komentare',
  createChannelFailed: 'Nije uspelo kreiranje kanala',
  channelNameRequired: 'Unesite naziv kanala',
  channelDescriptionPlaceholder: 'Opis',
  channelsHubTitle: 'Kanali',
  channelSearchPlaceholder: 'Pretraga kanala…',
  channelsHubEmpty: 'Još nema kanala',
  channelPreviewTitle: 'Pregled kanala',
  channelPreviewEmptyPosts: 'Još nema objavljenih postova',
  channelPreviewUnavailable: 'Ovaj kanal nema javni pregled — pretplati se da bi ga čitao',
  channelPreviewSubscribeHint: 'Pretplati se da se kanal pojavi u listi četova',
  channelSubscribe: 'Pretplati se',
  channelUnsubscribe: 'Otkaži pretplatu',
  channelManage: 'Upravljanje',
  channelSubscribersCount: 'Pretplatnici: {count}',
  channelDirectoryLoadFailed: 'Nije uspelo učitavanje kanala',
  channelSubscriptionFailed: 'Nije uspela promena pretplate',
  channelUpdateFailed: 'Nije uspelo ažuriranje kanala',
  channelEditorAddFailed: 'Nije uspelo dodavanje urednika',
  channelEditorRemoveFailed: 'Nije uspelo uklanjanje urednika',
  deleteChannel: 'Obriši kanal',
  saveChannel: 'Sačuvaj kanal',
  groupNamePlaceholder: 'Naziv',
  removeMember: 'Ukloni',
  cancel: 'Otkaži',
  dialogOk: 'U redu',
  create: 'Napravi',
  groupInfoTitle: 'Informacije o grupi',
  groupTabMembers: 'Članovi',
  groupTabSettings: 'Podešavanja',
  groupMembersLabel: 'Članova: {count}',
  groupYouSuffix: '(vi)',
  groupAdminBadge: 'Admin',
  groupSearchPlaceholder: 'Pronađite korisnike za dodavanje…',
  groupNoUsersFound: 'Nema pronađenih korisnika',
  groupAddAction: 'Dodaj',
  groupRetry: 'Pokušaj ponovo',
  groupLeaveBtn: 'Napusti grupu',
  groupLeaveTitle: 'Napusti grupu',
  groupLeaveBody: 'Da li ste sigurni da želite da napustite ovu grupu?',
  groupLeaveConfirm: 'Napusti',
  groupLeaveFailed: 'Napuštanje grupe nije uspelo',
  groupNameLabel: 'Naziv grupe',
  groupDescLabel: 'Opis',
  groupDescPlaceholder: 'Opis grupe…',
  groupSaveChanges: 'Sačuvaj izmene',
  groupNameRequired: 'Naziv grupe je obavezan',
  groupInfoSaved: 'Podaci grupe su ažurirani',
  groupInfoSaveFailed: 'Čuvanje podataka grupe nije uspelo',
  groupChangePhoto: 'Promeni sliku',
  groupRemoveMemberTitle: 'Ukloni člana',
  groupRemoveMemberBody: 'Ukloniti {name} iz grupe?',
  groupRemoveAction: 'Ukloni',
  groupRemoveMemberFailed: 'Uklanjanje člana nije uspelo',
  groupMakeAdminTitle: 'Postavi za admina',
  groupRemoveAdminTitle: 'Ukloni admina',
  groupMakeAdminBody: 'Postaviti {name} za administratora?',
  groupRemoveAdminBody: 'Ukloniti administratorska prava korisniku {name}?',
  groupChangeRoleFailed: 'Promena uloge nije uspela',
  groupAddMemberFailed: 'Dodavanje člana nije uspelo',
  groupLoadMembersFailed: 'Učitavanje članova nije uspelo',
  deleteChatConfirmTitle: 'Obrisati čet?',
  deleteChatFailed: 'Čet ne može da se obriše',
  errorTitle: 'Greška',
  openChatFailed: 'Ćaskanje se ne može otvoriti',
  createGroupFailed: 'Grupa se ne može napraviti',
  callUnavailableTitle: 'Poziv nije dostupan',
  webrtcExpoGoChat:
    'Glas radi samo u dev izgradnji (ne u Expo Go). mobile: npx expo prebuild, zatim run:android / run:ios.',
  webrtcExpoGoCall:
    'Glas radi samo u dev izgradnji (ne u Expo Go). U folderu mobile: npx expo prebuild, zatim npx expo run:android ili run:ios.',
  webrtcWebChat: 'Potrebni su WebRTC, mikrofon i HTTPS (ili localhost).',
  webrtcWebCall: 'Potreban je pregledač sa WebRTC, pristup mikrofonu i HTTPS (ili localhost).',
  loadMessagesFailed: 'Poruke se ne mogu učitati',
  sendFailed: 'Slanje nije uspelo',
  messageSending: 'Šalje se…',
  messageUploading: 'Otprema se…',
  messageCompressing: 'Kompresuje se…',
  messageNotDelivered: 'Nije isporučeno',
  audioStopRecord: 'Zaustavi snimanje',
  audioStartRecord: 'Snimi glasovnu poruku',
  accessibilityAttachMenu: 'Zakači',
  audioCancelRecord: 'Otkaži snimanje',
  accessibilitySend: 'Pošalji',
  audioPlay: 'Pusti glas',
  audioPause: 'Pušta se…',
  transcriptShow: 'Tekst',
  transcriptHide: 'Skupi',
  transcriptPending: 'Prepisuje se…',
  transcriptReadAll: 'Pročitaj celo',
  transcriptFailed: 'Nije moguće prepisati',
  transcriptTooLong: 'Snimak je predug za prepis',
  transcriptSheetTitle: 'Tekst glasovne poruke',
  audioRecordFailed: 'Ne može da se snimi glasovna poruka',
  audioPlayFailed: 'Ne može da se pusti glasovna poruka',
  audioRecordUnsupported: 'Vaš pregledač ne podržava snimanje glasa',
  audioRecordSecureContext: 'Za web snimanje potreban je HTTPS ili localhost',
  videoRecordFailed: 'Video poruka ne može da se snimi',
  videoDurationLimit: 'Video poruka mora biti kraća od 44 sekunde',
  videoPayloadTooLarge:
    'Fajl je prevelik. Video: mekani limit 100 MB, bitrejt do 5 Mbps, 1080p.',
  callReady: 'Spreman za poziv',
  callCalling: 'Zovemo… čekamo odgovor',
  callConnecting: 'Povezivanje…',
  callReconnecting: 'Ponovno povezivanje…',
  messagePlaceholder: 'Poruka…',
  emojiHide: 'Sakrij emodži',
  emojiShow: 'Emodži',
  emojiFrequent: 'Često korišćene',
  emojiPopular: 'Popularno',
  emojiDarkSkin: 'Tamna koza (HD)',
  endCall: 'Završi',
  accessibilityCall: 'Pozovi',
  callNavOutgoing: 'Poziv',
  callNavIncoming: 'Dolazni',
  callIncomingFull: 'Dolazni poziv',
  callIncomingAudioLine: 'Audio poziv',
  callIncomingVideoLine: 'Video poziv',
  callIncomingChipAudio: 'Audio',
  callIncomingChipVideo: 'Video',
  callTalkingAudio: 'Glasovni razgovor',
  callDuration: 'Trajanje poziva',
  callBtnAnswer: 'Odgovori',
  callBtnReject: 'Odbij',
  callBtnHangup: 'Završi',
  callFullScreenIntentWarn:
    'Punoekranska obaveštenja za pozive su isključena u podešavanjima — dolazni ekran možda neće biti prikazan kad je telefon zaključan.',
  callFullScreenIntentSettings: 'Punoekranski pozivi (sistemska podešavanja)',
  callUseSpeaker: 'Zvučnik',
  callUseEarpiece: 'Slušalica',
  callMicMute: 'Isključi mikrofon',
  callMicUnmute: 'Uključi mikrofon',
  callMicUnavailable: 'Mikrofon nije uključen — ne čuju vas.',
  callSpeakingNow: 'Trenutno govori',
  accessibilityVideoCall: 'Video poziv',
  nonJsonServer:
    'Server nije vratio JSON. Proverite da li je backend pokrenut (port 18080) i da li je API adresa tačna: EXPO_PUBLIC_API_BASE ili lokalni web → 127.0.0.1:18080.',
  loginFailed: 'Prijava nije uspela',
  loginTooManyAttempts: 'Previše pokušaja prijave. Sačekajte malo i pokušajte ponovo.',
  searchFailed: 'Pretraga nije mogla da se završi',
  chatFallback: 'Čet',
  newMessage: 'Nova poruka',
  callGeneric: 'Poziv',
  callGroupIncomingFrom: '{name} zove',
  callScopeBadgeGroupAudio: 'Grupni poziv',
  callParticipantOrdinal: 'Učesnik {n}',
  callParticipantsInCall: 'U pozivu: {count}',
  callEventMissedOutgoing: '📞 Bez odgovora',
  callEventMissedIncoming: '📞 Propušten poziv',
  callEventDeclinedOutgoing: '📞 Poziv odbijen',
  callEventBusy: '📞 Zauzeto',
  callEventEnded: '📞 Poziv završen',
  peerDefault: 'Sagovornik',
  callVideoFallbackAudio: 'Kamera nije dostupna, odgovaramo samo glasom.',
  callPeerBusy: 'Linija je zauzeta.',
  notifyChannelMessages: 'Poruke',
  notifyChannelMessagesSilent: 'Poruke (aplikacija otvorena)',
  notifyChannelCalls: 'Pozivi',
  notifyBodyMessage: 'Imaš novu poruku',
  notifyContactRequestTitle: 'Zahtev za prijateljstvo',
  notifyContactRequestBody: 'želi da vas doda u prijatelje',
  notifyCommentReplyTitle: 'Odgovor na vaš komentar',
  androidNotifyCallsTitle: 'Uključite obaveštenja',
  androidNotifyCallsBody:
    'Pozivi i poruke koriste obaveštenja. Uključite ih za ovu aplikaciju u podešavanjima sistema — inače pozivi u pozadini možda neće zvoneti.',
  androidNotifyCallsOpenSettings: 'Otvori podešavanja',
  androidNotifyCallsLater: 'Kasnije',
  androidFullScreenCallsTitle: 'Pozivi preko celog ekrana',
  androidFullScreenCallsBody:
    'Android 14 i noviji prikazuje dolazni poziv preko celog ekrana samo uz vašu dozvolu. I bez nje pozivi stižu, ali kao obično obaveštenje.',
  onlineNow: 'na mreži',
  scrollToLatest: 'Na poslednju poruku',
  sessionExpired: 'Sesija je istekla. Prijavite se ponovo.',
  photoPickFailed: 'Fotografija se ne može dodati',
  accessibilityExpandVideo: 'Ceo ekran',
  accessibilityPrevPhoto: 'Prethodna fotografija',
  accessibilityNextPhoto: 'Sledeća fotografija',
  videoResolutionTooHigh: 'Video rezolucija mora biti najviše 1080p (duža strana 1920 px).',
  photoProcessFailed: 'Ne mogu da obradim sliku (smanjivanje / kodiranje).',
  photoPreviewTitle: 'Pošalji fotografiju',
  photoPreviewBack: 'Nazad na izbor',
  photoPreviewSend: 'Pošalji',
  photoPreviewImageLabel: 'Izabrana fotografija',
  photoPreviewCaptionPlaceholder: 'Dodaj opis…',
  photoPreviewDiscardTitle: 'Odbaciti izmene?',
  photoPreviewDiscardMessage: 'Uneti opis će biti izgubljen.',
  photoPreviewDiscard: 'Odbaci',
  photoPreviewKeepEditing: 'Nastavi',
  photoMasksTitle: "Maske",
  photoMaskBlur: "Zamuti",
  photoMaskPixelate: "Pikselizacija",
  photoMaskEmoji: "Nalepnica",
  photoMaskHideAllFaces: "Sakrij sva lica",
  photoMaskNoFaces: "Lica nisu pronađena",
  photoMaskDetecting: "Tražim lica…",
  photoMaskHint: "Dodirnite da postavite masku, ponovni dodir je uklanja",
  photoFiltersTitle: "Filteri",
  photoFilterStrength: "Jačina",
  photoFilterOriginal: "Original",
  photoFilterFilm400: "Film 400",
  photoFilterNight800t: "Noć 800T",
  photoFilterDigicam2004: "Digicam 2004",
  photoFilterMatte: "Mat",
  photoFilterCinema: "Bioskop",
  photoFilterCleanLight: "Čisto svetlo",
  photoFilterMono3200: "Mono 3200",
  photoFilterDuotone: "Duoton",
  photoFilterGlow: "Sjaj",
  photoFilterInfra: "Infra",
  photoFilterChrome: "Hrom",
  photoFilterNoon: "Podne",
  photoCropTitle: "Kadar",
  photoCropApply: "Gotovo",
  photoCropFree: "Slobodno",
  photoCropRotate: "Rotiraj 90°",
  photoCropReset: "Poništi",
  photoCropFailed: "Kadriranje nije primenjeno",
  photoPreviewRemove: 'Ukloni ovu fotografiju',
  photoSendPartialFailed: 'Deo fotografija nije poslat',
  photoQualityEconomy: 'Štedljivo',
  photoQualityStandard: 'Standardno',
  photoQualityMax: 'Maksimum',
  photoQualityOriginal: 'Original',
  photoQualityCalculating: 'Priprema…',
  unitKilobytes: 'KB',
  unitMegabytes: 'MB',
  currentPassword: 'Trenutna lozinka',
  newPasswordLabel: 'Nova lozinka',
  savePassword: 'Sačuvaj lozinku',
  passwordChangeFailed: 'Lozinka se ne može promeniti',
  bossUsersTitle: 'Korisnici',
  bossUsersLoadFailed: 'Lista korisnika se ne može učitati',
  bossCreateUserFailed: 'Korisnik se ne može napraviti',
  bossDeleteUserFailed: 'Korisnik se ne može obrisati',
  bossUserLoginPlaceholder: 'Prijava (slova, cifre, ._-)',
  bossDisplayNamePlaceholder: 'Ime za prikaz',
  bossNewUserPassword: 'Privremena lozinka (automatski)',
  bossTempPasswordHint: 'Kopirajte i pošaljite korisniku; može da promeni u podešavanjima (⚙).',
  bossRegeneratePassword: 'Nova lozinka',
  bossAddUser: 'Dodaj',
  bossDeleteUser: 'Obriši',
  bossDeleteConfirmTitle: 'Obrisati korisnika?',
  bossDeleteConfirmBody: 'Nalog i povezani četovi biće uklonjeni.',
  bossCredentialsSentHint:
    'Poslali smo korisniku ličnu poruku sa podacima. Ispod možete kopirati isti tekst da podelite na drugi način.',
  bossInviteCopyBody:
    'Zdravo!\n\nVaš nalog na SVYAZ je spreman (beta verzija; funkcije će se širiti).\nPrijava: {login}\nPrivremena lozinka: {password}\n\nPosle prijave promenite lozinku: lista ćaskanja → ⚙ (podešavanja) → trenutna i nova lozinka.\n\nZašto baš ovaj čet:\n• Za pouzdanu vezu u užem krugu kada uobičajeni mesendžeri nisu dostupni, nisu pouzdani ili su „preglasni“ — pozivi i poruke na jednom mestu, za vaše potrebe.{retention}\n• Vi kontrolišete: čet možete obrisati u aplikaciji kad god želite.\n\nOvo je beta — zaštita i funkcije će se jačati.\n\nGrupe i pozivi su dostupni u osnovnoj formi.\n\nPrijatno!',
  bossCopyInviteButton: 'Kopiraj prijavu i uputstvo',
  bossCopyInviteDone: 'Kopirano',
  bossInstructionLanguage:
    'Jezik poruke dobrodošlice (samo taj tekst; ne menja jezik aplikacije korisnika)',
  forwardedLabel: 'Prosleđeno',
  forwardedFromPrefix: 'Od:',
  savedChatTitle: 'Sačuvano',
  shareIntentTitle: 'Kome poslati',
  shareIntentPhotos: 'Fotografije: {count}',
  shareIntentSkipped: 'Nije prihvaćeno datoteka: {count}. SVYAZ šalje slike, linkove i tekst.',
  shareIntentSending: 'Šaljemo… {sent} od {total}',
  shareIntentFailed: 'Slanje nije uspelo',
  shareIntentEmpty: 'Još nema ćaskanja — započnite razgovor pa podelite ponovo',
  shareIntentSavedHint: 'Vaš lični prostor',
  savedChatCardHint: 'Beleške, datoteke i linkovi — vidite samo vi',
  savedEmptyTitle: 'Ovde je sve vaše',
  savedEmptyBody: 'Beleške, datoteke, linkovi. Niko osim vas ovo ne vidi.',
  saveToSavedAction: 'U sačuvano',
  savedMessageDone: 'Dodato u „Sačuvano“',
  savedSourceDeleted: 'Izvorna poruka je obrisana',
  savedOpenFailed: 'Nije moguće otvoriti „Sačuvano“',
  savedSectionAll: 'Sve',
  savedSectionNotes: 'Beleške',
  savedSectionTasks: 'Zadaci',
  savedSectionFiles: 'Datoteke',
  savedSectionLinks: 'Linkovi',
  savedSectionVoice: 'Glasovne',
  savedSectionEmptyNotes: 'Još nema beleški. Sve što ovde napišete bez linka stiže u ovu karticu.',
  savedSectionEmptyTasks: 'Još nema spiskova. Pošaljite ček-listu i pojaviće se ovde.',
  savedSectionEmptyFiles: 'Još nema sačuvanih slika i video-zapisa.',
  savedSectionEmptyLinks: 'Još nema sačuvanih linkova.',
  savedSectionEmptyVoice: 'Još nema glasovnih poruka.',
  noteNewAction: 'Nova beleška',
  noteTitlePlaceholder: 'Naslov (opciono)',
  noteBodyPlaceholder: 'Tekst beleške',
  notePinAction: 'Zakači',
  noteUnpinAction: 'Otkači',
  notePinnedBadge: 'Zakačeno',
  noteSavingHint: 'Čuvanje…',
  noteSavedHint: 'Sačuvano',
  noteColorLabel: 'Boja',
  noteEditFailed: 'Nije moguće sačuvati belešku',
  settingsGenderLabel: 'Pol',
  genderUnset: 'Nije naveden',
  genderMale: 'Muški',
  genderFemale: 'Ženski',
  settingsBirthDateLabel: 'Datum rođenja',
  settingsBirthDateHint: 'DD.MM.GGGG, starost od {min} do {max} godina. Kontakti vide dan i godine starosti — ne i godinu rođenja. Dobiće podsetnik za vaš rođendan.',
  settingsBirthDateInvalid: 'Unesite datum u obliku DD.MM.GGGG',
  settingsBirthDateAgeRange: 'Starost mora biti između {min} i {max} godina',
  profileYearsSuffix: 'god.',
  birthdayTodayTitle: 'Danas je rođendan',
  birthdaySoonTitle: 'Uskoro rođendani',
  birthdayCongratulate: 'Čestitaj',
  birthdayTurns: 'puni {age}',
  birthdayInDays: 'za {days} d.',
  birthdayTomorrow: 'sutra',
  birthdayPickTemplate: 'Izaberi čestitku',
  birthdayTemplateHint: 'Dodirnite — tekst ide u polje, možete ga izmeniti pre slanja.',
  birthdaySend: 'Pošalji',
  birthdaySent: 'Čestitka poslata',
  birthdaySendFailed: 'Nije moguće poslati čestitku',
  birthdayHide: 'Sakrij',
  savedSectionReminders: 'Podsetnici',
  savedSectionScheduled: 'Zakazano',
  savedSectionEmptyReminders: 'Još nema podsetnika.',
  savedSectionEmptyScheduled: 'Još nema zakazanih poruka.',
  reminderNewAction: 'Novi podsetnik',
  reminderTitlePlaceholder: 'Podseti me da…',
  reminderBodyPlaceholder: 'Detalji (opciono)',
  reminderWhenLabel: 'Kada',
  reminderDatePlaceholder: 'DD.MM.GGGG',
  reminderTimePlaceholder: 'ČČ:MM',
  reminderPrecisionHint: 'Tačnost je oko jednog minuta.',
  reminderSaveAction: 'Sačuvaj',
  reminderSaveFailed: 'Nije moguće sačuvati podsetnik',
  reminderDeleteAction: 'Obriši',
  reminderSnoozeAction: 'Odloži 10 min',
  reminderStatusScheduled: 'Čeka',
  reminderStatusFired: 'Aktiviran',
  reminderStatusCancelled: 'Otkazan',
  reminderInvalidTime: 'Izaberite datum i vreme u budućnosti',
  reminderZoneNote: 'Zadato po {zone}',
  organizerEditAction: 'Izmeni',
  quickToday: 'Danas 18:00',
  quickTomorrow: 'Sutra 09:00',
  quickInAnHour: 'Za sat vremena',
  scheduledNewAction: 'Zakaži poruku',
  scheduledRecipientsLabel: 'Kome',
  scheduledRecipientsEmpty: 'Izaberite bar jednu osobu',
  scheduledRecipientsSearch: 'Pretraga kontakata',
  scheduledTextPlaceholder: 'Tekst poruke',
  scheduledWhenLabel: 'Kada poslati',
  scheduledCreateAction: 'Zakaži',
  scheduledCancelAction: 'Otkaži slanje',
  scheduledMessageSaveFailed: 'Nije moguće zakazati poruku',
  scheduledMessageCancelFailed: 'Nije moguće otkazati',
  scheduledStatusScheduled: 'Čeka slanje',
  scheduledStatusSent: 'Poslato',
  scheduledStatusCancelled: 'Otkazano',
  scheduledStatusFailed: 'Nije isporučeno',
  scheduledTooSoon: 'Izaberite vreme bar minut unapred',
  notifyReminderTitle: 'Podsetnik',
  notifyBirthdayTitle: 'Rođendan',
  notifyBirthdayBody: 'danas slavi rođendan',
  registerAction: 'Napravite nalog',
  registerTitle: 'Novi nalog',
  registerEmailPlaceholder: 'E-pošta',
  registerNamePlaceholder: 'Kako da vas zovemo?',
  registerSubmit: 'Registruj se',
  registerEmailInvalid: 'Ovo ne liči na adresu e-pošte',
  registerPasswordTooShort: 'Najmanje 8 znakova',
  registerNameRequired: 'Unesite ime',
  registerInviteOnly: 'Registracija je trenutno samo uz pozivnicu',
  registerFailed: 'Registracija nije uspela',
  verifySentTitle: 'Proverite poštu',
  verifySentBody: 'Poslali smo link za potvrdu na {email}. Otvorite ga da završite registraciju.',
  verifyResend: 'Pošalji link ponovo',
  verifyResendDone: 'Ako ta adresa čeka potvrdu, link je već na putu.',
  backToLogin: 'Nazad na prijavu',
  forgotAction: 'Zaboravili ste lozinku?',
  forgotTitle: 'Povraćaj pristupa',
  forgotBody: 'Unesite adresu sa kojom ste otvorili nalog i poslaćemo link za novu lozinku.',
  forgotSubmit: 'Pošalji link',
  forgotSentTitle: 'Proverite poštu',
  forgotSentBody: 'Ako nalog koristi {email}, link je već na putu. Radi jednom i važi sat vremena.',
  resetTitle: 'Nova lozinka',
  resetBody: 'Postavite novu lozinku. Svi koji su prijavljeni na ovaj nalog biće odjavljeni.',
  resetNewPassword: 'Nova lozinka',
  resetSubmit: 'Sačuvaj i prijavi se',
  resetDone: 'Lozinka je promenjena. Prijavite se novom.',
  resetFailed: 'Promena lozinke nije uspela',
  verifyDone: 'Adresa je potvrđena. Sada se možete prijaviti.',
  verifyLinkDead: 'Link nije radio: istekao je ili je već iskorišćen. Zatražite novi.',
  captchaRequired: 'Potvrdite da niste robot i pokušajte ponovo.',
  forwardSelectCount: 'Izabrano: {count}',
  selectMessagesAction: 'Izaberi',
  deleteSelectedAction: 'Obriši',
  deleteSelectedConfirmTitle: 'Obrisati izabrane poruke?',
  deleteSelectedConfirmBody: 'Poruka: {count}. Nestaće za sve učesnike ćaskanja i ne mogu se vratiti.',
  deleteSelectedFailed: 'Deo poruka nije obrisan',
  forwardAction: 'Prosledi',
  forwardDoneBody: 'Prosleđeno poruka: {count}',
  replyAction: 'Odgovori',
  replyQuoteLabel: 'Odgovor',
  replyOriginalDeleted: 'Poruka je obrisana',
  copyTextAction: 'Kopiraj tekst',
  copyTextDone: 'Kopirano',
  editAction: 'Izmeni',
  editingMessageTitle: 'Izmena',
  editMessageFailed: 'Poruka ne može da se izmeni',
  messageEditedMark: 'izm.',
  deleteMessageAction: 'Obriši',
  deleteMessageFailed: 'Poruka ne može da se obriše',
  reactionLimitReached: 'Najviše tri reakcije po poruci',
  savedClearAction: 'Očisti',
  savedClearDone: '„Sačuvano“ je prazno',
  savedClearFailed: 'Nije uspelo čišćenje „Sačuvano“',
  helpCenterTitle: 'Saveti i novosti',
  helpCenterOpenLabel: 'Saveti i novosti',
  helpCenterOpenLabelUnread: 'Saveti i novosti — ima nepročitanog',
  helpCenterNewBadge: 'Novo',
  helpCenterAllReadHint: 'Sve je pročitano. Sijalica će zatreptati kad stigne nova beleška.',
  helpCenterIntro: 'Šta aplikacija ume i kako se to koristi. Nove beleške idu na vrh i teraju sijalicu u zaglavlju da trepće.',
  settingsHandleSuggestions: 'Slobodne varijante',
  saveChatImage: 'Sačuvaj sliku',
  saveChatImageSuccess: 'Slika je sačuvana',
  saveChatImageFailed: 'Slika nije mogla da se sačuva',
  saveChatImagePermissionDenied: 'Dozvolite pristup galeriji da biste čuvali slike',
  saveChatVideo: 'Sačuvaj video',
  saveChatVideoSuccess: 'Video je sačuvan',
  saveChatVideoFailed: 'Video nije mogao da se sačuva',
  saveChatVideoPermissionDenied: 'Dozvolite pristup galeriji da biste čuvali video zapise',
  forwardSameChatBlocked: 'Ne možete proslediti poruke u isti čet',
  messageReferenceNotFound: 'Originalna poruka nije pronađena u učitanoj istoriji',
  gearSettings: 'Podešavanja naloga',
  myDruzhina: 'Moj tim',
  myDruzhinaHint: 'U timu su samo oni kojima veruješ kao sebi.',
  myDruzhinaVarangian: 'Hird',
  myDruzhinaHintVarangian: 'U hirdu su samo oni kojima veruješ kao sebi.',
  contactRequestIncomingTitle: 'Dolazni zahtevi',
  contactLoadFailed: 'Kontakti ne mogu da se učitaju',
  contactRequestLoadFailed: 'Zahtevi ne mogu da se učitaju',
  contactRequestSendFailed: 'Zahtev ne može da se pošalje',
  contactRequestApproveFailed: 'Zahtev ne može da se odobri',
  contactRequestRejectFailed: 'Zahtev ne može da se odbije',
  contactSearchSelfRow: 'Vi ste',
  chatLastMessageFallback: 'Još nema poruka',
  unseenReactionA11y: 'Nova reakcija: {emoji}',
  settingsHapticsLabel: 'Haptički odziv',
  settingsSoundsLabel: 'Zvukovi',
  retentionNoticeTitle: 'Prepiska je automatski očišćena',
  retentionNoticeBody:
    'Direktne poruke se brišu sa servera nakon nekoliko dana — tako je i predviđeno, ništa nije izgubljeno.',
  retentionNoticeWhen: 'Poslednje čišćenje: {when}',
  contactRemoveConfirmTitle: 'Ukloniti iz tima?',
  contactRemoveConfirmTitleVarangian: 'Ukloniti iz hirda?',
  contactRemoveConfirmBody:
    'Bićete uklonjeni jedno drugom iz kontakata, a direktna prepiska će biti trajno obrisana kod obe strane.',
  contactRemoveFailed: 'Kontakt ne može da se ukloni',
  profileUpdateFailed: 'Profil ne može da se ažurira',
  settingsProfileSection: 'Profil',
  settingsChangeNameLabel: 'Promeni ime za prikaz',
  settingsChangeLoginLabel: 'Promeni prijavu',
  settingsSaveProfile: 'Sačuvaj profil',
  settingsSaved: 'Promene su sačuvane',
  settingsShareSection: 'Javni link i nadimak',
  settingsPublicHandleLabel: 'Javni nadimak (bez #)',
  settingsPublicHandleHint:
    '4–32 znaka: mala slova, cifre, donja crta. Ispraznite polje da uklonite nadimak.',
  settingsProfilePublicLabel: 'Javna stranica profila',
  settingsAllowDiscoveryLabel: 'Pronađiv po nadimku u pretrazi',
  settingsKeepPhotoMetadataLabel: 'Čuvaj metapodatke fotografija',
  settingsKeepPhotoMetadataHint:
    'Isključeno: iz poslatih fotografija uklanjaju se lokacija, model kamere i serijski broj; orijentacija i datum snimanja ostaju. Uključeno: fajl se šalje sa svime što sadrži.',
  settingsHandleWillSaveAs: "Sačuvaćemo kao: #{handle}",
  settingsHandleChecking: "Proveravamo…",
  settingsHandleFree: "Nadimak je slobodan",
  settingsHandleTaken: "Nadimak je zauzet",
  settingsHandleFormat: "4–32 znaka: mala latinična slova, cifre, donja crta",
  settingsHandleReserved: "Ovaj nadimak je rezervisan",
  settingsHandleNotAllowed: "Ovaj nadimak nije dozvoljen",
  settingsHandleSaveFailedInvalid: "Server nije prihvatio ovaj nadimak. Probajte drugi.",
  settingsHandleConfirmChangeTitle: "Promeniti nadimak?",
  settingsHandleConfirmChangeBody: "Linkovi sa starim nadimkom prestaće da rade, a nadimak može zauzeti neko drugi.",
  settingsHandleConfirmClearTitle: "Ukloniti nadimak?",
  settingsHandleConfirmClearBody: "Javna stranica će se zatvoriti, linkovi prestaju da rade, a nadimak postaje dostupan drugima.",
  settingsHandleKeep: "Zadrži",
  settingsSaveShareSettings: 'Sačuvaj nadimak',
  settingsCopyHandleTag: 'Kopiraj #nadimak',
  settingsCopied: 'Kopirano',
  profileLinkUnavailable: "Profil nije dostupan ili taj nadimak više ne postoji.",
  profileLinkSelf: "To je link ka vašem sopstvenom profilu.",
  settingsShareDisabledHidden: "Javna stranica profila je isključena — uključite je da biste delili link.",
  settingsShareDisabledUnsaved: "Prvo sačuvajte nadimak — link se gradi od sačuvanog.",
  settingsProfileSaved: "Profil je sačuvan",
  settingsShareSaved: "Nadimak je sačuvan",
  settingsPasswordChanged: "Lozinka je promenjena",
  reportAction: "Prijavi",
  reportTitle: "Šta nije u redu?",
  reportSent: "Prijava je poslata. Proverićemo.",
  reportFailed: "Prijava nije poslata",
  reportCategorySpam: "Spam ili reklama",
  reportCategoryHarassment: "Uvrede ili pretnje",
  reportCategorySexualContent: "Seksualni sadržaj",
  reportCategoryViolence: "Nasilje",
  reportCategoryFraud: "Prevara",
  reportCategoryChildSafety: "Bezbednost deteta",
  reportCategoryOther: "Nešto drugo",
  blockAction: "Blokiraj",
  blockConfirmTitle: "Blokirati ovu osobu?",
  blockConfirmHint: "Neće moći da vam piše ni da vas zove. Poništićete to u «Podešavanjima» → «Privatnost».",
  blockDone: "Blokirano",
  blockFailed: "Blokiranje nije uspelo",
  unblockAction: "Odblokiraj",
  settingsBlockedSection: "Blokirani",
  settingsBlockedEmpty: "Niste nikoga blokirali",
  moderationCancel: "Otkaži",
  settingsDeleteAccountSection: "Brisanje naloga",
  settingsDeleteAccountWarning:
    "Brisanje je nepovratno. Profil, kontakti, podsetnici i «Sačuvano» se brišu. Poruke koje ste poslali ostaju u ćaskanjima sagovornika, potpisane kao «Obrisani nalog».",
  settingsDeleteAccountStart: "Obriši nalog",
  settingsDeleteAccountConfirmHint:
    "Unesite lozinku i otkucajte reč ispod da biste potvrdili.",
  settingsDeleteAccountConfirmWord: "OBRIŠI",
  settingsDeleteAccountConfirmPlaceholder: "Otkucajte OBRIŠI",
  settingsDeleteAccountConfirm: "Obriši zauvek",
  settingsDeleteAccountCancel: "Otkaži",
  settingsDeleteAccountPasswordRequired: "Unesite trenutnu lozinku",
  settingsDeleteAccountWordMismatch: "Reč za potvrdu se ne poklapa",
  settingsDeleteAccountFailed: "Nalog nije obrisan",
  settingsLegalSection: "Dokumenti",
  settingsPrivacyPolicyLink: "Politika privatnosti",
  settingsTermsLink: "Uslovi korišćenja",
  settingsLocalOnlyHint: "Primenjuje se odmah, samo na ovom uređaju",
  settingsNamePlaceholder: "Vaše ime",
  settingsPublicHandlePlaceholder: "my_handle",
  settingsAvatarTitle: 'Profilna fotografija',
  settingsAvatarChooseShape: 'Okvir',
  settingsAvatarRemove: 'Ukloni foto',
  settingsAvatarUploadFailed: 'Otpremanje fotografije nije uspelo',
  avatarAnimatedTooLarge: 'Animacija je preteška — izaberite GIF do 8 MB.',
  galleryPermissionDenied:
    'Nema pristupa fotografijama. Uključite ga u podešavanjima — dovoljan je pristup izabranim fotografijama.',
  settingsAvatarWebHint: 'Web: izaberite fajl u dijalogu; otpremanje ide preko multipart.',
  avatarChangePhoto: 'Promeni fotografiju',
  avatarTapToChange: 'Dodirnite fotografiju da je promenite',
  avatarTakePhoto: 'Slikaj',
  avatarFromLibrary: 'Izaberi iz galerije',
  avatarCameraDenied: 'Nema pristupa kameri. Uključite ga u podešavanjima sistema.',
  avatarRemoveConfirm: 'Fotografija nestaje za sve; ostaju inicijali.',
  avatarEditorHint: 'Primakni prstima za zum, prevuci za pomeranje, dvoklik za reset',
  avatarEditorHintPanOnly: 'Prevucite fotografiju da izaberete kadar',
  avatarPreviewCard: 'Pogledajte kako vas drugi vide',
  chatAvatarSectionTitle: 'Fotografija četa',
  userCardTitle: 'Korisnik',
  userCardOpenPhoto: 'Otvori fotografiju',
  userCardPhotoHint: 'Primakni prstima ili dvoklik za zum, dodir zatvara',
  userCardPhotoFriendsOnly: 'Fotografiju preko celog ekrana otvaraju samo prijatelji',
  userCardPhotoLoadFailed: 'Učitavanje fotografije nije uspelo',
  chatUnknownMember: 'Član',
  contactCardLabel: 'Kontakt',
  contactShareAction: 'Podeli kontakt',
  contactShareChooseChat: 'Kome poslati kontakt',
  contactShareSent: 'Kontakt poslat',
  contactShareFailed: 'Slanje kontakta nije uspelo',
  checklistItemA11y: 'Stavka spiska',
  checklistCheckedBy: 'Označio/la {name}',
  checklistToggleFailed: 'Ažuriranje spiska nije uspelo',
  checklistDuePast: 'Taj rok je već prošao. Navedite vreme koje tek dolazi.',
  checklistDueUnparsed: 'Rok nije razumljiv. Pišite {20:00} ili {02.02.2026 15:00}.',
  checklistAssigneeInvalid: 'Jedan izvršilac po stavci, i to nadimkom: #nadimak',
  checklistItemEmpty: 'Stavka mora imati tekst: zagrade i rok idu uz njega, a ne umesto njega.',
  checklistAssigneeNotFound: 'Nema profila s tim nadimkom',
  checklistDueToday: 'danas {time}',
  checklistDueTomorrow: 'sutra {time}',
  checklistOverdue: 'Rok istekao',
  checklistDueA11y: 'Rok: {when}',
  checklistAssigneeA11y: 'Izvršilac: {name}',
  channelPostCommentsCount: '💬 {count}',
  settingsLoginHint: 'Slova, brojevi, tačka, donja crta, crtica',
  loginCheckInProgress: 'Provera prijave...',
  loginAvailable: 'Prijava je slobodna',
  loginUnavailable: 'Prijava je zauzeta',
  chatTileCallAudio: 'Glasovni poziv',
  chatTileCallVideo: 'Video poziv',
  channelReadOnlyHint: 'Kanal je samo za čitanje: objavljuju admin i urednici.',
  channelCommentComposerHint: 'Da biste komentarisali, dodirnite 💬 „Komentari“ ispod objave.',
  channelUnsubscribeConfirmBody: 'Otkazati pretplatu na kanal? Nestaće sa liste ćaskanja.',
  channelUnsubscribeFailed: 'Otkazivanje pretplate nije uspelo.',
  channelPostRestrictedHint: 'Ne možete objaviti post u ovom kanalu.',
  channelArticlesLoadFailed: 'Učitavanje članaka nije uspelo.',
  channelArticlesDrafts: 'Nacrti',
  channelArticleStatusDraft: 'Nacrt',
  channelDraftSavedTitle: 'Nacrt sačuvan',
  channelDraftSavedBody: 'Nacrt je u odeljku Članci na vrhu kanala.',
  channelDraftView: 'Otvori nacrt',
  channelPostPublishedTitle: 'Članak objavljen',
  channelSettingsTitle: 'Podešavanja kanala',
  channelDraftsSettingsHint: 'Nacrti — dodirnite da uredite ili objavite.',
  channelDraftsEmpty: 'Nema nacrta.',
  deleteChannelConfirmBody: 'Kanal i svi članci biće trajno obrisani.',
  channelPostOpenFailed: 'Članak se ne može otvoriti.',
  channelPostLoadFailed: 'Učitavanje članka nije uspelo.',
  channelPostComments: 'Komentari',
  channelCommentsClosed: 'Komentarisanje na ovom kanalu je zatvoreno.',
  channelPublishAction: 'Objavi',
  channelPublishDraftTitle: 'Objaviti ovaj nacrt?',
  channelPublishFailed: 'Objavljivanje nije uspelo.',
  channelDraftOpen: 'Otvori',
  channelSubscribedTitle: 'Pretplata je aktivna',
  channelGuestSubscribeHint:
    'Čitate kanal kao gost. Pretplaćenih: {count} — nove članke dobijaćete i vi.',
  channelArchiveTitle: 'Svi članci',
  channelArchiveEmpty: 'Još nema objavljenih članaka.',
  channelArchiveLoadMore: 'Prikaži još',
  editorPreview: 'Pregled',
  editorPreviewBack: 'Nazad na uređivanje',
  editorInsertHint: 'Izaberite tip bloka ispod — staće odmah iza ovog.',
  editorInvalidChannel:
    'Ekran je otvoren bez kanala. Zatvorite ga, otvorite kanal iz liste i započnite članak odande.',
  blockParagraph: 'Pasus',
  blockHeading: 'Naslov',
  blockImage: 'Slika',
  blockQuote: 'Citat',
  blockBulletList: 'Lista',
  blockOrderedList: 'Numerisana lista',
  blockDivider: 'Razdelnik',
  blockCode: 'Kôd',
  blockEmbed: 'Ugrađeni sadržaj',
  blockVideo: 'Link ka videu',
  blockGallery: 'Galerija',
  editorTitleLabel: 'Naslov',
  editorTitlePlaceholder: 'Naslov članka',
  editorBodyLabel: 'Tekst',
  editorAddCover: 'Dodaj naslovnu sliku',
  editorChangeCover: 'Zameni naslovnu sliku',
  editorRemoveCover: 'Ukloni naslovnu sliku',
  editorSaveDraft: 'U nacrte',
  editorPublish: 'Objavi',
  editorListItem: 'Stavka liste',
  editorBlockEmpty: 'prazno',
  editorReplaceImage: 'Zameni sliku',
  editorUploadImage: 'Otpremi sliku',
  editorAddPhotos: 'Dodaj slike',
  editorAddBlockA11y: 'Dodaj: {name}',
  editorInsertHere: 'Umetni blok',
  commentsTitle: 'Komentari',
  commentsReply: 'Odgovori',
  commentsReplyingTo: 'Odgovor za {name}',
  commentsPlaceholder: 'Napišite komentar…',
  commentsSend: 'Pošalji',
  commentsPending: 'na proveri',
  commentsThumbUp: 'Sviđa mi se',
  commentsDelete: 'Obriši',
  commentsApprove: 'Odobri',
  commentsBan: 'Zabrani',
  commentsDeleteConfirmBody: 'Komentar nestaje iz diskusije.',
  commentsBanConfirmTitle: 'Zabraniti komentarisanje?',
  commentsBanConfirmBody: '{name} više neće moći da komentariše na ovom kanalu.',
  commentsModerationFailed: 'Radnja nije izvršena.',
  articleReadMore: 'Pročitaj članak →',
  articleCardKicker: 'Članak',
  channelMuteOn: 'Isključi obaveštenja',
  channelMuteOff: 'Uključi obaveštenja',
  channelMuteFailed: 'Promena obaveštenja nije uspela.',
  channelViewsCount: '👁 {count}',
  channelPostEdit: 'Izmeni',
  channelPostEditTitle: 'Izmena članka',
  channelPostEditPublishedHint:
    'Čuvanje menja članak i onima koji ga već imaju. Pretplatnici ne dobijaju novo obaveštenje.',
  channelPostSaveChanges: 'Sačuvaj',
  channelPostSaved: 'Izmene su sačuvane',
  channelPostSaveFailed: 'Članak nije sačuvan.',
  channelPostDelete: 'Obriši',
  channelPostDeleteConfirmTitle: 'Obrisati članak?',
  channelPostDeleteConfirmBody:
    'Članak i njegova najava u kanalu nestaju zauvek. Nestaje i onima koji su ga već otvorili.',
  channelPostDeleteDraftConfirmBody: 'Nacrt nestaje zauvek.',
  channelPostDeleteFailed: 'Brisanje članka nije uspelo.',
  channelPostUnpublish: 'U nacrte',
  channelPostUnpublishConfirmTitle: 'Vratiti u nacrte?',
  channelPostUnpublishConfirmBody:
    'Najava nestaje iz kanala. Tekst ostaje u nacrtima i možete ga ponovo objaviti.',
  channelPostUnpublishFailed: 'Vraćanje u nacrte nije uspelo.',
  channelDraftRestoredHint: 'Vraćen je nesačuvan tekst od prošlog puta.',
  channelDraftLeaveTitle: 'Izaći iz uređivača?',
  channelDraftLeaveBody:
    'Nesačuvan tekst ostaje na ovom uređaju — uređivač će ga ponuditi kad ga sledeći put otvoriš.',
  channelDraftLeaveConfirm: 'Izađi',
  channelDraftLeaveStay: 'Ostani',
  channelDraftDiscardRestored: 'Ukloni vraćeni tekst',
  channelSlugLabel: 'Adresa kanala',
  channelSlugPlaceholder: 'my_channel',
  channelSlugHint:
    '4–32 znaka: mala latinična slova, cifre, donja crta. Od nje se pravi link ka kanalu.',
  channelSlugInvalid:
    'Adresa mora imati 4–32 znaka i sadržati samo mala latinična slova, cifre i donje crte.',
  channelCommentSendFailed: 'Komentar nije poslat.',
  channelCommentsLoadFailed: 'Učitavanje komentara nije uspelo.',
  chatFilterGroup: 'Grupe',
  chatFilterChannel: 'Kanali',
  chatSortRecent: 'prvo najnovije',
  chatSortOld: 'prvo najstarije',
  chatSortTitleAsc: 'po nazivu A-Š',
  chatSortTitleDesc: 'po nazivu Š-A',
  callScopeBadgeGroupVideo: 'Grupni video poziv',
  callScopeBadgeDirectVideo: 'Lični video poziv',
  groupCallUnavailableTitle: 'Grupni pozivi nisu dostupni',
  groupCallsTemporarilyOff: 'Grupni pozivi su privremeno isključeni. Lični pozivi rade.',
  groupCallUnavailableDisabled: 'Server za grupne pozive nije podešen. Obratite se podršci.',
  groupCallUnavailableNetwork: 'Nije moguće dobiti server poziva. Proverite vezu i pokušajte ponovo.',
  groupCallUnavailableConnect: 'Povezivanje na poziv nije uspelo. Pokušajte ponovo za trenutak.',
  groupCallJoinOngoing: 'Pridruži se',
  groupCallOngoingBadge: 'Poziv je u toku',
  callDiagnosticsTitle: 'Dijagnostika poziva',
  callDiagnosticsHint: 'Dugi pritisak — dijagnostika poziva',
  callDiagnosticsCopy: 'Kopiraj izveštaj',
  callDiagTransport: 'Transport',
  callDiagCandidatePair: 'Par kandidata',
  callDiagBitrate: 'Bitrate',
  callDiagPacketsLost: 'Izgubljeni paketi',
  callDiagParticipants: 'Učesnika',
  screenShareTitle: 'Deljenje ekrana',
  screenShareBody: 'Ovaj pregledač nije dozvolio snimanje ekrana.',
  screenShareBusyBody: 'Neko već deli ekran. Sačekajte da završi.',
  screenShareLabel: 'deli ekran',
  callShareScreen: 'Podeli ekran',
  callShareScreenStop: 'Zaustavi deljenje',
  callSwitchCamera: 'Okreni kameru',
  settingsAppearanceSection: 'Izgled',
  settingsVisualThemeLabel: 'Stil aplikacije',
  visualThemeNeutral: 'Neutralan',
  visualThemeVarangian: 'Varjaški',
  visualThemeDark: 'Tamni',
  visualThemeAuto: 'Auto · Sunce / Kosmos',
  settingsLanguageSection: 'Jezik',
  settingsLanguageLabel: 'Jezik interfejsa',
  settingsTabPreferences: 'Aplikacija',
  settingsTabAccount: 'Nalog',
  settingsTabAvatar: 'Foto',
  settingsTabProfile: 'Profil',
  settingsTabPrivacy: 'Privatnost',
  settingsLoginSection: 'Korisničko ime za prijavu',
  settingsSaveLogin: 'Sačuvaj korisničko ime',
  settingsLoginSaved: 'Korisničko ime je sačuvano',
  settingsPasswordSection: 'Lozinka',
  settingsVisibilitySection: 'Ko može da vas pronađe',
  settingsProfilePublicHint: 'Isključeno — link ka profilu prestaje da se otvara za sve.',
  settingsAllowDiscoveryHint: 'Isključeno — više vas neće pronaći po nadimku u pretrazi.',
  settingsMediaPrivacySection: 'Fotografije i podaci',
  settingsShareDisabledNoHandle: 'Postavite nadimak i sačuvajte ga — link se gradi od njega.',
  settingsPreferencesHint: 'Tema i jezik se primenjuju odmah — lozinka nije potrebna.',
  settingsPreferencesDone: 'Gotovo',
  settingsCurrentPasswordRequired: 'Unesite trenutnu lozinku da biste postavili novu.',
  directRetentionPolicyTitle: 'Čišćenje ličnog četa',
  directRetentionPolicyBody:
    'Iz bezbednosnih razloga, istorija ličnih četova na serveru se periodično briše. Čišćenje ovog četa uskoro sledi. Sačuvajte na uređaj sve što vam je važno: poruke, fotografije, fajlove — ako je potrebno.',
  directRetentionTimerA11y: 'Vreme do brisanja istorije na serveru. Dodirnite za detalje.',
  quickRepliesTitle: 'Brzi odgovori',
  quickReplyAddTitle: 'Novi brzi odgovor',
  quickReplyAddPlaceholder: 'Vaša fraza…',
  quickReplySave: 'Sačuvaj',
  quickReplyDelete: 'Obriši',
  quickReplyDeleteConfirmTitle: 'Obrisati brzi odgovor?',
  quickReplyDefaultGreeting: 'Zdravo!',
  quickReplyDefaultInTouch: 'Tu sam',
  quickReplyDefaultThanks: 'Hvala!',
  sharePost: 'Podeli objavu',
  shareChannel: 'Podeli kanal',
  shareProfile: 'Podeli profil',
  settingsBioLabel: 'O meni',
  settingsBioPlaceholder: 'Par reči o sebi…',
  settingsStatusLabel: 'Status',
  settingsStatusPlaceholder: 'Čime se baviš?',
  selfStatusEmpty: 'Dodaj status',
  quickSettingsTitle: 'Brza podešavanja',
  selfStatusSave: 'Sačuvaj',
  dndLabel: 'Ne uznemiravaj',
  dndHint: 'Nema obaveštenja ni od koga. Pozivi i dalje stižu.',
  dndFailed: 'Podešavanje nije promenjeno',
  notifyShowTextLabel: 'Prikaži tekst poruke',
  notifyShowTextHint: 'Isključeno: obaveštenje kaže samo „Nova poruka”, bez teksta.',
  userCardAddContact: 'Dodaj kontakt',
  userCardContactPending: 'Zahtev poslat',
  navTitleNewPost: 'Novi članak',
  navTitlePost: 'Članak',
};

/** French */
const fr: Record<BaseMessageKey, string> = {
  appName: "SVYAZ",
  appTagline: "Appels et discussions — rester en contact",
  loginEpigraphCalm:
    "Un espace posé pour ceux qui veulent une liaison fiable : messages et appels au même endroit, avec une attention à la vie privée.",
  loginEpigraphVarangian:
    "En ces temps difficiles, rien n’arrête les vieux Varègues et rien ne les décourage — nous trouverons toujours un moyen de rester en contact ! Skol",
  loginField: "Identifiant ou e-mail",
  passwordPlaceholder: "Mot de passe (min. 8 caractères)",
  loginRequired: "Saisissez votre identifiant ou votre adresse e-mail",
  loginInvalid: "Un identifiant se compose de lettres latines, de chiffres, du point, du tiret et du tiret bas. Ou saisissez votre adresse e-mail",
  passwordRequired: "Saisissez votre mot de passe",
  validationFailed: "Vérifiez les informations saisies",
  validationFieldRequired: "Renseignez le champ obligatoire",
  validationFieldLength: "La longueur de la valeur n'est pas valide",
  validationFieldFormat: "La valeur contient des caractères non autorisés",
  errorGeneric: "Erreur",
  signIn: "Se connecter",
  logout: "Se déconnecter",
  group: "Groupe",
  chatTypeGroup: "Groupe",
  chatTypeChannel: "Canal",
  noChatsHint: "Pas encore de discussions — démarrez une conversation privée ou de groupe",
  nicknamePlaceholder: "Pseudonyme ou partie du nom…",
  searchError: "Erreur de recherche",
  nobodyFound: "Personne trouvé",
  close: "Fermer",
  settingsUnsavedTitle: "Modifications non enregistrées",
  settingsUnsavedBody: "Les enregistrer avant de fermer ?",
  settingsUnsavedDiscard: "Fermer sans enregistrer",
  settingsUnsavedStay: "Revenir",
  settingsNewPasswordTooShort: "Le nouveau mot de passe doit contenir au moins 8 caractères",
  newChannelTitle: "Nouveau canal",
  channelNamePlaceholder: "Nom du canal",
  channelEditorsHint: "Les éditeurs peuvent publier des messages dans ce canal.",
  channelInviteSubscribersHint: "Les invités deviennent abonnés. Ajoutez les éditeurs plus tard dans les paramètres du canal.",
  addSubscriber: "Ajouter un abonné",
  addEditor: "Ajouter un éditeur",
  channelAllowReactions: "Autoriser les réactions",
  channelAllowComments: "Autoriser les commentaires",
  createChannelFailed: "Impossible de créer le canal",
  channelNameRequired: "Entrez le nom du canal",
  channelDescriptionPlaceholder: "Description",
  channelsHubTitle: "Canaux",
  channelSearchPlaceholder: "Rechercher des canaux…",
  channelsHubEmpty: "Aucun canal pour le moment",
  channelPreviewTitle: "Aperçu du canal",
  channelPreviewEmptyPosts: "Aucune publication pour le moment",
  channelPreviewUnavailable: "Ce canal n’a pas d’aperçu public — abonnez-vous pour le lire",
  channelPreviewSubscribeHint: "Abonnez-vous pour retrouver le canal dans vos discussions",
  channelSubscribe: "S’abonner",
  channelUnsubscribe: "Se désabonner",
  channelManage: "Gérer",
  channelSubscribersCount: "Abonnés : {count}",
  channelDirectoryLoadFailed: "Impossible de charger les canaux",
  channelSubscriptionFailed: "Impossible de modifier l’abonnement",
  channelUpdateFailed: "Impossible de mettre à jour le canal",
  channelEditorAddFailed: "Impossible d’ajouter l’éditeur",
  channelEditorRemoveFailed: "Impossible de retirer l’éditeur",
  deleteChannel: "Supprimer le canal",
  saveChannel: "Enregistrer le canal",
  groupNamePlaceholder: "Nom",
  removeMember: "Retirer",
  cancel: "Annuler",
  dialogOk: "Compris",
  create: "Créer",
  groupInfoTitle: "Infos du groupe",
  groupTabMembers: "Membres",
  groupTabSettings: "Paramètres",
  groupMembersLabel: "{count} membres",
  groupYouSuffix: "(vous)",
  groupAdminBadge: "Admin",
  groupSearchPlaceholder: "Rechercher des utilisateurs à ajouter…",
  groupNoUsersFound: "Aucun utilisateur trouvé",
  groupAddAction: "Ajouter",
  groupRetry: "Réessayer",
  groupLeaveBtn: "Quitter le groupe",
  groupLeaveTitle: "Quitter le groupe",
  groupLeaveBody: "Voulez-vous vraiment quitter ce groupe ?",
  groupLeaveConfirm: "Quitter",
  groupLeaveFailed: "Échec pour quitter le groupe",
  groupNameLabel: "Nom du groupe",
  groupDescLabel: "Description",
  groupDescPlaceholder: "Description du groupe…",
  groupSaveChanges: "Enregistrer les modifications",
  groupNameRequired: "Le nom du groupe est requis",
  groupInfoSaved: "Infos du groupe mises à jour",
  groupInfoSaveFailed: "Échec de l'enregistrement des infos du groupe",
  groupChangePhoto: "Changer la photo",
  groupRemoveMemberTitle: "Retirer le membre",
  groupRemoveMemberBody: "Retirer {name} du groupe ?",
  groupRemoveAction: "Retirer",
  groupRemoveMemberFailed: "Échec du retrait du membre",
  groupMakeAdminTitle: "Nommer admin",
  groupRemoveAdminTitle: "Retirer l'admin",
  groupMakeAdminBody: "Nommer {name} administrateur ?",
  groupRemoveAdminBody: "Retirer les droits d'administrateur à {name} ?",
  groupChangeRoleFailed: "Échec du changement de rôle",
  groupAddMemberFailed: "Échec de l'ajout du membre",
  groupLoadMembersFailed: "Échec du chargement des membres",
  deleteChatConfirmTitle: "Supprimer la discussion ?",
  deleteChatFailed: "Impossible de supprimer la discussion",
  errorTitle: "Erreur",
  openChatFailed: "Impossible d’ouvrir la discussion",
  createGroupFailed: "Impossible de créer le groupe",
  callUnavailableTitle: "Appel indisponible",
  webrtcExpoGoChat: "La voix fonctionne uniquement dans une build de dev (pas dans Expo Go). mobile : npx expo prebuild, puis run:android / run:ios.",
  webrtcExpoGoCall: "La voix fonctionne uniquement dans une build de dev (pas dans Expo Go). Dans le dossier mobile : npx expo prebuild, puis npx expo run:android ou run:ios.",
  webrtcWebChat: "WebRTC, micro et HTTPS (ou localhost) sont requis.",
  webrtcWebCall: "Un navigateur compatible WebRTC, l’accès au micro et HTTPS (ou localhost) sont requis.",
  loadMessagesFailed: "Impossible de charger les messages",
  sendFailed: "Échec de l’envoi",
  messageSending: "Envoi…",
  messageUploading: "Téléversement…",
  messageCompressing: "Compression…",
  messageNotDelivered: "Non délivré",
  audioStopRecord: "Arrêter l’enregistrement",
  audioStartRecord: 'Enregistrer un message vocal',
  accessibilityAttachMenu: 'Joindre',
  audioCancelRecord: "Annuler l’enregistrement",
  accessibilitySend: "Envoyer",
  audioPlay: "Lire la voix",
  audioPause: "Lecture…",
  transcriptShow: "Texte",
  transcriptHide: "Replier",
  transcriptPending: "Transcription…",
  transcriptReadAll: "Lire en entier",
  transcriptFailed: "Transcription impossible",
  transcriptTooLong: "Enregistrement trop long pour la transcription",
  transcriptSheetTitle: "Texte du message vocal",
  audioRecordFailed: "Impossible d’enregistrer le message vocal",
  audioPlayFailed: "Impossible de lire le message vocal",
  audioRecordUnsupported: "L’enregistrement vocal n’est pas pris en charge dans ce navigateur",
  audioRecordSecureContext: "L’enregistrement vocal sur le web nécessite HTTPS ou localhost",
  videoRecordFailed: "Impossible d’enregistrer le message vidéo",
  videoDurationLimit: "Le message vidéo doit durer 44 secondes ou moins",
  videoPayloadTooLarge: "Fichier trop volumineux pour l’envoi. Vidéo : limite indicative 100 Mo, débit cible jusqu’à 5 Mbit/s, 1080p.",
  callReady: "Prêt à appeler",
  callCalling: "Appel… en attente de réponse",
  callConnecting: "Connexion…",
  callReconnecting: "Reconnexion…",
  messagePlaceholder: "Message…",
  emojiHide: "Masquer les emoji",
  emojiShow: "Emoji",
  emojiFrequent: "Souvent utilisés",
  emojiPopular: "Populaires",
  emojiDarkSkin: "Peau foncée (HD)",
  endCall: "Raccrocher",
  accessibilityCall: "Appeler",
  callNavOutgoing: "Appel",
  callNavIncoming: "Entrant",
  callIncomingFull: "Appel entrant",
  callIncomingAudioLine: "Appel vocal",
  callIncomingVideoLine: "Appel vidéo",
  callIncomingChipAudio: "Audio",
  callIncomingChipVideo: "Vidéo",
  callTalkingAudio: "Appel vocal",
  callDuration: "Durée de l’appel",
  callBtnAnswer: "Répondre",
  callBtnReject: "Refuser",
  callBtnHangup: "Raccrocher",
  callFullScreenIntentWarn:
    "Les alertes d’appel plein écran sont désactivées — l’interface d’appel entrant peut ne pas s’afficher sur écran verrouillé.",
  callFullScreenIntentSettings: "Alertes d’appel plein écran (réglages système)",
  callUseSpeaker: "Haut-parleur",
  callUseEarpiece: "Écouteur",
  callMicMute: "Couper le micro",
  callMicUnmute: "Réactiver le micro",
  callMicUnavailable: "Micro indisponible — les autres ne vous entendent pas.",
  callSpeakingNow: "Parle",
  accessibilityVideoCall: "Appel vidéo",
  nonJsonServer: "Le serveur n’a pas renvoyé du JSON. Vérifiez que le backend tourne (port 18080) et que l’URL de l’API est correcte : EXPO_PUBLIC_API_BASE ou web local → 127.0.0.1:18080.",
  loginFailed: "Échec de la connexion",
  loginTooManyAttempts: "Trop de tentatives de connexion. Patientez un instant, puis réessayez.",
  searchFailed: "La recherche n’a pas pu être effectuée",
  chatFallback: "Discussion",
  newMessage: "Nouveau message",
  callGeneric: "Appel",
  callGroupIncomingFrom: "{name} appelle",
  callScopeBadgeGroupAudio: "Appel de groupe",
  callParticipantOrdinal: "Participant n° {n}",
  callParticipantsInCall: "Dans l'appel : {count}",
  callEventMissedOutgoing: "📞 Pas de réponse",
  callEventMissedIncoming: "📞 Appel manqué",
  callEventDeclinedOutgoing: "📞 Refusé",
  callEventBusy: "📞 Occupé",
  callEventEnded: "📞 Appel terminé",
  peerDefault: "Contact",
  callVideoFallbackAudio: "Caméra indisponible, réponse en audio seulement.",
  callPeerBusy: "La ligne est occupée.",
  notifyChannelMessages: "Messages",
  notifyChannelMessagesSilent: "Messages (application ouverte)",
  notifyChannelCalls: "Appels",
  notifyBodyMessage: "Vous avez un nouveau message",
  notifyContactRequestTitle: "Demande d'ami",
  notifyContactRequestBody: "souhaite vous ajouter en ami",
  notifyCommentReplyTitle: "Réponse à votre commentaire",
  androidNotifyCallsTitle: 'Activer les notifications',
  androidNotifyCallsBody:
    "Les appels et les alertes passent par les notifications. Activez-les pour cette application dans les réglages du système — sinon les appels entrants en arrière-plan peuvent rester silencieux.",
  androidNotifyCallsOpenSettings: 'Ouvrir les réglages',
  androidNotifyCallsLater: 'Plus tard',
  androidFullScreenCallsTitle: "Appels en plein écran",
  androidFullScreenCallsBody:
    "Android 14 et versions suivantes n’affichent l’appel entrant en plein écran qu’avec votre autorisation. Sans elle, les appels arrivent quand même, mais comme une notification ordinaire.",
  onlineNow: "en ligne",
  scrollToLatest: "Aller au dernier message",
  sessionExpired: "Session expirée. Reconnectez-vous.",
  photoPickFailed: "Impossible d’ajouter la photo",
  accessibilityExpandVideo: "Plein écran",
  accessibilityPrevPhoto: "Photo précédente",
  accessibilityNextPhoto: "Photo suivante",
  videoResolutionTooHigh: "La résolution vidéo doit être au plus 1080p (grand côté 1920 px).",
  photoProcessFailed: "Impossible de traiter l’image (redimensionnement / encodage).",
  photoPreviewTitle: 'Envoyer la photo',
  photoPreviewBack: 'Retour à la sélection',
  photoPreviewSend: 'Envoyer',
  photoPreviewImageLabel: 'Photo sélectionnée',
  photoPreviewCaptionPlaceholder: 'Ajouter une légende…',
  photoPreviewDiscardTitle: 'Annuler les modifications ?',
  photoPreviewDiscardMessage: 'La légende saisie sera perdue.',
  photoPreviewDiscard: 'Annuler',
  photoPreviewKeepEditing: 'Continuer',
  photoMasksTitle: "Masques",
  photoMaskBlur: "Flou",
  photoMaskPixelate: "Pixelliser",
  photoMaskEmoji: "Autocollant",
  photoMaskHideAllFaces: "Masquer tous les visages",
  photoMaskNoFaces: "Aucun visage trouvé",
  photoMaskDetecting: "Recherche de visages…",
  photoMaskHint: "Touchez pour poser un masque, touchez-le à nouveau pour l’enlever",
  photoFiltersTitle: "Filtres",
  photoFilterStrength: "Intensité",
  photoFilterOriginal: "Original",
  photoFilterFilm400: "Film 400",
  photoFilterNight800t: "Nuit 800T",
  photoFilterDigicam2004: "Numérique 2004",
  photoFilterMatte: "Mat",
  photoFilterCinema: "Cinéma",
  photoFilterCleanLight: "Lumière claire",
  photoFilterMono3200: "Mono 3200",
  photoFilterDuotone: "Duoton",
  photoFilterGlow: "Éclat",
  photoFilterInfra: "Infra",
  photoFilterChrome: "Chrome",
  photoFilterNoon: "Midi",
  photoCropTitle: "Cadrage",
  photoCropApply: "Terminé",
  photoCropFree: "Libre",
  photoCropRotate: "Pivoter de 90°",
  photoCropReset: "Réinitialiser",
  photoCropFailed: "Impossible d’appliquer le cadrage",
  photoPreviewRemove: 'Retirer cette photo',
  photoSendPartialFailed: "Certaines photos n’ont pas pu être envoyées",
  photoQualityEconomy: 'Économie',
  photoQualityStandard: 'Standard',
  photoQualityMax: 'Maximum',
  photoQualityOriginal: 'Original',
  photoQualityCalculating: 'Préparation…',
  unitKilobytes: 'Ko',
  unitMegabytes: 'Mo',
  currentPassword: "Mot de passe actuel",
  newPasswordLabel: "Nouveau mot de passe",
  savePassword: 'Enregistrer le mot de passe',
  passwordChangeFailed: "Impossible de changer le mot de passe",
  bossUsersTitle: "Utilisateurs",
  bossUsersLoadFailed: "Impossible de charger les utilisateurs",
  bossCreateUserFailed: "Impossible de créer l’utilisateur",
  bossDeleteUserFailed: "Impossible de supprimer l’utilisateur",
  bossUserLoginPlaceholder: "Identifiant (lettres, chiffres, ._-)",
  bossDisplayNamePlaceholder: "Nom affiché",
  bossNewUserPassword: "Mot de passe temporaire (rempli automatiquement)",
  bossTempPasswordHint: "Copiez et envoyez à l’utilisateur ; il peut le changer dans les réglages (⚙).",
  bossRegeneratePassword: "Nouveau mot de passe",
  bossAddUser: "Ajouter un utilisateur",
  bossDeleteUser: "Supprimer",
  bossDeleteConfirmTitle: "Supprimer l’utilisateur ?",
  bossDeleteConfirmBody: "Le compte et les discussions associées seront supprimés.",
  bossCredentialsSentHint: "Nous avons envoyé à l’utilisateur un message privé avec ces informations. Vous pouvez copier le texte ci-dessous pour le partager autrement.",
  bossInviteCopyBody: "Bonjour !\n\nVotre compte SVYAZ est prêt (version bêta ; d’autres fonctionnalités arrivent).\nIdentifiant : {login}\nMot de passe temporaire : {password}\n\nAprès connexion, changez votre mot de passe : liste des discussions → ⚙ (réglages) → saisissez le mot de passe actuel puis le nouveau.\n\nPourquoi ce chat :\n• Pour garder un contact fiable dans un cercle restreint lorsque les messageries courantes sont bloquées, risquées ou trop bruyantes — un espace plus petit, avec appels et discussions réunis.{retention}\n• Vous gardez la maîtrise : supprimez une discussion dans l’application quand vous le souhaitez.\n\nLogiciel bêta — les protections et fonctionnalités continueront d’évoluer.\n\nLes groupes et les appels sont déjà disponibles dans une forme basique.\n\nBienvenue !",
  bossCopyInviteButton: "Copier identifiant et consignes",
  bossCopyInviteDone: "Copié dans le presse-papiers",
  bossInstructionLanguage: "Langue du message de bienvenue (ce texte seulement ; ne change pas la langue de l’app de l’utilisateur)",
  forwardedLabel: "Transféré",
  forwardedFromPrefix: "De :",
  savedChatTitle: "Enregistrés",
  shareIntentTitle: "Envoyer à",
  shareIntentPhotos: "Photos : {count}",
  shareIntentSkipped: "Fichiers ignorés : {count}. SVYAZ envoie des photos, des liens et du texte.",
  shareIntentSending: "Envoi… {sent} sur {total}",
  shareIntentFailed: "Échec de l'envoi",
  shareIntentEmpty: "Aucune discussion — démarrez-en une puis repartagez",
  shareIntentSavedHint: "Votre espace personnel",
  savedChatCardHint: "Notes, fichiers et liens — vous seul les voyez",
  savedEmptyTitle: "Ici, tout est à vous",
  savedEmptyBody: "Notes, fichiers, liens. Personne d’autre que vous ne le voit.",
  saveToSavedAction: "Enregistrer",
  savedMessageDone: "Ajouté aux enregistrés",
  savedSourceDeleted: "Le message d’origine a été supprimé",
  savedOpenFailed: "Impossible d’ouvrir les enregistrés",
  savedSectionAll: "Tout",
  savedSectionNotes: "Notes",
  savedSectionTasks: "Tâches",
  savedSectionFiles: "Fichiers",
  savedSectionLinks: "Liens",
  savedSectionVoice: "Vocaux",
  savedSectionEmptyNotes: "Aucune note pour l’instant. Tout ce que vous écrivez ici sans lien arrive dans cet onglet.",
  savedSectionEmptyTasks: "Aucune liste pour l’instant. Envoyez une check-list, elle apparaîtra ici.",
  savedSectionEmptyFiles: "Aucune photo ni vidéo enregistrée.",
  savedSectionEmptyLinks: "Aucun lien enregistré.",
  savedSectionEmptyVoice: "Aucun message vocal.",
  noteNewAction: "Nouvelle note",
  noteTitlePlaceholder: "Titre (facultatif)",
  noteBodyPlaceholder: "Texte de la note",
  notePinAction: "Épingler",
  noteUnpinAction: "Désépingler",
  notePinnedBadge: "Épinglée",
  noteSavingHint: "Enregistrement…",
  noteSavedHint: "Enregistré",
  noteColorLabel: "Couleur",
  noteEditFailed: "Impossible d’enregistrer la note",
  settingsGenderLabel: "Genre",
  genderUnset: "Non précisé",
  genderMale: "Homme",
  genderFemale: "Femme",
  settingsBirthDateLabel: "Date de naissance",
  settingsBirthDateHint: "JJ.MM.AAAA, âge de {min} à {max} ans. Vos contacts voient le jour et votre âge, pas l’année. Ils reçoivent un rappel pour votre anniversaire.",
  settingsBirthDateInvalid: "Saisissez la date au format JJ.MM.AAAA",
  settingsBirthDateAgeRange: "L’âge doit être compris entre {min} et {max} ans",
  profileYearsSuffix: "ans",
  birthdayTodayTitle: "Anniversaire aujourd’hui",
  birthdaySoonTitle: "Anniversaires à venir",
  birthdayCongratulate: "Souhaiter",
  birthdayTurns: "fête ses {age} ans",
  birthdayInDays: "dans {days} j.",
  birthdayTomorrow: "demain",
  birthdayPickTemplate: "Choisissez un message",
  birthdayTemplateHint: "Touchez pour l’insérer — vous pouvez encore le modifier avant l’envoi.",
  birthdaySend: "Envoyer",
  birthdaySent: "Message envoyé",
  birthdaySendFailed: "Impossible d’envoyer le message",
  birthdayHide: "Masquer",
  savedSectionReminders: "Rappels",
  savedSectionScheduled: "Différés",
  savedSectionEmptyReminders: "Aucun rappel pour l’instant.",
  savedSectionEmptyScheduled: "Aucun message différé pour l’instant.",
  reminderNewAction: "Nouveau rappel",
  reminderTitlePlaceholder: "Me rappeler de…",
  reminderBodyPlaceholder: "Détails (facultatif)",
  reminderWhenLabel: "Quand",
  reminderDatePlaceholder: "JJ.MM.AAAA",
  reminderTimePlaceholder: "HH:MM",
  reminderPrecisionHint: "Précision : environ une minute.",
  reminderSaveAction: "Enregistrer",
  reminderSaveFailed: "Impossible d’enregistrer le rappel",
  reminderDeleteAction: "Supprimer",
  reminderSnoozeAction: "Reporter de 10 min",
  reminderStatusScheduled: "En attente",
  reminderStatusFired: "Déclenché",
  reminderStatusCancelled: "Annulé",
  reminderInvalidTime: "Choisissez une date et une heure à venir",
  reminderZoneNote: "Défini en {zone}",
  organizerEditAction: "Modifier",
  quickToday: "Aujourd’hui 18:00",
  quickTomorrow: "Demain 09:00",
  quickInAnHour: "Dans une heure",
  scheduledNewAction: "Différer un message",
  scheduledRecipientsLabel: "À qui",
  scheduledRecipientsEmpty: "Choisissez au moins un contact",
  scheduledRecipientsSearch: "Rechercher un contact",
  scheduledTextPlaceholder: "Texte du message",
  scheduledWhenLabel: "Quand envoyer",
  scheduledCreateAction: "Programmer",
  scheduledCancelAction: "Annuler l’envoi",
  scheduledMessageSaveFailed: "Impossible de programmer le message",
  scheduledMessageCancelFailed: "Impossible d’annuler",
  scheduledStatusScheduled: "En attente",
  scheduledStatusSent: "Envoyé",
  scheduledStatusCancelled: "Annulé",
  scheduledStatusFailed: "Non délivré",
  scheduledTooSoon: "Choisissez une heure au moins une minute plus tard",
  notifyReminderTitle: "Rappel",
  notifyBirthdayTitle: "Anniversaire",
  notifyBirthdayBody: "fête son anniversaire aujourd’hui",
  registerAction: "Créer un compte",
  registerTitle: "Nouveau compte",
  registerEmailPlaceholder: "E-mail",
  registerNamePlaceholder: "Comment vous appeler ?",
  registerSubmit: "S’inscrire",
  registerEmailInvalid: "Cela ne ressemble pas à une adresse e-mail",
  registerPasswordTooShort: "8 caractères minimum",
  registerNameRequired: "Indiquez un nom",
  registerInviteOnly: "L’inscription se fait uniquement sur invitation",
  registerFailed: "Inscription impossible",
  verifySentTitle: "Consultez votre boîte mail",
  verifySentBody: "Nous avons envoyé un lien de confirmation à {email}. Ouvrez-le pour terminer l’inscription.",
  verifyResend: "Renvoyer le lien",
  verifyResendDone: "Si cette adresse attend une confirmation, le lien est en route.",
  backToLogin: "Retour à la connexion",
  forgotAction: "Mot de passe oublié ?",
  forgotTitle: "Récupération d’accès",
  forgotBody: "Saisissez l’adresse de votre compte et nous enverrons un lien pour changer le mot de passe.",
  forgotSubmit: "Envoyer le lien",
  forgotSentTitle: "Consultez votre boîte mail",
  forgotSentBody: "Si un compte utilise {email}, le lien est en route. Il ne sert qu’une fois et expire dans une heure.",
  resetTitle: "Nouveau mot de passe",
  resetBody: "Définissez un nouveau mot de passe. Toutes les sessions de ce compte seront fermées.",
  resetNewPassword: "Nouveau mot de passe",
  resetSubmit: "Enregistrer et se connecter",
  resetDone: "Mot de passe modifié. Connectez-vous avec le nouveau.",
  resetFailed: "Impossible de changer le mot de passe",
  verifyDone: "Adresse confirmée. Vous pouvez vous connecter.",
  verifyLinkDead: "Le lien n’a pas fonctionné : il a expiré ou a déjà servi. Demandez-en un nouveau.",
  captchaRequired: "Confirmez que vous n’êtes pas un robot et réessayez.",
  forwardSelectCount: "Sélection : {count}",
  selectMessagesAction: "Sélectionner",
  deleteSelectedAction: "Supprimer",
  deleteSelectedConfirmTitle: "Supprimer les messages sélectionnés ?",
  deleteSelectedConfirmBody: "Messages : {count}. Ils disparaîtront pour tous les participants et ne pourront pas être récupérés.",
  deleteSelectedFailed: "Certains messages n’ont pas pu être supprimés",
  forwardAction: "Transférer",
  forwardDoneBody: "{count} messages transférés",
  replyAction: "Répondre",
  replyQuoteLabel: "Réponse",
  replyOriginalDeleted: 'Message supprimé',
  copyTextAction: "Copier le texte",
  copyTextDone: "Copié",
  editAction: "Modifier",
  editingMessageTitle: "Modification",
  editMessageFailed: "Impossible de modifier le message",
  messageEditedMark: "modifié",
  deleteMessageAction: "Supprimer",
  deleteMessageFailed: "Impossible de supprimer le message",
  reactionLimitReached: "Trois réactions au maximum par message",
  savedClearAction: "Vider",
  savedClearDone: "Les favoris sont vides",
  savedClearFailed: "Impossible de vider les favoris",
  helpCenterTitle: "Conseils et nouveautés",
  helpCenterOpenLabel: "Conseils et nouveautés",
  helpCenterOpenLabelUnread: "Conseils et nouveautés — non lus",
  helpCenterNewBadge: "Nouveau",
  helpCenterAllReadHint: "Tout est lu. L’ampoule clignotera de nouveau à la prochaine nouveauté.",
  helpCenterIntro: "Ce que l’application sait faire et comment s’en servir. Les nouveautés arrivent en haut et font clignoter l’ampoule de l’en-tête.",
  settingsHandleSuggestions: "Variantes libres",
  saveChatImage: "Enregistrer l’image",
  saveChatImageSuccess: "Image enregistrée",
  saveChatImageFailed: "Impossible d’enregistrer l’image",
  saveChatImagePermissionDenied: "Autorisez l’accès à la photothèque pour enregistrer des images",
  saveChatVideo: "Enregistrer la vidéo",
  saveChatVideoSuccess: "Vidéo enregistrée",
  saveChatVideoFailed: "Impossible d’enregistrer la vidéo",
  saveChatVideoPermissionDenied: "Autorisez l’accès à la photothèque pour enregistrer des vidéos",
  forwardSameChatBlocked: "Impossible de transférer des messages vers la même discussion",
  messageReferenceNotFound: "Message d’origine introuvable dans l’historique chargé",
  gearSettings: "Paramètres du compte",
  myDruzhina: "Mon équipe",
  myDruzhinaHint: "Dans ton équipe, ne garde que ceux en qui tu as autant confiance qu’en toi.",
  myDruzhinaVarangian: "Hird",
  myDruzhinaHintVarangian: "Dans le hird, ne garde que ceux en qui tu as autant confiance qu’en toi.",
  contactRequestIncomingTitle: "Demandes reçues",
  contactLoadFailed: "Impossible de charger les contacts",
  contactRequestLoadFailed: "Impossible de charger les demandes",
  contactRequestSendFailed: "Impossible d’envoyer la demande",
  contactRequestApproveFailed: "Impossible d’accepter la demande",
  contactRequestRejectFailed: "Impossible de refuser la demande",
  contactSearchSelfRow: "Vous",
  chatLastMessageFallback: "Pas encore de messages",
  unseenReactionA11y: "Nouvelle réaction : {emoji}",
  settingsHapticsLabel: "Retour haptique",
  settingsSoundsLabel: "Sons",
  retentionNoticeTitle: "Conversation effacée automatiquement",
  retentionNoticeBody:
    "Les messages directs sont effacés du serveur au bout de quelques jours — c'est prévu, rien n'a été perdu.",
  retentionNoticeWhen: "Dernier effacement : {when}",
  contactRemoveConfirmTitle: "Retirer de l’équipe ?",
  contactRemoveConfirmTitleVarangian: "Retirer du hird ?",
  contactRemoveConfirmBody: "Vous serez retirés l’un de l’autre des contacts et l’historique de la discussion privée sera définitivement supprimé pour les deux.",
  contactRemoveFailed: "Impossible de retirer le contact",
  profileUpdateFailed: "Impossible de mettre à jour le profil",
  settingsProfileSection: "Profil",
  settingsChangeNameLabel: "Modifier le nom affiché",
  settingsChangeLoginLabel: "Modifier l’identifiant",
  settingsSaveProfile: "Enregistrer le profil",
  settingsSaved: "Modifications enregistrées",
  settingsShareSection: "Lien public et pseudo",
  settingsPublicHandleLabel: "Pseudo public (sans #)",
  settingsPublicHandleHint:
    "4–32 caractères : lettres minuscules, chiffres, tiret bas. Videz le champ pour retirer le pseudo.",
  settingsProfilePublicLabel: "Page de profil publique",
  settingsAllowDiscoveryLabel: "Trouvable par pseudo dans la recherche",
  settingsKeepPhotoMetadataLabel: 'Conserver les métadonnées des photos',
  settingsKeepPhotoMetadataHint:
    "Désactivé : la localisation, le modèle d’appareil et le numéro de série sont retirés des photos envoyées ; l’orientation et la date de prise de vue sont conservées. Activé : le fichier part avec tout ce qu’il contient.",
  settingsHandleWillSaveAs: "Sera enregistré comme : #{handle}",
  settingsHandleChecking: "Vérification…",
  settingsHandleFree: "Pseudo disponible",
  settingsHandleTaken: "Pseudo déjà pris",
  settingsHandleFormat: "4 à 32 caractères : lettres latines minuscules, chiffres, tiret bas",
  settingsHandleReserved: "Ce pseudo est réservé",
  settingsHandleNotAllowed: "Ce pseudo n'est pas autorisé",
  settingsHandleSaveFailedInvalid: "Le serveur a refusé ce pseudo. Essayez-en un autre.",
  settingsHandleConfirmChangeTitle: "Changer de pseudo ?",
  settingsHandleConfirmChangeBody: "Les liens avec l'ancien pseudo cesseront de fonctionner et quelqu'un d'autre pourra le prendre.",
  settingsHandleConfirmClearTitle: "Supprimer le pseudo ?",
  settingsHandleConfirmClearBody: "Votre page publique sera fermée, les liens cesseront de fonctionner et le pseudo redeviendra disponible.",
  settingsHandleKeep: "Garder",
  settingsSaveShareSettings: "Enregistrer le pseudo",
  settingsCopyHandleTag: "Copier le #pseudo",
  settingsCopied: "Copié",
  profileLinkUnavailable: "Ce profil est indisponible ou le pseudo n'existe plus.",
  profileLinkSelf: "Ce lien mène à votre propre profil.",
  settingsShareDisabledHidden: "La page publique du profil est désactivée — activez-la pour partager le lien.",
  settingsShareDisabledUnsaved: "Enregistrez d'abord le pseudo — le lien est construit à partir du pseudo enregistré.",
  settingsProfileSaved: "Profil enregistré",
  settingsShareSaved: "Pseudo enregistré",
  settingsPasswordChanged: "Mot de passe modifié",
  reportAction: "Signaler",
  reportTitle: "Quel est le problème ?",
  reportSent: "Signalement envoyé. Nous allons vérifier.",
  reportFailed: "Impossible d'envoyer le signalement",
  reportCategorySpam: "Spam ou publicité",
  reportCategoryHarassment: "Harcèlement ou menaces",
  reportCategorySexualContent: "Contenu sexuel",
  reportCategoryViolence: "Violence",
  reportCategoryFraud: "Arnaque",
  reportCategoryChildSafety: "Sécurité des enfants",
  reportCategoryOther: "Autre chose",
  blockAction: "Bloquer",
  blockConfirmTitle: "Bloquer cette personne ?",
  blockConfirmHint: "Elle ne pourra plus vous écrire ni vous appeler. Réversible dans « Réglages » → « Confidentialité ».",
  blockDone: "Bloqué",
  blockFailed: "Impossible de bloquer",
  unblockAction: "Débloquer",
  settingsBlockedSection: "Personnes bloquées",
  settingsBlockedEmpty: "Vous n'avez bloqué personne",
  moderationCancel: "Annuler",
  settingsDeleteAccountSection: "Suppression du compte",
  settingsDeleteAccountWarning:
    "La suppression est définitive. Profil, contacts, rappels et « Favoris » sont effacés. Les messages que vous avez envoyés restent dans les conversations de vos correspondants, signés « Compte supprimé ».",
  settingsDeleteAccountStart: "Supprimer le compte",
  settingsDeleteAccountConfirmHint:
    "Saisissez votre mot de passe et tapez le mot ci-dessous pour confirmer.",
  settingsDeleteAccountConfirmWord: "SUPPRIMER",
  settingsDeleteAccountConfirmPlaceholder: "Tapez SUPPRIMER",
  settingsDeleteAccountConfirm: "Supprimer définitivement",
  settingsDeleteAccountCancel: "Annuler",
  settingsDeleteAccountPasswordRequired: "Saisissez votre mot de passe actuel",
  settingsDeleteAccountWordMismatch: "Le mot de confirmation ne correspond pas",
  settingsDeleteAccountFailed: "Impossible de supprimer le compte",
  settingsLegalSection: "Documents",
  settingsPrivacyPolicyLink: "Politique de confidentialité",
  settingsTermsLink: "Conditions d'utilisation",
  settingsLocalOnlyHint: "Prend effet immédiatement, sur cet appareil uniquement",
  settingsNamePlaceholder: "Votre nom",
  settingsPublicHandlePlaceholder: "my_handle",
  settingsAvatarTitle: "Photo de profil",
  settingsAvatarChooseShape: "Cadre",
  settingsAvatarRemove: "Supprimer la photo",
  settingsAvatarUploadFailed: "Échec du téléversement de la photo",
  avatarAnimatedTooLarge: "Animation trop lourde — choisissez un GIF de moins de 8 Mo.",
  galleryPermissionDenied:
    "Pas d’accès aux photos. Activez-le dans les réglages — l’accès aux photos sélectionnées suffit.",
  settingsAvatarWebHint: "Web : choisissez un fichier dans la boîte de dialogue ; envoi en multipart.",
  avatarChangePhoto: "Changer la photo",
  avatarTapToChange: "Touchez la photo pour la changer",
  avatarTakePhoto: "Prendre une photo",
  avatarFromLibrary: "Choisir dans la galerie",
  avatarCameraDenied: "Pas d’accès à la caméra. Activez-le dans les réglages.",
  avatarRemoveConfirm: "La photo disparaîtra pour tout le monde ; les initiales restent.",
  avatarEditorHint: "Pincez pour zoomer, glissez pour déplacer, double tap pour réinitialiser",
  avatarEditorHintPanOnly: "Faites glisser la photo pour choisir le cadrage",
  avatarPreviewCard: "Voir comment les autres vous voient",
  chatAvatarSectionTitle: 'Photo du chat',
  userCardTitle: "Utilisateur",
  userCardOpenPhoto: "Ouvrir la photo",
  userCardPhotoHint: "Pincez ou double-tapez pour zoomer, touchez pour fermer",
  userCardPhotoFriendsOnly: "Seuls les amis peuvent ouvrir la photo en plein écran",
  userCardPhotoLoadFailed: "Impossible de charger la photo",
  chatUnknownMember: "Membre",
  contactCardLabel: "Contact",
  contactShareAction: "Partager le contact",
  contactShareChooseChat: "Envoyer le contact à…",
  contactShareSent: "Contact envoyé",
  contactShareFailed: "Échec de l’envoi du contact",
  checklistItemA11y: "Élément de la liste",
  checklistCheckedBy: "Coché par {name}",
  checklistToggleFailed: "Impossible de mettre à jour la liste",
  checklistDuePast: "Cette échéance est déjà passée. Indiquez une heure à venir.",
  checklistDueUnparsed: "Échéance incompréhensible. Écrivez {20:00} ou {02.02.2026 15:00}.",
  checklistAssigneeInvalid: "Un seul responsable par élément, sous forme de pseudo : #pseudo",
  checklistItemEmpty:
    "Un élément doit avoir du texte : les parenthèses et l’échéance s’y ajoutent, elles ne le remplacent pas.",
  checklistAssigneeNotFound: "Aucun profil avec ce pseudo",
  checklistDueToday: "aujourd’hui {time}",
  checklistDueTomorrow: "demain {time}",
  checklistOverdue: "En retard",
  checklistDueA11y: "Échéance : {when}",
  checklistAssigneeA11y: "Responsable : {name}",
  channelPostCommentsCount: "💬 {count}",
  settingsLoginHint: "Lettres, chiffres, point, tiret du bas, tiret",
  loginCheckInProgress: "Vérification de l’identifiant…",
  loginAvailable: "Identifiant disponible",
  loginUnavailable: "Identifiant déjà pris",
  chatTileCallAudio: "Appel vocal",
  chatTileCallVideo: "Appel vidéo",
  channelReadOnlyHint: "Canal en lecture seule : seuls les administrateurs et éditeurs peuvent publier.",
  channelCommentComposerHint: "Pour commenter, appuyez sur 💬 « Commentaires » sous une publication.",
  channelUnsubscribeConfirmBody: "Se désabonner de ce canal ? Il disparaîtra de votre liste de discussions.",
  channelUnsubscribeFailed: "Impossible de se désabonner du canal.",
  channelPostRestrictedHint: "Vous ne pouvez pas publier dans ce canal.",
  channelArticlesLoadFailed: 'Impossible de charger les articles.',
  channelArticlesDrafts: 'Brouillons',
  channelArticleStatusDraft: 'Brouillon',
  channelDraftSavedTitle: 'Brouillon enregistré',
  channelDraftSavedBody: 'Le brouillon est dans la section Articles en haut du canal.',
  channelDraftView: 'Ouvrir le brouillon',
  channelPostPublishedTitle: 'Article publié',
  channelSettingsTitle: 'Paramètres du canal',
  channelDraftsSettingsHint: 'Brouillons — appuyez pour modifier ou publier.',
  channelDraftsEmpty: 'Aucun brouillon.',
  deleteChannelConfirmBody: 'Le canal et tous les articles seront supprimés définitivement.',
  channelPostOpenFailed: "Impossible d'ouvrir l'article.",
  channelPostLoadFailed: "Impossible de charger l'article.",
  channelPostComments: 'Commentaires',
  channelCommentsClosed: "Les commentaires sont fermés dans ce canal.",
  channelPublishAction: 'Publier',
  channelPublishDraftTitle: 'Publier ce brouillon ?',
  channelPublishFailed: "Impossible de publier l'article.",
  channelDraftOpen: 'Ouvrir',
  channelSubscribedTitle: 'Abonnement activé',
  channelGuestSubscribeHint:
    'Vous lisez ce canal en invité. {count} personnes y sont abonnées — vous recevrez aussi les nouveaux articles.',
  channelArchiveTitle: 'Tous les articles',
  channelArchiveEmpty: 'Aucun article publié pour le moment.',
  channelArchiveLoadMore: 'Afficher plus',
  editorPreview: 'Aperçu',
  editorPreviewBack: "Revenir à l'édition",
  editorInsertHint: 'Choisissez un type de bloc ci-dessous : il se placera juste après celui-ci.',
  editorInvalidChannel:
    "Cet écran a été ouvert sans canal. Fermez-le, ouvrez le canal depuis la liste et commencez-y un article.",
  blockParagraph: 'Paragraphe',
  blockHeading: 'Titre',
  blockImage: 'Image',
  blockQuote: 'Citation',
  blockBulletList: 'Liste à puces',
  blockOrderedList: 'Liste numérotée',
  blockDivider: 'Séparateur',
  blockCode: 'Code',
  blockEmbed: 'Contenu intégré',
  blockVideo: 'Lien vidéo',
  blockGallery: 'Galerie',
  editorTitleLabel: 'Titre',
  editorTitlePlaceholder: "Titre de l'article",
  editorBodyLabel: 'Texte',
  editorAddCover: 'Ajouter une couverture',
  editorChangeCover: 'Changer la couverture',
  editorRemoveCover: 'Retirer la couverture',
  editorSaveDraft: 'Brouillon',
  editorPublish: 'Publier',
  editorListItem: 'Élément de liste',
  editorBlockEmpty: 'vide',
  editorReplaceImage: "Remplacer l'image",
  editorUploadImage: 'Téléverser une image',
  editorAddPhotos: 'Ajouter des photos',
  editorAddBlockA11y: 'Ajouter : {name}',
  editorInsertHere: 'Insérer un bloc',
  commentsTitle: 'Commentaires',
  commentsReply: 'Répondre',
  commentsReplyingTo: 'Réponse à {name}',
  commentsPlaceholder: 'Écrire un commentaire…',
  commentsSend: 'Envoyer',
  commentsPending: 'en attente',
  commentsThumbUp: "J'aime",
  commentsDelete: 'Supprimer',
  commentsApprove: 'Approuver',
  commentsBan: 'Bannir',
  commentsDeleteConfirmBody: 'Le commentaire disparaît de la discussion.',
  commentsBanConfirmTitle: 'Bannir des commentaires ?',
  commentsBanConfirmBody: '{name} ne pourra plus commenter dans ce canal.',
  commentsModerationFailed: "L'action n'a pas abouti.",
  articleReadMore: "Lire l'article →",
  articleCardKicker: 'Article',
  channelMuteOn: 'Couper les notifications',
  channelMuteOff: 'Réactiver les notifications',
  channelMuteFailed: 'Impossible de modifier les notifications.',
  channelViewsCount: '👁 {count}',
  channelPostEdit: 'Modifier',
  channelPostEditTitle: "Modifier l'article",
  channelPostEditPublishedHint:
    "L'enregistrement met à jour l'article pour ceux qui l'ont déjà. Les abonnés ne sont pas notifiés à nouveau.",
  channelPostSaveChanges: 'Enregistrer',
  channelPostSaved: 'Modifications enregistrées',
  channelPostSaveFailed: "Impossible d'enregistrer l'article.",
  channelPostDelete: 'Supprimer',
  channelPostDeleteConfirmTitle: "Supprimer l'article ?",
  channelPostDeleteConfirmBody:
    "L'article et son annonce dans le canal disparaissent définitivement, y compris pour ceux qui l'ont déjà ouvert.",
  channelPostDeleteDraftConfirmBody: 'Le brouillon disparaît définitivement.',
  channelPostDeleteFailed: "Impossible de supprimer l'article.",
  channelPostUnpublish: 'Remettre en brouillon',
  channelPostUnpublishConfirmTitle: 'Remettre en brouillon ?',
  channelPostUnpublishConfirmBody:
    "L'annonce disparaît du fil du canal. Le texte reste dans vos brouillons et vous pourrez le republier.",
  channelPostUnpublishFailed: "Impossible de remettre l'article en brouillon.",
  channelDraftRestoredHint: 'Le texte non enregistré de la dernière fois a été restauré.',
  channelDraftLeaveTitle: "Quitter l'éditeur ?",
  channelDraftLeaveBody:
    "Le texte non enregistré reste sur cet appareil : l'éditeur le proposera à la prochaine ouverture.",
  channelDraftLeaveConfirm: 'Quitter',
  channelDraftLeaveStay: 'Rester',
  channelDraftDiscardRestored: 'Supprimer le texte restauré',
  channelSlugLabel: 'Adresse du canal',
  channelSlugPlaceholder: 'my_channel',
  channelSlugHint:
    '4 à 32 caractères : lettres latines minuscules, chiffres, tiret bas. Sert à composer le lien du canal.',
  channelSlugInvalid:
    "L'adresse doit contenir 4 à 32 caractères : lettres latines minuscules, chiffres et tirets bas uniquement.",
  channelCommentSendFailed: 'Commentaire non envoyé.',
  channelCommentsLoadFailed: 'Impossible de charger les commentaires.',
  chatFilterGroup: "Groupes",
  chatFilterChannel: "Canaux",
  chatSortRecent: "les plus récents d'abord",
  chatSortOld: "les plus anciens d'abord",
  chatSortTitleAsc: "par nom A-Z",
  chatSortTitleDesc: "par nom Z-A",
  callScopeBadgeGroupVideo: "Appel vidéo de groupe",
  callScopeBadgeDirectVideo: "Appel vidéo direct",
  groupCallUnavailableTitle: "Les appels de groupe sont indisponibles",
  groupCallsTemporarilyOff:
    "Les appels de groupe sont temporairement désactivés. Les appels individuels fonctionnent.",
  groupCallUnavailableDisabled: "Le serveur d’appels de groupe n’est pas configuré. Contactez le support.",
  groupCallUnavailableNetwork: "Impossible de joindre le serveur d’appels. Vérifiez votre connexion et réessayez.",
  groupCallUnavailableConnect: "Impossible de rejoindre l’appel. Réessayez dans un instant.",
  groupCallJoinOngoing: "Rejoindre",
  groupCallOngoingBadge: "Appel en cours",
  callDiagnosticsTitle: "Diagnostic de l’appel",
  callDiagnosticsHint: "Appui long — diagnostic de l’appel",
  callDiagnosticsCopy: "Copier le rapport",
  callDiagTransport: "Transport",
  callDiagCandidatePair: "Paire de candidats",
  callDiagBitrate: "Débit",
  callDiagPacketsLost: "Paquets perdus",
  callDiagParticipants: "Participants",
  screenShareTitle: "Partage d’écran",
  screenShareBody: "Ce navigateur n’a pas autorisé la capture d’écran.",
  screenShareBusyBody: "Quelqu’un partage déjà son écran. Attendez qu’il arrête.",
  screenShareLabel: "partage son écran",
  callShareScreen: "Partager l’écran",
  callShareScreenStop: "Arrêter le partage",
  callSwitchCamera: "Retourner la caméra",
  settingsAppearanceSection: "Apparence",
  settingsVisualThemeLabel: "Style de l’application",
  visualThemeNeutral: "Neutre",
  visualThemeVarangian: "Varègue",
  visualThemeDark: "Sombre",
  visualThemeAuto: "Auto · Soleil / Cosmos",
  settingsLanguageSection: "Langue",
  settingsLanguageLabel: "Langue de l’interface",
  settingsTabPreferences: 'Application',
  settingsTabAccount: 'Compte',
  settingsTabAvatar: 'Photo',
  settingsTabProfile: 'Profil',
  settingsTabPrivacy: "Confidentialité",
  settingsLoginSection: "Identifiant de connexion",
  settingsSaveLogin: "Enregistrer l'identifiant",
  settingsLoginSaved: "Identifiant enregistré",
  settingsPasswordSection: "Mot de passe",
  settingsVisibilitySection: "Qui peut vous trouver",
  settingsProfilePublicHint: "Désactivé — le lien vers votre profil ne s'ouvre plus pour personne.",
  settingsAllowDiscoveryHint: "Désactivé — on ne peut plus vous trouver par pseudo dans la recherche.",
  settingsMediaPrivacySection: "Photos et données",
  settingsShareDisabledNoHandle: "Choisissez un pseudo et enregistrez-le — le lien en découle.",
  settingsPreferencesHint:
    'Le thème et la langue s’appliquent dès le choix — aucun mot de passe requis.',
  settingsPreferencesDone: 'Terminé',
  settingsCurrentPasswordRequired: 'Saisissez le mot de passe actuel pour en définir un nouveau.',
  directRetentionPolicyTitle: 'Nettoyage du chat privé',
  directRetentionPolicyBody:
    'Pour des raisons de sécurité, l’historique des chats personnels sur le serveur est effacé régulièrement. Un nettoyage de ce chat arrive bientôt. Enregistrez sur votre appareil tout ce dont vous avez besoin : messages, photos, fichiers — si nécessaire.',
  directRetentionTimerA11y: 'Temps avant l’effacement de l’historique sur le serveur. Appuyez pour les détails.',
  quickRepliesTitle: 'Réponses rapides',
  quickReplyAddTitle: 'Nouvelle réponse rapide',
  quickReplyAddPlaceholder: 'Votre phrase…',
  quickReplySave: 'Enregistrer',
  quickReplyDelete: 'Supprimer',
  quickReplyDeleteConfirmTitle: 'Supprimer la réponse rapide?',
  quickReplyDefaultGreeting: 'Bonjour !',
  quickReplyDefaultInTouch: 'Je suis là',
  quickReplyDefaultThanks: 'Merci !',
  sharePost: 'Partager la publication',
  shareChannel: 'Partager le canal',
  shareProfile: 'Partager le profil',
  settingsBioLabel: 'À propos de moi',
  settingsBioPlaceholder: 'Quelques mots sur vous…',
  settingsStatusLabel: 'Statut',
  settingsStatusPlaceholder: 'Quoi de neuf ?',
  selfStatusEmpty: 'Ajouter un statut',
  quickSettingsTitle: 'Réglages rapides',
  selfStatusSave: 'Enregistrer',
  dndLabel: 'Ne pas déranger',
  dndHint: 'Aucune notification. Les appels passent toujours.',
  dndFailed: 'Impossible de modifier le réglage',
  notifyShowTextLabel: 'Afficher le texte du message',
  notifyShowTextHint: 'Désactivé : la notification indique seulement « Nouveau message », sans le texte.',
  userCardAddContact: 'Ajouter un contact',
  userCardContactPending: 'Demande envoyée',
  navTitleNewPost: 'Nouvel article',
  navTitlePost: 'Article',
};
/** Строки участков (`areas/`) подмешиваются к основным: см. шапку любого файла там. */
export type MessageKey =
  | BaseMessageKey
  | CommonMessageKey
  | ChatListMessageKey
  | ChatMessageKey
  | CallsMessageKey
  | AccountMessageKey
  | MusicMessageKey
  | MeetingsMessageKey
  | FeedbackMessageKey;

function withAreas(locale: AppLocale, base: Record<BaseMessageKey, string>): Record<MessageKey, string> {
  return {
    ...base,
    ...commonMessages[locale],
    ...chatListMessages[locale],
    ...chatMessages[locale],
    ...callsMessages[locale],
    ...accountMessages[locale],
    ...musicMessages[locale],
    ...meetingsMessages[locale],
    ...feedbackMessages[locale],
  };
}

export const MESSAGES: Record<AppLocale, Record<MessageKey, string>> = {
  en: withAreas('en', en),
  ru: withAreas('ru', ru),
  sr: withAreas('sr', sr),
  fr: withAreas('fr', fr),
};

export const LOCALE_LABELS: Record<AppLocale, { a11yLabel: string }> = {
  sr: { a11yLabel: 'Srpski' },
  ru: { a11yLabel: 'Русский' },
  en: { a11yLabel: 'English' },
  fr: { a11yLabel: 'Français' },
};

export const DEFAULT_LOCALE: AppLocale = 'en';

export function isAppLocale(v: string | null | undefined): v is AppLocale {
  return v === 'ru' || v === 'en' || v === 'sr' || v === 'fr';
}
